import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import ReactDOM from "react-dom";
import "./App.css";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import toast, { Toaster } from "react-hot-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Cart Context
const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Cart Provider
const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('sessionId');
    if (stored) return stored;
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('sessionId', newId);
    return newId;
  });

  // Load cart from backend
  const loadCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      const cart = response.data;
      setCartItems(cart.items || []);
      setCartTotal(cart.total_amount || 0);
    } catch (error) {
      console.error("Failed to load cart:", error);
    }
  };

  // Add to cart
  const addToCart = async (product, quantity = 1) => {
    try {
      const existingItem = cartItems.find(item => item.product_id === product.id);
      
      let updatedItems;
      if (existingItem) {
        updatedItems = cartItems.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        updatedItems = [...cartItems, {
          product_id: product.id,
          quantity,
          price: product.selling_price,
          product_title: product.title,
          product_image: product.main_image
        }];
      }

      const response = await axios.post(`${API}/cart?session_id=${sessionId}`, {
        items: updatedItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price
        }))
      });

      setCartItems(updatedItems);
      setCartTotal(response.data.total_amount);
      toast.success("Produkt zum Warenkorb hinzugefügt!");
    } catch (error) {
      console.error("Failed to add to cart:", error);
      toast.error("Fehler beim Hinzufügen zum Warenkorb");
    }
  };

  // Remove from cart
  const removeFromCart = async (productId) => {
    try {
      const updatedItems = cartItems.filter(item => item.product_id !== productId);
      
      const response = await axios.post(`${API}/cart?session_id=${sessionId}`, {
        items: updatedItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price
        }))
      });

      setCartItems(updatedItems);
      setCartTotal(response.data.total_amount);
      toast.success("Produkt aus Warenkorb entfernt");
    } catch (error) {
      console.error("Failed to remove from cart:", error);
      toast.error("Fehler beim Entfernen");
    }
  };

  // Update quantity
  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      await removeFromCart(productId);
      return;
    }

    try {
      const updatedItems = cartItems.map(item =>
        item.product_id === productId
          ? { ...item, quantity: newQuantity }
          : item
      );

      const response = await axios.post(`${API}/cart?session_id=${sessionId}`, {
        items: updatedItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price
        }))
      });

      setCartItems(updatedItems);
      setCartTotal(response.data.total_amount);
    } catch (error) {
      console.error("Failed to update quantity:", error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
    setCartTotal(0);
  };

  useEffect(() => {
    loadCart();
  }, [sessionId]);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      cartTotal,
      cartCount,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      sessionId
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Header Component
const Header = ({ onOpenCart, onSetActiveTab, activeTab, wishlistCount, onNavigate, wishlistEnabled = true }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { cartCount } = useCart();

  return (
    <header className="sticky top-0 z-50 bg-white shadow-lg border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => onSetActiveTab('products')}>
            {/* New imported24.de Logo with Gradient */}
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-magenta-600 p-2 rounded-lg shadow-lg">
                <svg width="32" height="32" viewBox="0 0 32 32" className="text-white">
                  <path fill="currentColor" d="M16 2L4 8v8c0 7.73 5.8 14.67 13.71 15.96L18 32c.33 0 .67-.03 1-.08L31 26v-8L16 2z"/>
                  <path fill="rgba(255,255,255,0.8)" d="M16 6L8 10v6c0 4.84 3.63 9.17 8.57 9.98L17 26c.17 0 .33-.02.5-.04L26 20v-6L16 6z"/>
                  <text x="16" y="18" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif">24</text>
                </svg>
              </div>
              <div className="flex flex-col">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-magenta-600 bg-clip-text text-transparent">
                  imported24.de
                </h1>
                <span className="text-xs text-gray-500 font-medium">Neuheiten & Schnäppchen Finder</span>
              </div>
            </div>
          </div>
          
          {/* Search Bar - Desktop */}
          <div className="hidden md:block flex-1 max-w-2xl mx-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Suche nach Elektronik, Gadgets, Dekoration..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center space-x-4">
            <nav className="hidden md:flex space-x-6 items-center">
              <button 
                onClick={() => onSetActiveTab('products')}
                className={`font-medium text-sm ${activeTab === 'products' ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
              >
                Produkte
              </button>
              <button 
                onClick={() => onSetActiveTab('categories')}
                className={`font-medium text-sm ${activeTab === 'categories' ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
              >
                Kategorien
              </button>
              {wishlistEnabled && (
                <button 
                  onClick={() => onNavigate('wishlist')}
                  className="relative font-medium text-sm text-gray-700 hover:text-blue-600 flex items-center gap-1"
                >
                  ❤️ Wunschliste
                  {wishlistCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {wishlistCount}
                    </span>
                  )}
                </button>
              )}
              {/* Login button hidden - accessible via footer logo */}
              {/* <button 
                onClick={() => onSetActiveTab('add-product')}
                className={`font-medium text-sm px-4 py-2 rounded-lg ${activeTab === 'add-product' ? 'bg-blue-600 text-white' : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'}`}
              >
                🔐 Login
              </button> */}
            </nav>

            {/* Cart Button - Removed */}

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-blue-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden pb-4">
          <input
            type="text"
            placeholder="Suchen..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-2 space-y-2">
            <button 
              onClick={() => { onSetActiveTab('products'); setIsMenuOpen(false); }}
              className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium py-2"
            >
              Produkte
            </button>
            <button 
              onClick={() => { onSetActiveTab('categories'); setIsMenuOpen(false); }}
              className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium py-2"
            >
              Kategorien
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

// Impressum Page Component
const ImpressumPage = ({ settings }) => {
  // If custom content exists, use it
  if (settings.impressum_content && settings.impressum_content.trim()) {
    const formattedContent = autoFormatText(settings.impressum_content);
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8">Impressum</h1>
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div 
            dangerouslySetInnerHTML={{ __html: formattedContent }} 
            className="prose max-w-none"
          />
        </div>
      </div>
    );
  }

  // Default template
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold mb-8">Impressum</h1>
      
      <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2">Angaben gemäß § 5 TMG</h2>
          <p className="text-gray-700">{settings.company_name}</p>
          <p className="text-gray-700">{settings.address}</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Kontakt</h2>
          <p className="text-gray-700">E-Mail: <a href={`mailto:${settings.email}`} className="text-blue-600 hover:underline">{settings.email}</a></p>
          {settings.phone && <p className="text-gray-700">Telefon: {settings.phone}</p>}
        </div>

        {settings.vat_id && (
          <div>
            <h2 className="text-xl font-semibold mb-2">Umsatzsteuer-ID</h2>
            <p className="text-gray-700">
              Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
              {settings.vat_id}
            </p>
          </div>
        )}

        <div>
          <h2 className="text-xl font-semibold mb-2">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h2>
          <p className="text-gray-700">{settings.responsible_person}</p>
          <p className="text-gray-700">{settings.address}</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">EU-Streitschlichtung</h2>
          <p className="text-gray-700">
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:<br />
            <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              https://ec.europa.eu/consumers/odr/
            </a>
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Verbraucherstreitbeilegung</h2>
          <p className="text-gray-700">
            Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
          </p>
        </div>
      </div>
    </div>
  );
};

// Datenschutz Page Component
const DatenschutzPage = ({ settings }) => {
  // If custom content exists, use it
  if (settings.datenschutz_content && settings.datenschutz_content.trim()) {
    const formattedContent = autoFormatText(settings.datenschutz_content);
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8">Datenschutzerklärung</h1>
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div 
            dangerouslySetInnerHTML={{ __html: formattedContent }} 
            className="prose max-w-none"
          />
        </div>
      </div>
    );
  }

  // Default template
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold mb-8">Datenschutzerklärung</h1>
      
      <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4">1. Datenschutz auf einen Blick</h2>
          <h3 className="text-xl font-semibold mb-2">Allgemeine Hinweise</h3>
          <p className="text-gray-700 mb-2">
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
            passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
            persönlich identifiziert werden können.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">2. Verantwortliche Stelle</h2>
          <p className="text-gray-700 mb-2">
            Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
          </p>
          <p className="text-gray-700 font-medium">
            {settings.company_name}<br />
            {settings.address}<br />
            E-Mail: <a href={`mailto:${settings.email}`} className="text-blue-600 hover:underline">{settings.email}</a>
            {settings.phone && <><br />Telefon: {settings.phone}</>}
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">3. Datenerfassung auf dieser Website</h2>
          
          <h3 className="text-xl font-semibold mb-2 mt-4">Server-Log-Dateien</h3>
          <p className="text-gray-700 mb-2">
            Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, 
            die Ihr Browser automatisch an uns übermittelt. Dies sind:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
            <li>Browsertyp und Browserversion</li>
            <li>verwendetes Betriebssystem</li>
            <li>Referrer URL</li>
            <li>Hostname des zugreifenden Rechners</li>
            <li>Uhrzeit der Serveranfrage</li>
            <li>IP-Adresse</li>
          </ul>
          <p className="text-gray-700 mt-2">
            Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen.
          </p>

          <h3 className="text-xl font-semibold mb-2 mt-4">Newsletter-Anmeldung</h3>
          <p className="text-gray-700 mb-2">
            Wenn Sie den auf der Website angebotenen Newsletter beziehen möchten, benötigen wir von Ihnen 
            eine E-Mail-Adresse sowie Informationen, welche uns die Überprüfung gestatten, dass Sie der Inhaber 
            der angegebenen E-Mail-Adresse sind.
          </p>
          <p className="text-gray-700 mb-2">
            Die Datenverarbeitung zum Zwecke der Newsletter-Versendung erfolgt auf Grundlage Ihrer Einwilligung 
            (Art. 6 Abs. 1 lit. a DSGVO). Sie können diese Einwilligung jederzeit widerrufen. 
            Ein entsprechender Link befindet sich in jedem Newsletter.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">4. Affiliate-Links und externe Dienste</h2>
          
          <h3 className="text-xl font-semibold mb-2">AliExpress und andere Partner</h3>
          <p className="text-gray-700 mb-2">
            Diese Website nutzt Affiliate-Links zu externen Shops (hauptsächlich AliExpress). Wenn Sie auf einen 
            dieser Links klicken, werden Sie zu der jeweiligen Partner-Website weitergeleitet.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Wichtig:</strong> Ab dem Zeitpunkt der Weiterleitung gelten die Datenschutzbestimmungen des 
            jeweiligen Partners. Wir haben keinen Einfluss auf die Datenverarbeitung durch diese Partner.
          </p>
          <p className="text-gray-700 mb-2">
            Bei der Nutzung von Affiliate-Links können folgende Daten an den Partner übermittelt werden:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
            <li>Ihre IP-Adresse</li>
            <li>Referrer-URL (von welcher Seite Sie kommen)</li>
            <li>Tracking-ID (zur Zuordnung der Provision)</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">5. Cookies</h2>
          <p className="text-gray-700 mb-2">
            Diese Website verwendet nur technisch notwendige Cookies. Diese sind erforderlich, damit die Website 
            funktioniert. Sie dienen nicht der Erfassung personenbezogener Daten.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">6. Ihre Rechte</h2>
          <p className="text-gray-700 mb-2">
            Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
            <li><strong>Recht auf Auskunft:</strong> Sie können Auskunft über Ihre gespeicherten Daten verlangen</li>
            <li><strong>Recht auf Berichtigung:</strong> Sie können die Berichtigung unrichtiger Daten verlangen</li>
            <li><strong>Recht auf Löschung:</strong> Sie können die Löschung Ihrer Daten verlangen</li>
            <li><strong>Recht auf Einschränkung:</strong> Sie können die Einschränkung der Verarbeitung verlangen</li>
            <li><strong>Recht auf Datenübertragbarkeit:</strong> Sie können Ihre Daten in einem strukturierten Format erhalten</li>
            <li><strong>Widerspruchsrecht:</strong> Sie können der Verarbeitung Ihrer Daten widersprechen</li>
          </ul>
          <p className="text-gray-700 mt-2">
            Für die Ausübung Ihrer Rechte wenden Sie sich bitte an: <a href={`mailto:${settings.email}`} className="text-blue-600 hover:underline">{settings.email}</a>
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">7. Dauer der Datenspeicherung</h2>
          <p className="text-gray-700 mb-2">
            Wir speichern Ihre personenbezogenen Daten nur so lange, wie dies für die Erfüllung der Zwecke, 
            für die sie erhoben wurden, erforderlich ist oder gesetzliche Aufbewahrungsfristen dies erfordern.
          </p>
          <p className="text-gray-700 mb-2">
            Newsletter-Anmeldungen werden gespeichert, bis Sie sich vom Newsletter abmelden.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">8. Beschwerderecht</h2>
          <p className="text-gray-700 mb-2">
            Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung Ihrer 
            personenbezogenen Daten durch uns zu beschweren.
          </p>
        </div>

        <div className="pt-6 border-t">
          <p className="text-sm text-gray-600">
            Stand: {new Date().toLocaleDateString('de-DE')}<br />
            {settings.company_name}
          </p>
        </div>
      </div>
    </div>
  );
};

// AGB Page Component
const AGBPage = ({ settings }) => {
  // If custom content exists, use it
  if (settings.agb_content && settings.agb_content.trim()) {
    const formattedContent = autoFormatText(settings.agb_content);
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8">Allgemeine Geschäftsbedingungen (AGB)</h1>
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div 
            dangerouslySetInnerHTML={{ __html: formattedContent }} 
            className="prose max-w-none"
          />
        </div>
      </div>
    );
  }

  // Default template
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold mb-8">Allgemeine Geschäftsbedingungen (AGB)</h1>
      
      <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 1 Geltungsbereich</h2>
          <p className="text-gray-700 mb-2">
            Diese Allgemeinen Geschäftsbedingungen (im Folgenden "AGB") gelten für die Nutzung der Website imported24.de 
            und alle darüber vermittelten Geschäfte zwischen {settings.company_name} (im Folgenden "Anbieter") und dem Kunden.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 2 Affiliate-Portal</h2>
          <p className="text-gray-700 mb-2">
            imported24.de ist ein Affiliate-Portal. Wir verkaufen keine Produkte direkt, sondern vermitteln Produkte 
            von Drittanbietern (hauptsächlich AliExpress) über Affiliate-Links.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Wichtig:</strong> Der Kaufvertrag kommt direkt zwischen Ihnen und dem jeweiligen Partner-Shop (z.B. AliExpress) zustande. 
            Wir sind nicht Vertragspartner des Kaufvertrages.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 3 Affiliate-Links und Haftung</h2>
          <p className="text-gray-700 mb-2">
            Alle auf unserer Website angezeigten Produkte enthalten Affiliate-Links zu Partner-Shops. 
            Wenn Sie auf "Kaufen" oder ähnliche Buttons klicken, werden Sie zum jeweiligen Partner-Shop weitergeleitet.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Keine Haftung für fehlerhafte Links:</strong> Wir übernehmen keine Haftung für fehlerhafte oder nicht mehr 
            funktionierende Affiliate-Links. Die Produktverfügbarkeit, Preise und Lieferbedingungen werden ausschließlich 
            vom Partner-Shop festgelegt.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 4 Preise und Produktinformationen</h2>
          <p className="text-gray-700 mb-2">
            Alle auf unserer Website angezeigten Preise sind unverbindliche Empfehlungen und können von den tatsächlichen 
            Preisen im Partner-Shop abweichen. Der verbindliche Preis wird Ihnen im Partner-Shop angezeigt.
          </p>
          <p className="text-gray-700 mb-2">
            Die angezeigten Preise verstehen sich inklusive der gesetzlichen Mehrwertsteuer (19%).
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 5 Kein Widerrufsrecht bei imported24.de</h2>
          <p className="text-gray-700 mb-2">
            Da wir selbst keine Produkte verkaufen, besteht bei uns kein Widerrufsrecht. 
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Hinweis:</strong> Ihr Widerrufsrecht richtet sich nach den AGB und dem Widerrufsrecht des jeweiligen 
            Partner-Shops (z.B. AliExpress). Bitte informieren Sie sich dort über Ihre Rechte.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 6 Versand und Lieferung</h2>
          <p className="text-gray-700 mb-2">
            Versand und Lieferung werden ausschließlich vom Partner-Shop durchgeführt. Wir haben keinen Einfluss auf:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
            <li>Lieferzeiten</li>
            <li>Versandkosten</li>
            <li>Versandmethoden</li>
            <li>Tracking-Informationen</li>
          </ul>
          <p className="text-gray-700 mt-2">
            Bitte wenden Sie sich bei Fragen zur Lieferung direkt an den Partner-Shop.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 7 Gewährleistung und Reklamation</h2>
          <p className="text-gray-700 mb-2">
            Da der Kaufvertrag zwischen Ihnen und dem Partner-Shop geschlossen wird, sind auch Gewährleistungsansprüche 
            ausschließlich gegenüber dem Partner-Shop geltend zu machen.
          </p>
          <p className="text-gray-700 mb-2">
            Wir übernehmen keine Haftung für Produktmängel, fehlerhafte Lieferungen oder sonstige Probleme mit den 
            über unsere Affiliate-Links erworbenen Produkten.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 8 Datenschutz</h2>
          <p className="text-gray-700 mb-2">
            Wir nehmen den Schutz Ihrer persönlichen Daten ernst. Einzelheiten entnehmen Sie bitte unserer Datenschutzerklärung.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">§ 9 Schlussbestimmungen</h2>
          <p className="text-gray-700 mb-2">
            Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.
          </p>
        </div>

        <div className="pt-6 border-t">
          <p className="text-sm text-gray-600">
            Stand: {new Date().toLocaleDateString('de-DE')}<br />
            {settings.company_name}<br />
            {settings.address}
          </p>
        </div>
      </div>
    </div>
  );
};

// Newsletter Subscription Component
const NewsletterSubscription = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return; // Prevent double submission
    
    setIsSubmitting(true);
    setMessage(null);

    try {
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await axios.post(`${API}/newsletter/subscribe`, { email }, {
        signal: controller.signal,
        timeout: 10000
      });
      
      clearTimeout(timeoutId);
      
      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: '✅ Erfolgreich angemeldet! Vielen Dank für Ihr Interesse.' });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: response.data?.message || 'Fehler bei der Anmeldung' });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        setMessage({ 
          type: 'error', 
          text: 'Zeitüberschreitung. Bitte versuchen Sie es erneut.' 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: error.response?.data?.message || 'Fehler bei der Anmeldung. Bitte versuchen Sie es später erneut.' 
        });
      }
    } finally {
      // Always reset submitting state
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-2">
            📧 Newsletter abonnieren
          </h3>
          <p className="text-base text-white/90 mb-4">
            Erhalten Sie die neuesten Angebote und Schnäppchen direkt in Ihr Postfach!
          </p>

          <form onSubmit={handleSubscribe} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ihre E-Mail-Adresse"
                required
                className="flex-1 px-4 py-2 rounded-lg text-gray-900 focus:ring-2 focus:ring-white focus:outline-none"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-white text-purple-600 hover:bg-gray-100 transform hover:scale-105'
                }`}
              >
                {isSubmitting ? '⏳' : '✉️ Anmelden'}
              </button>
            </div>
          </form>

          {message && (
            <div className={`mt-3 p-2 rounded-lg text-sm ${
              message.type === 'success' 
                ? 'bg-green-500/20 border border-green-300 text-white' 
                : 'bg-red-500/20 border border-red-300 text-white'
            }`}>
              {message.text}
            </div>
          )}

          <p className="text-xs text-white/70 mt-3">
            Keine Sorge, wir spammen nicht! Sie können sich jederzeit abmelden.
          </p>
        </div>
      </div>
    </section>
  );
};

// Helper function to auto-format plain text to HTML
const autoFormatText = (text) => {
  if (!text) return '';
  
  // Check if text already contains HTML tags
  if (text.includes('<h') || text.includes('<p>') || text.includes('<div>')) {
    return text; // Already HTML formatted
  }
  
  // Split into lines
  const lines = text.split('\n');
  let html = '';
  let inParagraph = false;
  let inList = false;
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) {
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      return;
    }
    
    // Check if it's a VAT ID or similar identifier (e.g., "DE295820071")
    const isVATorID = /^[A-Z]{2}\d+$/.test(trimmed) || trimmed.length > 60;
    
    // Check if line is a heading (all caps, but NOT VAT IDs or very long text)
    const isMainHeading = !isVATorID && trimmed === trimmed.toUpperCase() && trimmed.length < 50 && trimmed.length > 2;
    
    // Check for numbered headings (e.g., "1. Datenschutz auf einen Blick", "2. Verantwortliche Stelle")
    const isNumberedHeading = /^\d+\.\s+[A-ZÄÖÜ]/.test(trimmed);
    
    // Check for section headings with § (e.g., "§ 1 Geltungsbereich", "§ 2 Affiliate-Portal")
    const isParagraphHeading = /^§\s*\d+/.test(trimmed);
    
    // Check for bold sub-headings (Server-Log-Dateien, Newsletter-Anmeldung, etc.)
    const isSubHeading = /^(Server-Log|Newsletter|Allgemeine Hinweise|Cookies|Kontaktformular|Anfrage per E-Mail|Registrierung|Kommentarfunktion|Plugins|Speicherdauer)/i.test(trimmed);
    
    // Check for other section headings
    const isSectionHeading = /^(Angaben|Kontakt|Umsatzsteuer|Verantwortlich|EU-|Verbraucher|Streitschlichtung|Haftung|Urheberrecht|Wichtig:|Keine Haftung)/i.test(trimmed);
    
    // Check if line is a list item (starts with •, -, *, or specific keywords)
    const isListItem = /^[•\-\*]/.test(trimmed) || /^(Lieferzeiten|Versandkosten|Versandmethoden|Tracking|Browsertyp|Browserversion|verwendetes Betriebssystem|Referrer URL|Hostname|Uhrzeit|IP-Adresse)/i.test(trimmed);
    
    if (isMainHeading) {
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h2 class="text-2xl font-bold mb-4 mt-6">${trimmed}</h2>`;
    } else if (isNumberedHeading || isParagraphHeading) {
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h3 class="text-xl font-bold mb-3 mt-5">${trimmed}</h3>`;
    } else if (isSubHeading) {
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h4 class="text-lg font-bold mb-2 mt-4">${trimmed}</h4>`;
    } else if (isSectionHeading) {
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h4 class="text-lg font-semibold mb-2 mt-4">${trimmed}</h4>`;
    } else if (isListItem) {
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      if (!inList) {
        html += '<ul class="list-disc pl-6 mb-3">';
        inList = true;
      }
      
      // Remove bullet character if present
      let itemText = trimmed.replace(/^[•\-\*]\s*/, '');
      
      // Make links clickable
      itemText = itemText
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" class="text-blue-600 hover:underline">$1</a>');
      
      html += `<li class="mb-1">${itemText}</li>`;
    } else {
      // Regular text - add to paragraph
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      if (!inParagraph) {
        html += '<p class="mb-3">';
        inParagraph = true;
      } else {
        html += '<br>';
      }
      
      // Make links clickable
      let formattedLine = trimmed
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" class="text-blue-600 hover:underline">$1</a>');
      
      html += formattedLine;
    }
  });
  
  if (inParagraph) {
    html += '</p>';
  }
  if (inList) {
    html += '</ul>';
  }
  
  return html;
};

// Footer Component
const Footer = ({ onNavigate, settings, onSetActiveTab }) => {
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);

  const handleLogoClick = () => {
    // Triple click to access admin login
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (clickTimer) clearTimeout(clickTimer);
    
    if (newCount === 3) {
      // Open admin login on triple click
      if (onSetActiveTab) {
        onSetActiveTab('add-product');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setClickCount(0);
    } else {
      // Reset counter after 1 second
      const timer = setTimeout(() => setClickCount(0), 1000);
      setClickTimer(timer);
    }
  };

  const handleLoginClick = () => {
    // Direct login access with single click
    if (onSetActiveTab) {
      onSetActiveTab('add-product');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white mt-16">
      {/* Main Footer */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col items-center space-y-4">
          {/* Company Info - Centered */}
          <div className="text-center">
            {/* Footer Logo - Only */}
            <div 
              className="flex items-center justify-center mb-2 cursor-pointer hover:scale-110 transition-transform duration-300"
              onClick={handleLogoClick}
              title="imported24.de"
            >
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-2 rounded-xl shadow-2xl">
                <svg width="40" height="40" viewBox="0 0 32 32" className="text-white">
                  <path fill="currentColor" d="M16 2L4 8v8c0 7.73 5.8 14.67 13.71 15.96L18 32c.33 0 .67-.03 1-.08L31 26v-8L16 2z"/>
                  <path fill="rgba(255,255,255,0.8)" d="M16 6L8 10v6c0 4.84 3.63 9.17 8.57 9.98L17 26c.17 0 .33-.02.5-.04L26 20v-6L16 6z"/>
                  <text x="16" y="18" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif">24</text>
                </svg>
              </div>
            </div>
          </div>

          {/* Links - Horizontal */}
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <button 
              onClick={() => onNavigate('impressum')}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              📄 Impressum
            </button>
            <button 
              onClick={() => onNavigate('agb')}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              📋 AGB
            </button>
            <button 
              onClick={() => onNavigate('datenschutz')}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              🔒 Datenschutz
            </button>
            <a 
              href={`mailto:${settings.email}`} 
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              ✉️ Kontakt
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="text-center text-gray-400 text-sm">
            © {new Date().getFullYear()} imported24.de - Alle Rechte vorbehalten
          </div>
        </div>
      </div>
    </footer>
  );
};

// Scroll to Top Button Component
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);

    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-blue-600 via-purple-600 to-magenta-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 z-50"
          aria-label="Nach oben scrollen"
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
        </button>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const [siteSettings, setSiteSettings] = useState({});

  useEffect(() => {
    // Load settings for hero GIF
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  const heroGifUrl = siteSettings.hero_gif_url || "https://media.giphy.com/media/3oKIPEqDGUULpEU0aQ/giphy.gif";
  const overlayEnabled = siteSettings.hero_overlay_enabled !== false; // Default true

  return (
    <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-magenta-600 text-white py-12">
      <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
        {/* Animated GIF Background - 5% größer */}
        <img 
          src={heroGifUrl}
          alt="Shopping Animation"
          className="w-full md:w-3/4 h-76 md:h-[357px] object-contain opacity-20"
        />
        {/* Halbtransparente Farbschicht - optional */}
        {overlayEnabled && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/75 via-purple-600/75 to-magenta-600/75"></div>
        )}
      </div>
      
      <div className="relative container mx-auto px-4 text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-4 mt-4 animate-fade-in">
          🔍 Wir suchen für Sie die 
          <span className="block text-yellow-300 animate-pulse">Neuheiten & Schnäppchen</span>
        </h2>
        
        <p className="text-lg md:text-xl mb-6 max-w-2xl mx-auto opacity-90">
          ✈️ Schneller Versand aus dem Ausland<br/>
          💰 Die besten Preise für Sie gefunden<br/>
          🎯 Handverlesene Produkte direkt von AliExpress
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
          <button 
            onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
            className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            🛍️ Jetzt Schnäppchen entdecken
          </button>
          <div className="text-center">
            <div className="text-sm opacity-75">Über 1000+ Produkte verfügbar</div>
            <div className="flex items-center justify-center mt-2 space-x-2">
              <span className="w-3 h-3 bg-green-400 rounded-full animate-ping"></span>
              <span className="text-sm">Live aktualisiert</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Scrolling Deals Banner Component
const ScrollingDealsBanner = () => {
  const deals = [
    { text: "🔥 MEGA DEAL: Bluetooth Kopfhörer bis -60%", link: "#" },
    { text: "⚡ BLITZANGEBOT: Smartphone Hüllen ab 9,99€", link: "#" },
    { text: "💥 HOT: Smart Watches bis -40% reduziert", link: "#" },
    { text: "🎯 TOP: LED Strips 5m nur 19,99€", link: "#" },
    { text: "🔥 HAMMER: Fitness Tracker ab 29,99€", link: "#" },
    { text: "⭐ BESTSELLER: Gaming Zubehör bis -35%", link: "#" },
    { text: "💰 KRASS: Powerbanks ab 12,99€", link: "#" },
    { text: "🚀 NEU: Wireless Charger nur 16,99€", link: "#" },
  ];

  return (
    <div className="bg-gradient-to-r from-red-500 via-pink-500 to-red-600 text-white py-2 overflow-hidden relative">
      <div className="flex animate-scroll whitespace-nowrap">
        {/* Dupliziere die Angebote für endloses Scrollen */}
        {[...deals, ...deals].map((deal, index) => (
          <div key={index} className="flex-shrink-0 mx-8">
            <a 
              href={deal.link}
              className="hover:text-yellow-300 transition-colors duration-200 font-semibold text-sm"
            >
              {deal.text}
            </a>
          </div>
        ))}
      </div>
      
      {/* Gradient Fade für sanften Übergang */}
      <div className="absolute top-0 left-0 w-20 h-full bg-gradient-to-r from-red-500 to-transparent z-10"></div>
      <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-red-600 to-transparent z-10"></div>
    </div>
  );
};
// Product Image Banner Component - Neuheit & Schnäppchen (Auto-Scroll Carousel)
const ProductImageBanner = ({ featuredProducts, onProductClick, onAddToWishlist, wishlistEnabled = true }) => {
  const [isPaused, setIsPaused] = React.useState(false);
  
  // Get latest 18 products
  const displayProducts = featuredProducts?.slice(0, 18) || [];

  if (displayProducts.length === 0) {
    return null;
  }
  
  // Duplicate products for seamless infinite scroll
  const duplicatedProducts = [...displayProducts, ...displayProducts];

  return (
    <section className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 py-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
              🔥 Neuheiten & Schnäppchen
            </span>
          </h2>
        </div>

        {/* Scrolling Banner Container */}
        <div 
          className="relative overflow-hidden"
        >
          <div 
            className="flex gap-4 animate-scroll"
            style={{ 
              animationPlayState: isPaused ? 'paused' : 'running',
              width: 'max-content'
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {duplicatedProducts.map((product, index) => (
              <div 
                key={`${product.id || index}-${index}`}
                className="group relative bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden flex-shrink-0"
                style={{ width: '220px' }}
              >
              {/* Product Image */}
              <div className="aspect-square relative overflow-hidden">
                <img
                  src={product.main_image || product.images?.[0] || "https://via.placeholder.com/200x200"}
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                
                {/* Discount Badge */}
                {product.discount_percentage > 0 && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                    -{product.discount_percentage}%
                  </div>
                )}
                
                {/* Wishlist Button */}
                {wishlistEnabled && onAddToWishlist && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddToWishlist(product);
                    }}
                    className="absolute top-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 text-red-500 p-1.5 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                    title="Zur Wunschliste hinzufügen"
                  >
                    ❤️
                  </button>
                )}
                
                {/* Quick View Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button 
                      onClick={() => onProductClick && onProductClick(product)}
                      className="bg-white text-gray-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-gray-100"
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-3">
                <h3 className="text-sm font-medium text-gray-800 line-clamp-2 mb-2 h-10">
                  {product.title}
                </h3>
                
                {/* Price Section */}
                <div className="mb-2">
                  {/* Old Price (if available) */}
                  {(product.original_price || product.price?.market_price) && 
                   (product.original_price || product.price?.market_price) !== (product.price?.selling_price || product.selling_price) && (
                    <div className="text-xs text-gray-500 line-through mb-1">
                      Alter Preis: {(product.original_price || product.price?.market_price || 0).toFixed(2)}&nbsp;€
                    </div>
                  )}
                  
                  {/* Current Price */}
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-blue-600 whitespace-nowrap">
                      {(product.price?.selling_price || product.selling_price || product.market_price || product.price || 0).toFixed(2)}&nbsp;€
                    </span>
                    
                    {/* Quick Buy Button */}
                    <button 
                      onClick={() => {
                        const affiliateLink = product.affiliate_link || product.aliexpress_url || '#';
                        window.open(affiliateLink, '_blank');
                      }}
                      className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-1 rounded text-xs font-medium hover:from-green-600 hover:to-green-700 transition-all duration-300"
                    >
                      Kaufen
                    </button>
                  </div>
                </div>
                
                {/* Rating and Sales Count */}
                <div className="flex items-center justify-between mt-2">
                  {product.rating ? (
                    <div className="flex items-center">
                      <div className="flex items-center text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-3 h-3 ${i < Math.floor(product.rating) ? 'fill-current' : 'text-gray-300'}`}
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="ml-1 text-xs text-gray-600">
                        {product.rating.toFixed(1)}
                      </span>
                    </div>
                  ) : (
                    <div></div>
                  )}
                  
                  {/* Sales Count */}
                  {(product.sales_count || product.orders) && (
                    <div className="text-xs text-gray-600">
                      📦 {(product.sales_count || product.orders || 0).toLocaleString('de-DE')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
      </div>
    </section>
  );
};

// New Product Entry Component with Promo Material, Image ZIP, and Description
const NewProductEntry = ({ setActiveTab }) => {
  const [inputMode, setInputMode] = useState('promo'); // 'promo' oder 'manual'
  const [imageUploadMode, setImageUploadMode] = useState('zip'); // 'zip' oder 'files'
  const [promoMaterial, setPromoMaterial] = useState('');
  const [imageZipFile, setImageZipFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  
  // Duplikat-Prüfung
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [pendingProductData, setPendingProductData] = useState(null);
  
  // Manuelle Eingabefelder
  const [manualData, setManualData] = useState({
    trackingLink: '',
    title: '',
    price: '',
    oldPrice: ''
  });

  const categories = [
    { id: 'electronics', name: 'Elektronik 📱', commission: '5.5%' },
    { id: 'gadgets', name: 'Gadgets 🔧', commission: '6.0%' },
    { id: 'home', name: 'Haushalt 🏠', commission: '4.5%' },
    { id: 'decoration', name: 'Dekoration 🎨', commission: '7.0%' },
    { id: 'leisure', name: 'Freizeit ⚽', commission: '5.0%' }
  ];

  const [category, setCategory] = useState('electronics');
  const [additionalData, setAdditionalData] = useState({
    rating: 4.5,
    reviewCount: 500,
    salesCount: 1000
  });

  // Parse promo material
  const parsePromoMaterial = () => {
    try {
      const lines = promoMaterial.split('\n').filter(line => line.trim());
      
      let title = '';
      let price = '';
      let originalPrice = '';
      let discount = '';
      let trackingLink = '';

      for (const line of lines) {
        // Find price line with both current and original price
        if (line.includes('Price Now:') || line.includes('EUR')) {
          // Extract current price
          const priceMatch = line.match(/Price Now:\s*EUR\s*([\d.]+)/i) || line.match(/EUR\s*([\d.]+)/);
          if (priceMatch) {
            price = priceMatch[1];
          }
          
          // Extract original price
          const originalPriceMatch = line.match(/Original price:\s*EUR\s*([\d.]+)/i) || line.match(/\(EUR\s*([\d.]+)/);
          if (originalPriceMatch) {
            originalPrice = originalPriceMatch[1];
          }
          
          // Extract discount percentage
          const discountMatch = line.match(/(\d+)%\s*off/i);
          if (discountMatch) {
            discount = discountMatch[1];
          }
        }
        
        // Find tracking link
        if (line.includes('https://s.click.aliexpress.com')) {
          const linkMatch = line.match(/(https:\/\/s\.click\.aliexpress\.com\/e\/[^\s]+)/);
          if (linkMatch) {
            trackingLink = linkMatch[1];
          }
        }
        
        // Title is usually the longest line or contains product info
        if (!line.includes('Price') && !line.includes('Click') && !line.includes('EUR') && 
            !line.includes('Top On Sale') && !line.includes('🔗') && line.length > 20) {
          if (!title || line.length > title.length) {
            title = line;
          }
        }
      }

      // Calculate discount if not found but both prices are available
      if (!discount && price && originalPrice) {
        const discountCalc = Math.round(((parseFloat(originalPrice) - parseFloat(price)) / parseFloat(originalPrice)) * 100);
        discount = discountCalc.toString();
      }

      if (title && price && trackingLink) {
        const parsedInfo = { 
          title, 
          price, 
          originalPrice: originalPrice || price,
          discount: discount || '0',
          savings: originalPrice ? (parseFloat(originalPrice) - parseFloat(price)).toFixed(2) : '0',
          trackingLink 
        };
        setParsedData(parsedInfo);
        return parsedInfo;
      } else {
        throw new Error('Konnte nicht alle erforderlichen Daten extrahieren');
      }
    } catch (error) {
      console.error('Parse error:', error);
      setSubmitResult({ success: false, message: `Fehler beim Parsen: ${error.message}` });
      return null;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if it's a ZIP file by extension or mime type
      const isZip = file.name.toLowerCase().endsWith('.zip') || 
                    file.type === 'application/zip' || 
                    file.type === 'application/x-zip-compressed' ||
                    file.type === 'application/x-zip';
      
      if (isZip) {
        console.log('ZIP-Datei ausgewählt:', file.name, 'Größe:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        setImageZipFile(file);
        setSubmitResult({ success: true, message: `ZIP-Datei "${file.name}" bereit zum Upload (${(file.size / 1024 / 1024).toFixed(2)} MB)` });
      } else {
        alert(`Bitte wählen Sie eine ZIP-Datei aus. Gewählte Datei: ${file.name} (${file.type})`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let productData;
    
    if (inputMode === 'promo') {
      // Parse promo material first
      const parsed = parsePromoMaterial();
      if (!parsed) {
        return;
      }
      productData = parsed;
    } else {
      // Manuelle Eingabe validieren
      if (!manualData.trackingLink || !manualData.title || !manualData.price) {
        setSubmitResult({ success: false, message: 'Bitte füllen Sie alle Pflichtfelder aus (Tracking-Link, Titel, Preis)' });
        return;
      }
      productData = {
        trackingLink: manualData.trackingLink,
        title: manualData.title,
        price: manualData.price
      };
    }

    if (!imageZipFile && imageFiles.length === 0) {
      setSubmitResult({ success: false, message: 'Bitte wählen Sie eine ZIP-Datei oder Bilddateien aus' });
      return;
    }

    // Duplikat-Prüfung durchführen
    try {
      const checkFormData = new FormData();
      checkFormData.append('title', productData.title);
      checkFormData.append('aliexpress_url', productData.trackingLink);
      
      const checkResponse = await axios.post(`${API}/products/check-duplicate`, checkFormData);
      
      if (checkResponse.data.has_duplicates) {
        // Zeige Duplikat-Modal
        setDuplicates(checkResponse.data.duplicates);
        setPendingProductData(productData);
        setShowDuplicateModal(true);
        return; // Warte auf Benutzerentscheidung
      }
    } catch (error) {
      console.error('Fehler bei Duplikat-Prüfung:', error);
      // Bei Fehler: Fortfahren ohne Prüfung
    }

    // Kein Duplikat gefunden oder Benutzer hat "Fortfahren" gewählt
    await submitProduct(productData);
  };

  const submitProduct = async (productData) => {
    if (!imageZipFile && imageFiles.length === 0) {
      setSubmitResult({ success: false, message: 'Bitte wählen Sie eine ZIP-Datei oder Bilddateien aus' });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('aliexpress_url', productData.trackingLink);
      formData.append('title', productData.title);
      formData.append('price', productData.price);
      formData.append('category', category);
      formData.append('description', description || `Hochwertiges ${productData.title} - Direkt von AliExpress importiert. Schnelle Lieferung nach Deutschland.`);
      
      // Add promo_text (raw promo material)
      if (inputMode === 'promo' && promoMaterial) {
        formData.append('promo_text', promoMaterial);
      }
      
      // Add original_price if available
      if (inputMode === 'promo' && productData.originalPrice && productData.originalPrice !== productData.price) {
        formData.append('original_price', productData.originalPrice);
      } else if (inputMode === 'manual' && manualData.oldPrice) {
        formData.append('original_price', manualData.oldPrice);
      }
      
      // Bilder anhängen - entweder ZIP oder einzelne Dateien
      if (imageUploadMode === 'zip' && imageZipFile) {
        formData.append('image_zip', imageZipFile);
      } else if (imageUploadMode === 'files' && imageFiles.length > 0) {
        imageFiles.forEach((file, index) => {
          formData.append('images', file);
        });
      }
      
      // Additional data
      formData.append('rating', additionalData.rating.toString());
      formData.append('review_count', additionalData.reviewCount.toString());
      formData.append('sales_count', additionalData.salesCount.toString());

      const response = await axios.post(`${API}/products/manual/add-with-zip`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSubmitResult({
          success: true,
          message: response.data.message,
          details: {
            final_price: response.data.final_price,
            commission_rate: response.data.commission_rate
          }
        });
        
        // Reset form
        setPromoMaterial('');
        setManualData({ trackingLink: '', title: '', price: '', oldPrice: '' });
        setImageZipFile(null);
        setDescription('');
        setParsedData(null);
        setAdditionalData({
          rating: 4.5,
          reviewCount: 500,
          salesCount: 1000
        });
        
        // Wechsel zurück zur Produktverwaltung
        setTimeout(() => {
          setActiveTab('admin-manage');
        }, 2000);
      } else {
        setSubmitResult({ success: false, message: response.data.message });
      }

    } catch (error) {
      console.error('Product submission failed:', error);
      setSubmitResult({ 
        success: false, 
        message: error.response?.data?.message || 'Fehler beim Hinzufügen des Produkts' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Duplikat-Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-orange-600 mb-4">
                ⚠️ Mögliche Duplikate gefunden
              </h3>
              <p className="text-gray-700 mb-4">
                Es wurden bereits ähnliche Produkte in der Datenbank gefunden. Bitte überprüfen Sie:
              </p>
              
              <div className="space-y-3 mb-6">
                {duplicates.map((dup, index) => (
                  <div key={index} className="border border-orange-200 bg-orange-50 rounded-lg p-4 flex gap-4">
                    {dup.image && (
                      <img 
                        src={dup.image} 
                        alt={dup.title}
                        className="w-20 h-20 object-cover rounded"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-block px-2 py-0.5 bg-blue-100 rounded text-sm font-bold text-blue-800">
                          #{dup.product_number || '-'}
                        </span>
                        <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                          {dup.match_type}
                        </span>
                      </div>
                      <p className="font-semibold text-gray-900">{dup.title}</p>
                      {dup.created_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Erstellt: {new Date(dup.created_at).toLocaleDateString('de-DE')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDuplicateModal(false);
                    setDuplicates([]);
                    setPendingProductData(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                >
                  ❌ Abbrechen
                </button>
                <button
                  onClick={async () => {
                    setShowDuplicateModal(false);
                    await submitProduct(pendingProductData);
                    setDuplicates([]);
                    setPendingProductData(null);
                  }}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
                >
                  ✓ Trotzdem hinzufügen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h3 className="text-3xl font-bold mb-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          🚀 Neues Produkt hinzufügen
        </h3>
        <p className="text-gray-600">
          Fügen Sie AliExpress-Produkte mit Promo-Material oder manuellen Daten hinzu.
        </p>
      </div>

      {/* Toggle zwischen Promo-Material und Manuell */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1 flex gap-2">
          <button
            type="button"
            onClick={() => setInputMode('promo')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
              inputMode === 'promo'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📋 Mit Promo-Material
          </button>
          {inputMode === 'promo' && (
            <button
              type="button"
              onClick={() => {
                const parsed = parsePromoMaterial();
                if (parsed) {
                  setParsedData(parsed);
                }
              }}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-all"
            >
              ✓ Promo überprüfen
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setInputMode('manual')}
          className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
            inputMode === 'manual'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ✍️ Manuelle Eingabe
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* 1. Produktdaten - Promo-Material oder Manuell */}
        {inputMode === 'promo' ? (
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
            <h4 className="text-xl font-semibold mb-4 text-gray-800">
              1️⃣ Promo-Material *
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Kopieren Sie das komplette Promo-Material (enthält Titel, Preis, Tracking-Link)
            </p>
            <textarea
              value={promoMaterial}
              onChange={(e) => setPromoMaterial(e.target.value)}
              rows="8"
              placeholder={`Top On Sale Product Recommendations!
Wireless LED Night Light Sensor Lighting Mini EU/US Plug Night Lights For Children Room Bedroom Living Room Decoration Lighting
Price Now: EUR 1.09 (Original price: EUR 3.11,  65% off)
🔗Click & Buy: https://s.click.aliexpress.com/e/_c2uj4SX9`}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              required={inputMode === 'promo'}
            />
            
            {parsedData && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h5 className="font-semibold text-green-800 mb-2">✅ Erkannte Daten:</h5>
                <div className="text-sm text-green-700 space-y-1">
                  <p><strong>Titel:</strong> {parsedData.title}</p>
                  <p><strong>Aktueller Preis:</strong> {parsedData.price}&nbsp;€</p>
                  {parsedData.originalPrice !== parsedData.price && (
                    <>
                      <p><strong>Originalpreis:</strong> {parsedData.originalPrice}&nbsp;€</p>
                      <p><strong>Ersparnis:</strong> {parsedData.savings}&nbsp;€ ({parsedData.discount}% Rabatt) 💰</p>
                    </>
                  )}
                  <p><strong>Tracking-Link:</strong> {parsedData.trackingLink.substring(0, 50)}...</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500">
            <h4 className="text-xl font-semibold mb-4 text-gray-800">
              1️⃣ Manuelle Produktdaten *
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AliExpress Tracking-Link *
                </label>
                <input
                  type="text"
                  value={manualData.trackingLink}
                  onChange={(e) => setManualData({...manualData, trackingLink: e.target.value})}
                  placeholder="https://s.click.aliexpress.com/e/_xxxxx"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required={inputMode === 'manual'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produkttitel *
                </label>
                <input
                  type="text"
                  value={manualData.title}
                  onChange={(e) => setManualData({...manualData, title: e.target.value})}
                  placeholder="z.B. Wireless LED Night Light"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required={inputMode === 'manual'}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aktueller Preis (EUR) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualData.price}
                    onChange={(e) => setManualData({...manualData, price: e.target.value})}
                    placeholder="1.09"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required={inputMode === 'manual'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alter Preis (EUR)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualData.oldPrice}
                    onChange={(e) => setManualData({...manualData, oldPrice: e.target.value})}
                    placeholder="3.11"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional - für Rabattberechnung</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 1. Promo-Material */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500" style={{display: 'none'}}>
          <h4 className="text-xl font-semibold mb-4 text-gray-800">
            1️⃣ Promo-Material *
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Kopieren Sie das komplette Promo-Material (enthält Titel, Preis, Tracking-Link)
          </p>
          <textarea
            value={promoMaterial}
            onChange={(e) => setPromoMaterial(e.target.value)}
            rows="8"
            placeholder={`Top On Sale Product Recommendations!
Wireless LED Night Light Sensor Lighting Mini EU/US Plug Night Lights For Children Room Bedroom Living Room Decoration Lighting
Price Now: EUR 1.09 (Original price: EUR 3.11,  65% off)
🔗Click & Buy: https://s.click.aliexpress.com/e/_c2uj4SX9`}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            required
          />
          
          {parsedData && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h5 className="font-semibold text-green-800 mb-2">✅ Erkannte Daten:</h5>
              <div className="text-sm text-green-700 space-y-1">
                <p><strong>Titel:</strong> {parsedData.title}</p>
                <p><strong>Aktueller Preis:</strong> {parsedData.price}&nbsp;€</p>
                {parsedData.originalPrice !== parsedData.price && (
                  <>
                    <p><strong>Originalpreis:</strong> {parsedData.originalPrice}&nbsp;€</p>
                    <p><strong>Ersparnis:</strong> {parsedData.savings}&nbsp;€ ({parsedData.discount}% Rabatt) 💰</p>
                  </>
                )}
                <p><strong>Link:</strong> {parsedData.trackingLink.substring(0, 50)}...</p>
              </div>
            </div>
          )}
          
          <button
            type="button"
            onClick={parsePromoMaterial}
            className="mt-4 bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition"
          >
            📋 Promo-Material analysieren
          </button>
        </div>

        {/* 2. Bilderpaket ZIP oder einzelne Dateien */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500">
          <h4 className="text-xl font-semibold mb-4 text-gray-800">
            2️⃣ Bilderpaket *
          </h4>
          
          {/* Toggle für Upload-Typ */}
          <div className="mb-4 flex gap-3">
            <button
              type="button"
              onClick={() => {
                setImageUploadMode('zip');
                setImageFiles([]);
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                imageUploadMode === 'zip'
                  ? 'bg-purple-600 text-white shadow'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📦 ZIP-Datei
            </button>
            <button
              type="button"
              onClick={() => {
                setImageUploadMode('files');
                setImageZipFile(null);
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                imageUploadMode === 'files'
                  ? 'bg-purple-600 text-white shadow'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🖼️ Bilddateien
            </button>
          </div>

          {imageUploadMode === 'zip' ? (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Laden Sie die ZIP-Datei mit allen Produktbildern hoch
              </p>
              
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition cursor-pointer"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-purple-500', 'bg-purple-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                  const file = e.dataTransfer.files[0];
                  if (file && file.name.endsWith('.zip')) {
                    setImageZipFile(file);
                  } else {
                    alert('Bitte nur ZIP-Dateien hochladen');
                  }
                }}
                onClick={() => document.getElementById('zip-upload').click()}
              >
                <input
                  type="file"
                  accept=".zip,application/zip,application/x-zip,application/x-zip-compressed"
                  onChange={handleFileChange}
                  className="hidden"
                  id="zip-upload"
                />
                <div className="mb-4">
                  <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-700">
                  {imageZipFile ? (
                    <span className="text-green-600">✓ {imageZipFile.name}</span>
                  ) : (
                    <>
                      Ziehen Sie die ZIP-Datei hierher oder <span className="text-purple-600">klicken zum Hochladen</span>
                    </>
                  )}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Unterstützt: .zip
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Laden Sie einzelne Bilddateien hoch (mehrere möglich)
              </p>
              
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition cursor-pointer"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-purple-500', 'bg-purple-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                  const files = Array.from(e.dataTransfer.files).filter(file => 
                    file.type.startsWith('image/')
                  );
                  if (files.length > 0) {
                    setImageFiles(files);
                  } else {
                    alert('Bitte nur Bilddateien hochladen');
                  }
                }}
                onClick={() => document.getElementById('images-upload').click()}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setImageFiles(files);
                  }}
                  className="hidden"
                  id="images-upload"
                />
                <div className="mb-4">
                  <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-700">
                  {imageFiles.length > 0 ? (
                    <span className="text-green-600">✓ {imageFiles.length} Bild(er) ausgewählt</span>
                  ) : (
                    <>
                      Ziehen Sie Bilder hierher oder <span className="text-purple-600">klicken zum Hochladen</span>
                    </>
                  )}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Unterstützt: JPG, PNG, WEBP, etc.
                </p>
              </div>
              
              {imageFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {imageFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImageFiles(imageFiles.filter((_, i) => i !== index));
                        }}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                      <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* 2. Bilderpaket ZIP (versteckt, nur für Struktur) */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500" style={{display: 'none'}}>
          <h4 className="text-xl font-semibold mb-4 text-gray-800">
            2️⃣ Bilderpaket (ZIP-Datei) *
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Laden Sie die ZIP-Datei mit allen Produktbildern hoch
          </p>
          
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition cursor-pointer"
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add('border-purple-500', 'bg-purple-50');
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
              const file = e.dataTransfer.files[0];
              if (file && file.name.endsWith('.zip')) {
                setImageZipFile(file);
              } else {
                alert('Bitte nur ZIP-Dateien hochladen');
              }
            }}
            onClick={() => document.getElementById('zip-upload').click()}
          >
            <input
              type="file"
              accept=".zip,application/zip,application/x-zip,application/x-zip-compressed"
              onChange={handleFileChange}
              className="hidden"
              id="zip-upload"
            />
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-700">
              ZIP-Datei hierher ziehen oder klicken zum Auswählen
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Unterstützte Formate: .zip (max. 50MB)
            </p>
          </div>
          
          {imageZipFile && (
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <p className="font-medium text-purple-900">{imageZipFile.name}</p>
                  <p className="text-sm text-purple-600">{(imageZipFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setImageZipFile(null)}
                className="text-red-500 hover:text-red-700"
              >
                ❌
              </button>
            </div>
          )}
        </div>

        {/* 3. Artikelbeschreibung */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500">
          <h4 className="text-xl font-semibold mb-4 text-gray-800">
            3️⃣ Artikelbeschreibung
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Fügen Sie eine detaillierte Produktbeschreibung hinzu (optional)
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="6"
            placeholder="Detaillierte Produktbeschreibung... (wird automatisch generiert, wenn leer gelassen)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* Kategorie */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-orange-500">
          <h4 className="text-xl font-semibold mb-4 text-gray-800">
            📂 Kategorie (bestimmt Provision) *
          </h4>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-lg"
            required
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name} - {cat.commission} Provision
              </option>
            ))}
          </select>
        </div>

        {/* Zusätzliche Produktdaten */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-indigo-500">
          <h4 className="text-xl font-semibold mb-4 text-gray-800">
            ⭐ Zusätzliche Produktinformationen
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Diese Daten erhöhen die Glaubwürdigkeit des Produkts
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Sterne */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ⭐ Sternebewertung (1.0 - 5.0)
              </label>
              <input
                type="number"
                step="0.1"
                min="1.0"
                max="5.0"
                value={additionalData.rating}
                onChange={(e) => setAdditionalData({...additionalData, rating: parseFloat(e.target.value) || 4.5})}
                placeholder="4.7"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">z.B. 4.7</p>
            </div>
            
            {/* Bewertungen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                💬 Anzahl Bewertungen
              </label>
              <input
                type="number"
                min="0"
                value={additionalData.reviewCount}
                onChange={(e) => setAdditionalData({...additionalData, reviewCount: parseInt(e.target.value) || 0})}
                placeholder="523"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">z.B. 523 Bewertungen</p>
            </div>
            
            {/* Verkaufsmenge */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📦 Verkaufsmenge
              </label>
              <input
                type="number"
                min="0"
                value={additionalData.salesCount}
                onChange={(e) => setAdditionalData({...additionalData, salesCount: parseInt(e.target.value) || 0})}
                placeholder="5000"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">z.B. +5.000 verkauft</p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center pt-6 border-t">
          <button
            type="submit"
            disabled={isSubmitting || !parsedData}
            className={`px-12 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
              isSubmitting || !parsedData
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 via-purple-600 to-magenta-600 hover:from-blue-700 hover:via-purple-700 hover:to-magenta-700 transform hover:scale-105 shadow-lg hover:shadow-xl'
            } text-white`}
          >
            {isSubmitting ? '🔄 Wird verarbeitet...' : '🚀 Produkt hinzufügen'}
          </button>
          {!parsedData && (
            <p className="text-sm text-gray-500 mt-2">
              Bitte analysieren Sie zuerst das Promo-Material
            </p>
          )}
        </div>
      </form>

      {/* Results */}
      {submitResult && (
        <div className={`mt-8 p-6 rounded-lg border ${
          submitResult.success 
            ? 'bg-green-50 border-green-300 text-green-800'
            : 'bg-red-50 border-red-300 text-red-800'
        }`}>
          <div className="font-bold text-xl mb-2">{submitResult.message}</div>
          {submitResult.success && submitResult.details && (
            <div className="text-lg mt-3">
              <p>💰 Finaler Preis: {submitResult.details.final_price}&nbsp;€</p>
              <p>📊 Provision: {submitResult.details.commission_rate}%</p>
              <p className="mt-4 text-blue-600">🎉 Seite wird in 2 Sekunden aktualisiert...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Simplified Product Entry Component - Only Tracking-ID + Image needed
const QuickProductEntry = () => {
  const [products, setProducts] = useState([
    {
      id: 1,
      tracking_id: '',
      image_url: '',
      title: '',
      price: '',
      category: 'electronics'
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResults, setSubmitResults] = useState([]);

  const categories = [
    { id: 'electronics', name: 'Elektronik 📱', commission: '5.5%' },
    { id: 'gadgets', name: 'Gadgets 🔧', commission: '6.0%' },
    { id: 'home', name: 'Haushalt 🏠', commission: '4.5%' },
    { id: 'decoration', name: 'Dekoration 🎨', commission: '7.0%' },
    { id: 'leisure', name: 'Freizeit ⚽', commission: '5.0%' }
  ];

  const addProductRow = () => {
    const newId = Math.max(...products.map(p => p.id)) + 1;
    setProducts([...products, {
      id: newId,
      tracking_id: '',
      image_url: '',
      title: '',
      price: '',
      category: 'electronics'
    }]);
  };

  const removeProductRow = (id) => {
    if (products.length > 1) {
      setProducts(products.filter(p => p.id !== id));
    }
  };

  const updateProduct = (id, field, value) => {
    setProducts(products.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const extractImageFromHtml = (htmlString) => {
    // Extract image URL from HTML like: <img src="//ae01.alicdn.com/kf/...jpg" />
    const match = htmlString.match(/src=["']([^"']+)["']/);
    if (match) {
      let imageUrl = match[1];
      if (imageUrl.startsWith('//')) {
        imageUrl = 'https:' + imageUrl;
      }
      return imageUrl;
    }
    return htmlString; // Return as-is if no HTML found
  };

  const validateProducts = () => {
    const errors = [];
    products.forEach((product, index) => {
      if (!product.tracking_id.trim()) {
        errors.push(`Produkt ${index + 1}: Tracking-ID ist erforderlich`);
      }
      if (!product.image_url.trim()) {
        errors.push(`Produkt ${index + 1}: Bild-URL ist erforderlich`);
      }
      if (!product.title.trim()) {
        errors.push(`Produkt ${index + 1}: Titel ist erforderlich`);
      }
      if (!product.price || parseFloat(product.price) <= 0) {
        errors.push(`Produkt ${index + 1}: Gültiger Preis ist erforderlich`);
      }
    });
    return errors;
  };

  const submitAllProducts = async () => {
    const errors = validateProducts();
    if (errors.length > 0) {
      setSubmitResults([{ success: false, message: errors.join(', ') }]);
      return;
    }

    setIsSubmitting(true);
    setSubmitResults([]);
    
    const results = [];
    
    for (const product of products) {
      try {
        const imageUrl = extractImageFromHtml(product.image_url);
        
        const submitData = {
          aliexpress_url: product.tracking_id,
          title: product.title,
          price: parseFloat(product.price),
          category: product.category,
          images: [imageUrl],
          description: `Hochwertiges ${product.title} - Direkt von AliExpress importiert. Schnelle Lieferung nach Deutschland.`
        };

        const response = await axios.post(`${API}/products/manual/add`, submitData);
        
        if (response.data.success) {
          results.push({
            success: true,
            title: product.title,
            message: `✅ ${product.title} erfolgreich hinzugefügt`,
            final_price: response.data.final_price,
            commission_rate: response.data.commission_rate
          });
        } else {
          results.push({
            success: false,
            title: product.title,
            message: `❌ ${product.title}: ${response.data.message}`
          });
        }

      } catch (error) {
        results.push({
          success: false,
          title: product.title,
          message: `❌ ${product.title}: ${error.response?.data?.message || 'Fehler beim Hinzufügen'}`
        });
      }
    }
    
    setSubmitResults(results);
    setIsSubmitting(false);
    
    // Reset form if all successful
    if (results.every(r => r.success)) {
      setProducts([{
        id: 1,
        tracking_id: '',
        image_url: '',
        title: '',
        price: '',
        category: 'electronics'
      }]);
      
      // Wechsel zurück zur Produktverwaltung
      setTimeout(() => {
        setActiveTab('admin-manage');
      }, 3000);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h3 className="text-2xl font-semibold mb-3 text-gradient bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          🚀 Schnell-Import: Nur Tracking-ID + Bild benötigt
        </h3>
        <p className="text-gray-600 mb-4">
          Fügen Sie mehrere AliExpress-Produkte gleichzeitig hinzu. Sie benötigen nur die Tracking-ID und das Bild - der Rest wird automatisch berechnet!
        </p>
        
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h4 className="font-semibold text-blue-900 mb-2">📋 So funktioniert's:</h4>
          <ol className="text-sm text-blue-800 space-y-1">
            <li><strong>1.</strong> Tracking-ID von AliExpress kopieren (z.B. https://s.click.aliexpress.com/e/_c3NKbyIL)</li>
            <li><strong>2.</strong> Bild-HTML kopieren (z.B. &lt;img src="//ae01.alicdn.com/kf/..."&gt;)</li>
            <li><strong>3.</strong> Titel und Preis manuell eingeben</li>
            <li><strong>4.</strong> Kategorie wählen → Deutsche MwSt wird automatisch berechnet</li>
          </ol>
        </div>
      </div>

      <div className="space-y-4">
        {products.map((product, index) => (
          <div key={product.id} className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-800">
                Produkt #{index + 1}
              </h4>
              {products.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeProductRow(product.id)}
                  className="text-red-500 hover:text-red-700 font-bold"
                >
                  ❌ Entfernen
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Tracking-ID */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔗 Tracking-ID (AliExpress Link) *
                </label>
                <input
                  type="url"
                  value={product.tracking_id}
                  onChange={(e) => updateProduct(product.id, 'tracking_id', e.target.value)}
                  placeholder="https://s.click.aliexpress.com/e/_c3NKbyIL"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Bild-URL */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🖼️ Bild-HTML oder direkte URL *
                </label>
                <textarea
                  value={product.image_url}
                  onChange={(e) => updateProduct(product.id, 'image_url', e.target.value)}
                  placeholder={`<img src="//ae01.alicdn.com/kf/S44960e19d5d443e991110be8cd6bab8ca.jpg_140x140.jpg" /> 

ODER direkt:

//ae01.alicdn.com/kf/S44960e19d5d443e991110be8cd6bab8ca.jpg_140x140.jpg`}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Titel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📝 Produkttitel *
                </label>
                <input
                  type="text"
                  value={product.title}
                  onChange={(e) => updateProduct(product.id, 'title', e.target.value)}
                  placeholder="z.B. USB-C Kabel 3m Schnellladung"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Preis */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  💰 Preis (EUR) *
                </label>
                <input
                  type="number"
                  value={product.price}
                  onChange={(e) => updateProduct(product.id, 'price', e.target.value)}
                  placeholder="1.39"
                  step="0.01"
                  min="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Kategorie */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📂 Kategorie (bestimmt Provision) *
                </label>
                <select
                  value={product.category}
                  onChange={(e) => updateProduct(product.id, 'category', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} - {cat.commission} Provision
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview */}
              {product.image_url && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    👁️ Bild-Vorschau:
                  </label>
                  <img 
                    src={extractImageFromHtml(product.image_url)}
                    alt="Produktvorschau"
                    className="w-32 h-32 object-cover border rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* Add Product Button */}
        <div className="text-center">
          <button
            type="button"
            onClick={addProductRow}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 font-semibold"
          >
            ➕ Weiteres Produkt hinzufügen
          </button>
        </div>

        {/* Submit All Button */}
        <div className="text-center pt-6 border-t">
          <button
            onClick={submitAllProducts}
            disabled={isSubmitting}
            className={`px-12 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 via-purple-600 to-magenta-600 hover:from-blue-700 hover:via-purple-700 hover:to-magenta-700 transform hover:scale-105 shadow-lg hover:shadow-xl'
            } text-white`}
          >
            {isSubmitting ? '🔄 Wird verarbeitet...' : `🚀 ${products.length} Produkt${products.length > 1 ? 'e' : ''} hinzufügen`}
          </button>
        </div>
      </div>

      {/* Results */}
      {submitResults.length > 0 && (
        <div className="mt-8 space-y-3">
          <h4 className="text-lg font-semibold">📊 Ergebnisse:</h4>
          {submitResults.map((result, index) => (
            <div key={index} className={`p-4 rounded-lg border ${
              result.success 
                ? 'bg-green-50 border-green-300 text-green-800'
                : 'bg-red-50 border-red-300 text-red-800'
            }`}>
              <div className="font-medium">{result.message}</div>
              {result.success && result.final_price && (
                <div className="text-sm mt-1">
                  💰 Finaler Preis: {result.final_price}&nbsp;€ | 📊 Provision: {result.commission_rate}%
                </div>
              )}
            </div>
          ))}
          
          {submitResults.every(r => r.success) && (
            <div className="bg-blue-100 border border-blue-300 text-blue-800 p-4 rounded-lg text-center">
              🎉 Alle Produkte erfolgreich hinzugefügt! Seite wird in 3 Sekunden aktualisiert...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Product Card Component
// Loading skeleton component
const ProductSkeleton = () => (
  <div className="relative bg-white rounded-lg shadow-lg animate-pulse">
    <div className="relative rounded-t-lg">
      <div className="w-full h-48 bg-gray-200 rounded-t-lg"></div>
    </div>
    <div className="p-2 sm:p-4">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="flex items-center justify-between mb-2">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
      </div>
      <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
      <div className="flex gap-1">
        <div className="h-8 bg-gray-200 rounded flex-1"></div>
        <div className="h-8 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  </div>
);

const ProductCard = ({ product, onAddToCart, onViewDetails, onAddToWishlist, isAdminView = false, wishlistEnabled = true }) => {
  // Check if this is an affiliate product
  const isAffiliateProduct = product.affiliate_link;
  
  // Calculate display prices with automatic discount display
  const getDisplayPrices = () => {
    const sellingPrice = product.price?.selling_price || product.selling_price || product.market_price || product.price || 0;
    let marketPrice = product.price?.market_price || product.market_price || sellingPrice;
    
    // If market_price equals selling_price, create a display discount (common e-commerce practice)
    // Add 25-40% to show "original price"
    if (marketPrice === sellingPrice && sellingPrice > 0) {
      marketPrice = sellingPrice * 1.35; // 35% markup for display
    }
    
    const savings = marketPrice - sellingPrice;
    const savingsPercent = marketPrice > 0 ? Math.round((savings / marketPrice) * 100) : 0;
    
    return {
      sellingPrice,
      marketPrice,
      savings,
      savingsPercent,
      hasDiscount: savings > 0.01 // Show discount if savings > 1 cent
    };
  };
  
  const prices = getDisplayPrices();
  
  const handlePurchaseClick = () => {
    if (isAffiliateProduct) {
      // Open affiliate link in new tab for commission tracking
      window.open(product.affiliate_link, '_blank');
      
      // Optional: Track affiliate click
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'affiliate_click', {
          product_id: product.id || product._id,
          product_name: product.title,
          commission_rate: product.commission_rate || 5.0
        });
      }
    } else {
      // Regular add to cart for non-affiliate products
      onAddToCart(product);
    }
  };

  return (
    <div 
      className="relative bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
    >
      {/* Image Container */}
      <div className="relative rounded-t-lg">
        <img
          src={product.main_image || product.images?.[0] || "https://via.placeholder.com/300x200"}
          alt={product.title}
          loading="lazy"
          className="w-full h-48 object-contain bg-gray-50"
        />
        
        {/* Affiliate Badge - nur für Admin */}
        {isAffiliateProduct && isAdminView && (
          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
            {product.commission_rate}% Provision
          </div>
        )}
        
        {product.discount_percentage > 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
            -{product.discount_percentage}%
          </div>
        )}
        
        {/* Wishlist Button */}
        {!isAdminView && wishlistEnabled && onAddToWishlist && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddToWishlist(product);
            }}
            className="absolute bottom-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 text-red-500 p-2 rounded-full shadow-lg transition-all hover:scale-110"
            title="Zur Wunschliste hinzufügen"
          >
            ❤️
          </button>
        )}
      </div>
      
      <div className="p-2 sm:p-4">
        <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 text-sm sm:text-base">{product.title}</h3>
        
        {/* Price Section - No line breaks, shrink text if needed */}
        <div className="flex items-center justify-between mb-2 gap-1">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1 flex-wrap-reverse">
              <span className="text-base sm:text-2xl font-bold text-blue-600 whitespace-nowrap">
                {prices.sellingPrice.toFixed(2)}&nbsp;€
              </span>
              {prices.hasDiscount && (
                <span className="text-xs sm:text-sm text-gray-500 line-through whitespace-nowrap">
                  {prices.marketPrice.toFixed(2)}&nbsp;€
                </span>
              )}
            </div>
          </div>
          {product.sales_count > 0 && (
            <div className="text-green-600 bg-green-100 px-1.5 py-0.5 rounded text-center leading-tight flex-shrink-0">
              <div className="text-xs font-bold whitespace-nowrap">{product.sales_count}+</div>
              <div className="text-xs whitespace-nowrap">verkauft</div>
            </div>
          )}
        </div>
        
        {/* Savings Display - Single line, shrink if needed */}
        {prices.hasDiscount && (
          <div className="mb-2 sm:mb-3 text-xs sm:text-sm text-green-600 font-semibold truncate">
            💰 Sie sparen: {prices.savings.toFixed(2)}&nbsp;€ ({prices.savingsPercent}%)
          </div>
        )}
        
        {/* Shipping Info */}
        <div className="text-xs text-gray-500 mb-2 sm:mb-3 truncate">
          <span>📦 {product.shipping_time || "7-15 Tage"}</span>
          {product.inventory && product.inventory > 0 && (
            <span className="ml-1 sm:ml-2 text-green-600">✓ Auf Lager</span>
          )}
        </div>
        
        <div className="flex gap-1 sm:gap-2">
          <button
            onClick={handlePurchaseClick}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-base font-medium hover:from-green-600 hover:to-green-700 transition-all duration-300 flex-1"
          >
            🔗 Bei AliExpress kaufen
          </button>
          <button
            onClick={() => onViewDetails(product)}
            className="px-2 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-xs sm:text-base whitespace-nowrap"
          >
            Details
          </button>
        </div>
        
        {/* Partner Info für Kunden */}
        {isAffiliateProduct && (
          <div className="mt-2 text-gray-500 text-center" style={{ fontSize: '0.65rem', lineHeight: '1' }}>
            Partner-Link – direkte sichere Verlinkungen
          </div>
        )}
      </div>
    </div>
  );
};

// Product Details Modal
const ProductDetailsModal = ({ product, isOpen, onClose, onAddToWishlist, wishlistEnabled = true }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  if (!isOpen || !product) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Calculate display prices with automatic discount display (same as ProductCard)
  const getDisplayPrices = () => {
    const sellingPrice = product.price?.selling_price || product.selling_price || product.market_price || product.price || 0;
    let marketPrice = product.price?.market_price || product.market_price || sellingPrice;
    
    // If market_price equals selling_price, create a display discount
    if (marketPrice === sellingPrice && sellingPrice > 0) {
      marketPrice = sellingPrice * 1.35; // 35% markup for display
    }
    
    const savings = marketPrice - sellingPrice;
    const savingsPercent = marketPrice > 0 ? Math.round((savings / marketPrice) * 100) : 0;
    
    return {
      sellingPrice,
      marketPrice,
      savings,
      savingsPercent,
      hasDiscount: savings > 0.01
    };
  };

  const prices = getDisplayPrices();

  const allImages = product.images && product.images.length > 0 
    ? product.images 
    : [product.main_image || "https://via.placeholder.com/600x400?text=Produkt"];

  // Handle AliExpress link click
  const handleBuyClick = () => {
    const affiliateLink = product.affiliate_link || product.aliexpress_url || '#';
    window.open(affiliateLink, '_blank');
  };

  return (
    <div className="fixed inset-0 z-[99999] overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-[99998]" onClick={onClose}></div>
        
        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl relative z-[99999]">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">Produktdetails</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Images */}
              <div className="space-y-4">
                <div className="relative">
                  {/* Main Image - clickable for zoom only on desktop */}
                  <div 
                    className="relative lg:cursor-zoom-in overflow-hidden rounded-lg bg-gray-50"
                    onClick={(e) => {
                      // Only allow zoom on desktop (screen width > 1024px)
                      if (window.innerWidth >= 1024) {
                        setIsZoomed(true);
                      }
                    }}
                  >
                    <img
                      src={allImages[selectedImage]}
                      alt={product.title}
                      className="w-full h-96 object-contain"
                    />
                  </div>
                  {prices.hasDiscount && prices.savingsPercent > 0 && (
                    <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      -{prices.savingsPercent}% gespart
                    </div>
                  )}
                  
                  {/* Wishlist Button */}
                  {wishlistEnabled && onAddToWishlist && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToWishlist(product);
                      }}
                      className="absolute top-4 right-4 bg-white bg-opacity-90 hover:bg-opacity-100 text-red-500 p-3 rounded-full shadow-lg transition-all hover:scale-110"
                      title="Zur Wunschliste hinzufügen"
                    >
                      ❤️
                    </button>
                  )}
                </div>
                
                {/* Image thumbnails */}
                {allImages.length > 1 && (
                  <div className="flex space-x-2 overflow-x-auto">
                    {allImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                          selectedImage === index ? 'border-blue-500' : 'border-gray-200'
                        }`}
                      >
                        <img src={image} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.title}</h1>
                  
                  {/* Rating */}
                  {product.rating && (
                    <div className="flex items-center mb-4">
                      <div className="flex items-center text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'fill-current' : 'text-gray-300'}`}
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                        <span className="ml-2 text-lg text-gray-600">
                          {product.rating.toFixed(1)} ({product.review_count || 0} Bewertungen)
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pricing */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-4xl font-bold text-blue-600">
                        {formatPrice(prices.sellingPrice)}
                      </div>
                      {prices.hasDiscount && (
                        <>
                          <div className="text-lg text-gray-500 line-through mt-1">
                            Ursprünglich: {formatPrice(prices.marketPrice)}
                          </div>
                          <div className="text-md text-green-600 font-semibold mt-2">
                            💰 Sie sparen: {formatPrice(prices.savings)} ({prices.savingsPercent}%)
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Kategorie:</span>
                      <div className="font-semibold capitalize">{product.category}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Lieferzeit:</span>
                      <div className="font-semibold text-green-600">{product.shipping_time}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Verfügbar:</span>
                      <div className="font-semibold">{product.inventory} Stück</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Verkäufe:</span>
                      <div className="font-semibold">{product.sales_count} verkauft</div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-bold text-lg mb-2">Produktbeschreibung</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {product.description || "Hochwertiges Produkt mit hervorragender Qualität. Direkt vom Hersteller für beste Preise und Qualitätsgarantie. Schneller Versand und zuverlässiger Kundenservice."}
                  </p>
                </div>

                {/* Specifications */}
                {Object.keys(product.specifications || {}).length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-bold text-lg mb-3">Spezifikationen</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {Object.entries(product.specifications)
                        .filter(([key]) => key !== 'Commission Rate')  // Commission Rate ausblenden
                        .map(([key, value]) => (
                        <div key={key} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                          <span className="text-gray-600 capitalize">{key}:</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4">
                  <button
                    onClick={handleBuyClick}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-lg hover:from-green-600 hover:to-green-700 transition duration-300 font-bold text-lg shadow-lg flex items-center justify-center gap-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    Bei AliExpress kaufen
                  </button>
                </div>

                {/* Trust badges */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Sichere Bezahlung</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>30 Tage Rückgabe</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Kostenloser Support</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Tracking inklusive</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Zoom Modal */}
      {isZoomed && (
        <div 
          className="fixed inset-0 z-[100000] bg-black bg-opacity-95 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <div className="relative max-w-7xl max-h-full">
            {/* Close button */}
            <button
              onClick={() => setIsZoomed(false)}
              className="absolute top-4 right-4 bg-white text-gray-900 rounded-full p-2 hover:bg-gray-200 transition-colors z-10"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Zoomed image with pan/scroll on mobile */}
            <div className="overflow-auto max-h-screen">
              <img
                src={allImages[selectedImage]}
                alt={product.title}
                className="max-w-none w-auto h-auto cursor-move"
                style={{ minWidth: '100%', minHeight: '100%' }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            
            <div className="text-white text-center mt-4 text-sm">
              Klicken Sie außerhalb des Bildes zum Schließen • Auf Mobile können Sie das Bild verschieben
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// eBay Integration Component
const EbayIntegration = () => {
  const [ebayListings, setEbayListings] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [ebayStatus, setEbayStatus] = useState(null);
  const [competitionData, setCompetitionData] = useState([]);
  const [researchKeyword, setResearchKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeEbayTab, setActiveEbayTab] = useState('listings');

  // Test eBay connection
  const testEbayConnection = async () => {
    try {
      const response = await axios.post(`${API}/ebay/test`);
      setEbayStatus(response.data);
      if (response.data.success) {
        toast.success("eBay Verbindung erfolgreich!");
      } else {
        toast.error("eBay Verbindung fehlgeschlagen");
      }
    } catch (error) {
      console.error("eBay connection test failed:", error);
      toast.error("eBay Verbindungstest fehlgeschlagen");
    }
  };

  // Load data
  const loadData = async () => {
    try {
      // Load products
      const productsResponse = await axios.get(`${API}/products?limit=50`);
      setProducts(productsResponse.data);

      // Load eBay listings
      const listingsResponse = await axios.get(`${API}/ebay/listings`);
      setEbayListings(listingsResponse.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    }
  };

  // Create eBay listing
  const createEbayListing = async () => {
    if (!selectedProduct) {
      toast.error("Bitte wählen Sie ein Produkt aus");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/ebay/listings`, {
        product_id: selectedProduct,
        category_id: "183454" // Electronics
      });

      if (response.data.success) {
        toast.success("eBay Listing erfolgreich erstellt!");
        loadData(); // Reload listings
        setSelectedProduct('');
      } else {
        toast.error("Fehler beim Erstellen des Listings");
      }
    } catch (error) {
      console.error("Failed to create eBay listing:", error);
      toast.error("Fehler beim Erstellen des eBay Listings");
    } finally {
      setIsLoading(false);
    }
  };

  // Research competition
  const researchCompetition = async () => {
    if (!researchKeyword.trim()) {
      toast.error("Bitte geben Sie ein Suchbegriff ein");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/ebay/research`, {
        params: {
          keywords: researchKeyword,
          research_type: 'active'
        }
      });

      setCompetitionData(response.data.results || []);
      toast.success(`${response.data.results?.length || 0} Konkurrenz-Produkte gefunden`);
    } catch (error) {
      console.error("Competition research failed:", error);
      toast.error("Konkurrenz-Recherche fehlgeschlagen");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    testEbayConnection();
    loadData();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">eBay Integration</h2>
        
        {/* Connection Status */}
        <div className="mb-6">
          {ebayStatus ? (
            <div className={`p-4 rounded-lg ${ebayStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${ebayStatus.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <div>
                  <p className={`font-medium ${ebayStatus.success ? 'text-green-800' : 'text-red-800'}`}>
                    eBay API Status: {ebayStatus.success ? 'Verbunden' : 'Fehler'}
                  </p>
                  <p className={`text-sm ${ebayStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                    {ebayStatus.message}
                  </p>
                  {ebayStatus.sandbox_mode && (
                    <p className="text-xs text-yellow-600 mt-1">
                      🧪 Sandbox Modus aktiv - Für Production benötigen Sie OAuth Setup
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              <span>eBay Verbindung wird getestet...</span>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveEbayTab('listings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeEbayTab === 'listings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meine Listings ({ebayListings.length})
            </button>
            <button
              onClick={() => setActiveEbayTab('create')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeEbayTab === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Neues Listing
            </button>
            <button
              onClick={() => setActiveEbayTab('research')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeEbayTab === 'research'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Konkurrenz-Analyse
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeEbayTab === 'listings' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Aktuelle eBay Listings</h3>
            {ebayListings.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">📦</div>
                <p className="text-gray-500">Noch keine eBay Listings erstellt</p>
                <button
                  onClick={() => setActiveEbayTab('create')}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Erstes Listing erstellen
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ebayListings.map((listing) => (
                  <div key={listing.id} className="border rounded-lg p-4">
                    <h4 className="font-medium mb-2 truncate">{listing.title}</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>eBay ID: {listing.ebay_item_id}</p>
                      <p>Preis: {formatPrice(listing.price)}</p>
                      <p>Status: <span className={`px-2 py-1 rounded text-xs ${
                        listing.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>{listing.status}</span></p>
                    </div>
                    {listing.listing_url && (
                      <a
                        href={listing.listing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-block text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Auf eBay ansehen →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeEbayTab === 'create' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Neues eBay Listing erstellen</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produkt auswählen
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Bitte Produkt wählen...</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.title} - {formatPrice(product.selling_price)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProduct && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  {(() => {
                    const product = products.find(p => p.id === selectedProduct);
                    return product ? (
                      <div>
                        <h4 className="font-medium mb-2">Ausgewähltes Produkt:</h4>
                        <div className="flex items-center space-x-4">
                          <img
                            src={product.main_image || "https://via.placeholder.com/60x60?text=Produkt"}
                            alt={product.title}
                            className="w-16 h-16 object-cover rounded"
                          />
                          <div>
                            <p className="font-medium">{product.title}</p>
                            <p className="text-sm text-gray-600">Preis: {formatPrice(product.selling_price)}</p>
                            <p className="text-sm text-gray-600">Kategorie: {product.category}</p>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              <button
                onClick={createEbayListing}
                disabled={!selectedProduct || isLoading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {isLoading ? "Listing wird erstellt..." : "eBay Listing erstellen"}
              </button>
            </div>
          </div>
        )}

        {activeEbayTab === 'research' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Konkurrenz-Analyse auf eBay</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Suchbegriff für Konkurrenz-Analyse..."
                  value={researchKeyword}
                  onChange={(e) => setResearchKeyword(e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={researchCompetition}
                  disabled={isLoading}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 font-semibold"
                >
                  {isLoading ? "Suche..." : "Analysieren"}
                </button>
              </div>

              {competitionData.length > 0 && (
                <div>
                  <h4 className="font-medium mb-4">
                    Konkurrenz-Ergebnisse ({competitionData.length} Produkte gefunden)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Produkt
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Preis
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Versand
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Gesamt
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Verkäufer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {competitionData.slice(0, 10).map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              <div className="max-w-xs truncate">{item.title}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {formatPrice(item.current_price)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {formatPrice(item.shipping_cost)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {formatPrice(item.total_price)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {item.seller}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Ansehen →
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Email System Component
const EmailSystem = () => {
  const [testEmail, setTestEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('Test E-Mail von imported24.de');
  const [emailMessage, setEmailMessage] = useState('Dies ist eine Test-E-Mail zur Überprüfung des Systems.');
  const [isLoading, setIsLoading] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);

  const sendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error("Bitte geben Sie eine E-Mail-Adresse ein");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/email/test`, {
        to_email: testEmail,
        subject: emailSubject,
        message: emailMessage
      });

      if (response.data.success) {
        toast.success("Test-E-Mail erfolgreich versendet!");
        setEmailHistory(prev => [...prev, {
          to: testEmail,
          subject: emailSubject,
          timestamp: new Date().toLocaleString('de-DE'),
          status: 'success'
        }]);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error("Failed to send test email:", error);
      toast.error("Fehler beim Versenden der Test-E-Mail");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">E-Mail System</h2>

        {/* SMTP Status */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-blue-500 mr-3"></div>
            <div>
              <p className="font-medium text-blue-800">SMTP Konfiguration</p>
              <p className="text-sm text-blue-600">
                Server: server34.publicompserver.de | E-Mail: bestellung@imported24.de
              </p>
            </div>
          </div>
        </div>

        {/* Test Email Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test E-Mail senden
            </label>
            <input
              type="email"
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Betreff
            </label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nachricht
            </label>
            <textarea
              rows={4}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={sendTestEmail}
            disabled={isLoading}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {isLoading ? "E-Mail wird versendet..." : "Test-E-Mail senden"}
          </button>
        </div>

        {/* Email History */}
        {emailHistory.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">E-Mail Verlauf</h3>
            <div className="space-y-2">
              {emailHistory.map((email, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{email.subject}</p>
                      <p className="text-sm text-gray-600">An: {email.to}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs ${
                        email.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {email.status === 'success' ? 'Versendet' : 'Fehler'}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">{email.timestamp}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Templates Info */}
        <div className="mt-8 bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold mb-3">Automatische E-Mail-Benachrichtigungen</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Bestellbestätigung (Kunde & Admin)</span>
            </div>
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Versandbenachrichtigung</span>
            </div>
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>eBay Listing Benachrichtigungen</span>
            </div>
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Admin Alerts & Warnungen</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shopping Cart Component
const ShoppingCart = ({ isOpen, onClose }) => {
  const { cartItems, cartTotal, updateQuantity, removeFromCart } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);

  if (!isOpen) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Warenkorb ({cartItems.length})</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cartItems.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">🛒</div>
                <p className="text-gray-500">Ihr Warenkorb ist leer</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.product_id} className="flex items-center space-x-4 bg-gray-50 rounded-lg p-3">
                    <img
                      src={item.product_image || "https://via.placeholder.com/60x60?text=Produkt"}
                      alt={item.product_title}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {item.product_title}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {formatPrice(item.price)}
                      </p>
                      <div className="flex items-center mt-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="mx-2 text-sm font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="border-t p-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold">Gesamt:</span>
                <span className="text-xl font-bold text-blue-600">{formatPrice(cartTotal)}</span>
              </div>
              <button
                onClick={() => setShowCheckout(true)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-300 font-semibold"
              >
                Zur Kasse
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          cartItems={cartItems}
          cartTotal={cartTotal}
        />
      )}
    </div>
  );
};

// Checkout Modal Component
const CheckoutModal = ({ isOpen, onClose, cartItems, cartTotal }) => {
  const [step, setStep] = useState(1); // 1: Info, 2: Payment
  const [customerInfo, setCustomerInfo] = useState({
    email: '',
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    postalCode: '',
    phone: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [isProcessing, setIsProcessing] = useState(false);
  const { clearCart } = useCart();

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (!customerInfo.email || !customerInfo.firstName || !customerInfo.lastName || 
        !customerInfo.street || !customerInfo.city || !customerInfo.postalCode) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }
    setStep(2);
  };

  const createOrder = async () => {
    try {
      setIsProcessing(true);
      
      const orderData = {
        customer_email: customerInfo.email,
        shipping_address: {
          first_name: customerInfo.firstName,
          last_name: customerInfo.lastName,
          street: customerInfo.street,
          city: customerInfo.city,
          postal_code: customerInfo.postalCode,
          country: "DE",
          phone: customerInfo.phone
        },
        cart_items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price
        })),
        payment_method: paymentMethod
      };

      const response = await axios.post(`${API}/orders`, orderData);
      return response.data;
    } catch (error) {
      console.error("Order creation failed:", error);
      toast.error("Fehler beim Erstellen der Bestellung");
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-75" onClick={onClose}></div>
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold">
              {step === 1 ? "Lieferinformationen" : "Bezahlung"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step 1: Customer Information */}
          {step === 1 && (
            <form onSubmit={handleInfoSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail-Adresse *
                  </label>
                  <input
                    type="email"
                    required
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vorname *
                  </label>
                  <input
                    type="text"
                    required
                    value={customerInfo.firstName}
                    onChange={(e) => setCustomerInfo({...customerInfo, firstName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nachname *
                  </label>
                  <input
                    type="text"
                    required
                    value={customerInfo.lastName}
                    onChange={(e) => setCustomerInfo({...customerInfo, lastName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Straße und Hausnummer *
                  </label>
                  <input
                    type="text"
                    required
                    value={customerInfo.street}
                    onChange={(e) => setCustomerInfo({...customerInfo, street: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stadt *
                  </label>
                  <input
                    type="text"
                    required
                    value={customerInfo.city}
                    onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PLZ *
                  </label>
                  <input
                    type="text"
                    required
                    value={customerInfo.postalCode}
                    onChange={(e) => setCustomerInfo({...customerInfo, postalCode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon (optional)
                  </label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                >
                  Weiter zur Bezahlung
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="p-6">
              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-3">Bestellübersicht</h3>
                <div className="space-y-2 text-sm">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex justify-between">
                      <span>{item.quantity}x {item.product_title.substring(0, 30)}...</span>
                      <span>{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 font-semibold flex justify-between">
                    <span>Gesamt:</span>
                    <span>{formatPrice(cartTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Zahlungsart wählen</h3>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment"
                      value="paypal"
                      checked={paymentMethod === 'paypal'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" alt="PayPal" className="h-6" />
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <span className="flex items-center space-x-2">
                      <span>Kreditkarte</span>
                      <img src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4.svg" alt="Visa" className="h-4" />
                      <img src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130.svg" alt="Mastercard" className="h-4" />
                    </span>
                  </label>
                </div>
              </div>

              {/* Payment Components */}
              {paymentMethod === 'paypal' && (
                <PayPalCheckout
                  cartItems={cartItems}
                  cartTotal={cartTotal}
                  customerInfo={customerInfo}
                  onSuccess={() => {
                    clearCart();
                    onClose();
                    toast.success("Bestellung erfolgreich abgeschlossen!");
                  }}
                  createOrder={createOrder}
                />
              )}

              {paymentMethod === 'stripe' && (
                <Elements stripe={stripePromise}>
                  <StripeCheckout
                    cartItems={cartItems}
                    cartTotal={cartTotal}
                    customerInfo={customerInfo}
                    onSuccess={() => {
                      clearCart();
                      onClose();
                      toast.success("Bestellung erfolgreich abgeschlossen!");
                    }}
                    createOrder={createOrder}
                  />
                </Elements>
              )}

              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  ← Zurück
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// PayPal Checkout Component
const PayPalCheckout = ({ cartTotal, onSuccess, createOrder }) => {
  const [orderId, setOrderId] = useState(null);

  return (
    <PayPalScriptProvider options={{
      "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
      currency: "EUR",
      locale: "de_DE"
    }}>
      <PayPalButtons
        style={{
          layout: "vertical",
          color: "blue",
          shape: "rect",
          label: "paypal"
        }}
        createOrder={async () => {
          try {
            // Create order in our backend first
            const order = await createOrder();
            setOrderId(order.id);
            
            // Create PayPal order
            const response = await axios.post(`${API}/payments/paypal/create-order`, {
              order_id: order.id
            });
            
            return response.data.paypal_order_id;
          } catch (error) {
            console.error("PayPal order creation failed:", error);
            toast.error("Fehler bei PayPal-Zahlung");
            throw error;
          }
        }}
        onApprove={async (data) => {
          try {
            // Capture payment
            const response = await axios.post(`${API}/payments/paypal/capture`, {
              order_id: orderId
            });
            
            if (response.data.status === 'COMPLETED') {
              onSuccess();
            } else {
              toast.error("Zahlung fehlgeschlagen");
            }
          } catch (error) {
            console.error("PayPal capture failed:", error);
            toast.error("Fehler bei der Zahlungsabwicklung");
          }
        }}
        onError={(error) => {
          console.error("PayPal error:", error);
          toast.error("PayPal-Fehler aufgetreten");
        }}
      />
    </PayPalScriptProvider>
  );
};

// Stripe Checkout Component
const StripeCheckout = ({ cartTotal, onSuccess, createOrder }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) return;
    
    setIsProcessing(true);
    
    try {
      // Create order in our backend first
      const order = await createOrder();
      
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      
      if (error) {
        toast.error(error.message);
        return;
      }
      
      // Create payment intent
      const response = await axios.post(`${API}/payments/stripe/create-intent`, {
        order_id: order.id,
        payment_method_id: paymentMethod.id
      });
      
      if (response.data.status === 'succeeded') {
        onSuccess();
      } else if (response.data.status === 'requires_action') {
        // Handle 3D Secure
        const { error: confirmError } = await stripe.confirmCardPayment(response.data.client_secret);
        
        if (!confirmError) {
          onSuccess();
        } else {
          toast.error("Zahlung fehlgeschlagen");
        }
      } else {
        toast.error("Zahlung fehlgeschlagen");
      }
      
    } catch (error) {
      console.error("Stripe payment failed:", error);
      toast.error("Fehler bei der Kreditkartenzahlung");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 border border-gray-300 rounded-md">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </div>
      
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? 'Verarbeitung...' : `${new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR'
        }).format(cartTotal)} bezahlen`}
      </button>
    </form>
  );
};

// Category Cards
const CategoryCard = ({ category, icon, description, productCount }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition duration-300 overflow-hidden group cursor-pointer">
      <div className="p-8">
        <div className="text-4xl mb-4">{icon}</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{category}</h3>
        <p className="text-gray-600 mb-4">{description}</p>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">{productCount} Produkte</span>
          <span className="text-blue-600 group-hover:text-blue-800 font-medium">
            Entdecken →
          </span>
        </div>
      </div>
    </div>
  );
};

// Admin Wishlist Orders Management Component
const AdminWishlistManagement = () => {
  const [orders, setOrders] = useState([]);
  const [trashOrders, setTrashOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editedOrder, setEditedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSendingOffer, setIsSendingOffer] = useState(false);
  const [showCompletedOrders, setShowCompletedOrders] = useState(false);
  const [currentTab, setCurrentTab] = useState('active'); // 'active' or 'trash'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showCustomerInfoModal, setShowCustomerInfoModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Search field
  const [statusFilter, setStatusFilter] = useState(''); // Status filter
  const [dateSortOrder, setDateSortOrder] = useState('desc'); // Date sort: 'asc' or 'desc'
  
  useEffect(() => {
    if (currentTab === 'active') {
      loadOrders();
    } else {
      loadTrashOrders();
    }
  }, [currentTab, showCompletedOrders]);
  
  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/wishlist/orders?include_completed=${showCompletedOrders}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Fehler beim Laden der Bestellungen:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadTrashOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/wishlist/orders/trash/list`);
      setTrashOrders(response.data.orders || []);
    } catch (error) {
      console.error('Fehler beim Laden des Papierkorbs:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await axios.put(`${API}/wishlist/orders/${orderId}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        toast.success('Status aktualisiert');
        loadOrders();
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };
  
  const deleteOrder = async (orderId) => {
    if (!window.confirm('Bestellung wirklich löschen?')) return;
    
    try {
      const response = await axios.delete(`${API}/wishlist/orders/${orderId}`);
      
      if (response.data.success) {
        toast.success('Bestellung gelöscht');
        loadOrders();
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      toast.error('Fehler beim Löschen');
    }
  };
  
  const handleDeleteOrder = (order) => {
    const protectedStatuses = ["bezahlt", "bestellt", "versendet", "abgeschlossen", "teilstorniert"];
    setOrderToDelete(order);
    if (protectedStatuses.includes(order.status)) {
      setShowDeleteModal(true);
    } else {
      // Direct delete without password
      confirmDelete(order._id, "");
    }
  };
  
  const confirmDelete = async (orderId, password) => {
    try {
      const response = await axios.delete(`${API}/wishlist/orders/${orderId}`, {
        data: {
          password: password,
          user: JSON.parse(localStorage.getItem('admin_user') || '{}').username || 'Admin'
        }
      });
      
      if (response.data.success) {
        toast.success('Bestellung in Papierkorb verschoben');
        setShowDeleteModal(false);
        setDeletePassword('');
        setOrderToDelete(null);
        loadOrders();
      } else {
        toast.error(response.data.message || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };
  
  const handleRestoreOrder = async (orderId) => {
    if (!window.confirm('Bestellung wiederherstellen?')) return;
    
    try {
      const response = await axios.put(`${API}/wishlist/orders/${orderId}/restore`, {
        user: JSON.parse(localStorage.getItem('admin_user') || '{}').username || 'Admin'
      });
      
      if (response.data.success) {
        toast.success('Bestellung wiederhergestellt');
        loadTrashOrders();
      }
    } catch (error) {
      console.error('Fehler beim Wiederherstellen:', error);
      toast.error('Fehler beim Wiederherstellen');
    }
  };
  
  const handlePermanentDelete = async (orderId) => {
    if (!window.confirm('Bestellung ENDGÜLTIG löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) return;
    
    try {
      const response = await axios.delete(`${API}/wishlist/orders/${orderId}/permanent`);
      
      if (response.data.success) {
        toast.success('Bestellung endgültig gelöscht');
        loadTrashOrders();
      }
    } catch (error) {
      console.error('Fehler beim endgültigen Löschen:', error);
      toast.error('Fehler beim endgültigen Löschen');
    }
  };
  
  const handleEditAddress = (order) => {
    setEditingAddress({
      orderId: order._id,
      street: order.customer_street || '',
      plz: order.customer_plz || '',
      city: order.customer_city || '',
      country: order.customer_country || ''
    });
    setShowAddressModal(true);
  };
  
  const handleSaveAddress = async () => {
    try {
      const response = await axios.put(`${API}/wishlist/orders/${editingAddress.orderId}/address`, {
        street: editingAddress.street,
        plz: editingAddress.plz,
        city: editingAddress.city,
        country: editingAddress.country,
        user: JSON.parse(localStorage.getItem('admin_user') || '{}').username || 'Admin'
      });
      
      if (response.data.success) {
        toast.success('Adresse aktualisiert');
        setShowAddressModal(false);
        setEditingAddress(null);
        loadOrders();
        // Reload details if modal is open
        if (selectedOrder && selectedOrder._id === editingAddress.orderId) {
          const updatedOrder = await axios.get(`${API}/wishlist/orders/${editingAddress.orderId}`);
          setSelectedOrder(updatedOrder.data.order);
        }
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Adresse:', error);
      toast.error('Fehler beim Speichern der Adresse');
    }
  };

  const handleEditOrder = () => {
    setIsEditMode(true);
    setEditedOrder(JSON.parse(JSON.stringify(selectedOrder))); // Deep copy
  };
  
  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(`${API}/wishlist/orders/${editedOrder._id}`, {
        items: editedOrder.items,
        subtotal: editedOrder.subtotal,
        service_fee: editedOrder.service_fee,
        risk_buffer: editedOrder.risk_buffer,
        total_amount: editedOrder.total_amount
      });
      
      if (response.data.success) {
        toast.success('Bestellung aktualisiert');
        setSelectedOrder(editedOrder);
        setIsEditMode(false);
        loadOrders();
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      toast.error('Fehler beim Speichern der Änderungen');
    }
  };
  
  const handleSendOffer = async () => {
    // Open modal for customer info input
    setShowCustomerInfoModal(true);
  };
  
  const confirmSendOffer = async () => {
    setIsSendingOffer(true);
    setShowCustomerInfoModal(false);
    try {
      const response = await axios.post(`${API}/wishlist/orders/${selectedOrder._id}/send-offer`, {
        customer_info: customerInfo
      });
      
      if (response.data.success) {
        toast.success('✅ Angebot erfolgreich an Kunden gesendet!');
        setShowDetailsModal(false);
        setCustomerInfo('');
        loadOrders();
      } else {
        toast.error(response.data.message || 'Fehler beim Senden des Angebots');
      }
    } catch (error) {
      console.error('Fehler beim Senden des Angebots:', error);
      toast.error('Fehler beim Senden des Angebots');
    } finally {
      setIsSendingOffer(false);
    }
  };
  
  const updateEditedItem = (index, field, value) => {
    const newItems = [...editedOrder.items];
    newItems[index][field] = parseFloat(value) || 0;
    const newSubtotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setEditedOrder({
      ...editedOrder,
      items: newItems,
      subtotal: newSubtotal,
      total_amount: newSubtotal + editedOrder.service_fee + editedOrder.risk_buffer
    });
  };
  
  const removeEditedItem = (index) => {
    const newItems = editedOrder.items.filter((_, i) => i !== index);
    const newSubtotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setEditedOrder({
      ...editedOrder,
      items: newItems,
      subtotal: newSubtotal,
      total_amount: newSubtotal + editedOrder.service_fee + editedOrder.risk_buffer
    });
  };

  
  const getStatusBadge = (status) => {
    const statusConfig = {
      'neu': { color: 'bg-blue-100 text-blue-800', label: '🆕 Neu' },
      'geprueft': { color: 'bg-yellow-100 text-yellow-800', label: '🔍 Geprüft' },
      'angebot_gesendet': { color: 'bg-purple-100 text-purple-800', label: '📧 Angebot gesendet' },
      'bezahlt': { color: 'bg-green-100 text-green-800', label: '💰 Bezahlt' },
      'bestellt': { color: 'bg-indigo-100 text-indigo-800', label: '📦 Bestellt' },
      'versendet': { color: 'bg-teal-100 text-teal-800', label: '✈️ Versendet' },
      'abgeschlossen': { color: 'bg-gray-100 text-gray-800', label: '✓ Abgeschlossen' }
    };
    
    const config = statusConfig[status] || statusConfig['neu'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Tabs für Aktiv / Papierkorb */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setCurrentTab('active')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                currentTab === 'active'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              📋 Wunschlisten-Bestellungen ({orders.length})
            </button>
            <button
              onClick={() => setCurrentTab('trash')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                currentTab === 'trash'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              🗑️ Papierkorb ({trashOrders.length})
            </button>
          </div>
          
          {currentTab === 'active' && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showCompletedOrders}
                onChange={(e) => setShowCompletedOrders(e.target.checked)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm text-gray-700">Abgeschlossene anzeigen {/* v2.0 */}</span>
            </label>
          )}
        </div>
        
        {/* Such-, Filter- und Sortierbereich */}
        {!isLoading && ((currentTab === 'active' && orders.length > 0) || (currentTab === 'trash' && trashOrders.length > 0)) && (
          <div className="mb-4 space-y-3">
            {/* Suchfeld */}
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="🔍 Suche nach Bestellnummer, Kundenname oder Email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Filter und Sortierung */}
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Alle Status</option>
                  <option value="neu">🆕 Neu</option>
                  <option value="in_bearbeitung">⚙️ In Bearbeitung</option>
                  <option value="angebot_gesendet">📧 Angebot gesendet</option>
                  <option value="geprueft">✔️ Geprüft</option>
                  <option value="bezahlt">💰 Bezahlt</option>
                  <option value="bestellt">🛒 Bestellt</option>
                  <option value="versendet">✈️ Versendet</option>
                  <option value="abgeschlossen">✅ Abgeschlossen</option>
                  <option value="teilstorniert">⚠️ Teilstorniert</option>
                  <option value="storniert">❌ Storniert</option>
                </select>
              </div>
              
              <button
                onClick={() => setDateSortOrder(dateSortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold text-gray-700 flex items-center gap-2"
              >
                Datum {dateSortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        )}
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : currentTab === 'active' && orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Bestellungen</h3>
            <p className="text-gray-500">Noch keine Wunschlisten-Bestellungen eingegangen</p>
          </div>
        ) : currentTab === 'trash' && trashOrders.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🗑️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Papierkorb leer</h3>
            <p className="text-gray-500">Keine gelöschten Bestellungen</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bestellnummer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kunde</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Artikel</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Summe</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // Get current orders based on tab
                  let displayOrders = currentTab === 'active' ? orders : trashOrders;
                  
                  // Apply search filter
                  if (searchQuery.trim()) {
                    const query = searchQuery.toLowerCase();
                    displayOrders = displayOrders.filter(order => 
                      (order.order_number && order.order_number.toLowerCase().includes(query)) ||
                      (order.customer_name && order.customer_name.toLowerCase().includes(query)) ||
                      (order.customer_email && order.customer_email.toLowerCase().includes(query))
                    );
                  }
                  
                  // Apply status filter
                  if (statusFilter) {
                    displayOrders = displayOrders.filter(order => order.status === statusFilter);
                  }
                  
                  // Apply date sorting
                  displayOrders = [...displayOrders].sort((a, b) => {
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
                  });
                  
                  return displayOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        // Backend sendet UTC-Zeit ohne 'Z', wir müssen 'Z' hinzufügen
                        const utcDateString = order.created_at.endsWith('Z') ? order.created_at : order.created_at + 'Z';
                        const date = new Date(utcDateString);
                        return date.toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: 'Europe/Berlin'
                        });
                      })()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-semibold text-blue-600">
                      {order.order_number || 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                      <div className="text-xs text-gray-500">{order.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {order.items_count} Artikel
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                      {order.total_amount?.toFixed(2)}&nbsp;€
                    </td>
                    <td className="px-4 py-3 text-center">
                      {currentTab === 'active' ? (
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                          className="text-xs border-0 bg-transparent cursor-pointer font-semibold"
                        >
                          <option value="neu">🆕 Neu</option>
                          <option value="geprueft">🔍 Geprüft</option>
                          <option value="angebot_gesendet">📧 Angebot gesendet</option>
                          <option value="bezahlt">💰 Bezahlt</option>
                          <option value="bestellt">📦 Bestellt</option>
                          <option value="versendet">✈️ Versendet</option>
                          <option value="abgeschlossen">✓ Abgeschlossen</option>
                          <option value="storniert">❌ Storniert</option>
                        </select>
                      ) : (
                        getStatusBadge(order.status)
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {currentTab === 'active' ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowDetailsModal(true);
                            }}
                            className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                            title="Details"
                          >
                            Bestelldetails
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order)}
                            className="text-red-600 hover:text-red-800 text-sm"
                            title="Löschen"
                          >
                            🗑️
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleRestoreOrder(order._id)}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                            title="Wiederherstellen"
                          >
                            ↩️ Wiederherstellen
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(order._id)}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                            title="Endgültig löschen"
                          >
                            🗑️ Endgültig
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Bestelldetails</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold">Kundendaten</h3>
                  <button
                    onClick={() => handleEditAddress(selectedOrder)}
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    📍 Adresse ändern
                  </button>
                </div>
                <div className="text-sm space-y-1">
                  <p><strong>Name:</strong> {selectedOrder.customer_name}</p>
                  <p><strong>E-Mail:</strong> {selectedOrder.customer_email}</p>
                  <p><strong>Telefon:</strong> {selectedOrder.customer_phone || 'Nicht angegeben'}</p>
                  <p><strong>Adresse:</strong><br/>
                    {selectedOrder.customer_street}<br/>
                    {selectedOrder.customer_plz} {selectedOrder.customer_city}<br/>
                    {selectedOrder.customer_country}
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Bestellinformationen</h3>
                <div className="text-sm space-y-1">
                  <p><strong>Bestellnummer:</strong> <span className="font-mono text-blue-600 font-semibold">{selectedOrder.order_number || 'N/A'}</span></p>
                  <p><strong>Erstellt:</strong> {(() => {
                    const utcDateString = selectedOrder.created_at.endsWith('Z') ? selectedOrder.created_at : selectedOrder.created_at + 'Z';
                    const date = new Date(utcDateString);
                    return date.toLocaleString('de-DE', {
                      timeZone: 'Europe/Berlin'
                    });
                  })()}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedOrder.status)}</p>
                  {selectedOrder.offer_sent_at && (
                    <p className="text-green-700 font-semibold">
                      <strong>✅ Angebot gesendet:</strong> {(() => {
                        const utcDateString = selectedOrder.offer_sent_at.endsWith('Z') ? selectedOrder.offer_sent_at : selectedOrder.offer_sent_at + 'Z';
                        const date = new Date(utcDateString);
                        return date.toLocaleString('de-DE', {
                          timeZone: 'Europe/Berlin'
                        });
                      })()}
                    </p>
                  )}
                  <p><strong>Artikel:</strong> {selectedOrder.items_count}</p>
                </div>
              </div>
            </div>
            
            <h3 className="font-semibold mb-3">Bestellte Produkte</h3>
            <div className="border rounded-lg overflow-hidden mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Produkt</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Menge</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Einzelpreis</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Gesamt</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">{isEditMode ? 'Aktionen' : 'Link'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(isEditMode ? editedOrder : selectedOrder).items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm">{item.title}</td>
                      <td className="px-4 py-2 text-sm text-center">
                        {isEditMode ? (
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateEditedItem(idx, 'quantity', e.target.value)}
                            className="w-16 px-2 py-1 border rounded text-center"
                            min="1"
                          />
                        ) : (
                          item.quantity
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        {isEditMode ? (
                          <input
                            type="number"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => updateEditedItem(idx, 'price', e.target.value)}
                            className="w-20 px-2 py-1 border rounded text-right"
                            min="0"
                          />
                        ) : (
                          `${item.price.toFixed(2)} €`
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold">{(item.price * item.quantity).toFixed(2)}&nbsp;€</td>
                      <td className="px-4 py-2 text-center">
                        {isEditMode ? (
                          <button
                            onClick={() => removeEditedItem(idx)}
                            className="text-red-600 hover:text-red-800"
                            title="Entfernen"
                          >
                            🗑️
                          </button>
                        ) : (
                          <a href={item.affiliate_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            🔗
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Zwischensumme:</span>
                  <span>{(isEditMode ? editedOrder : selectedOrder).subtotal?.toFixed(2)}&nbsp;€</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Servicegebühr:</span>
                  {isEditMode ? (
                    <input
                      type="number"
                      step="0.01"
                      value={editedOrder.service_fee}
                      onChange={(e) => setEditedOrder({
                        ...editedOrder,
                        service_fee: parseFloat(e.target.value) || 0,
                        total_amount: editedOrder.subtotal + (parseFloat(e.target.value) || 0) + editedOrder.risk_buffer
                      })}
                      className="w-24 px-2 py-1 border rounded text-right"
                    />
                  ) : (
                    <span>{selectedOrder.service_fee?.toFixed(2)}&nbsp;€</span>
                  )}
                </div>
                <div className="flex justify-between items-center text-xs text-gray-600">
                  <span>Risikopuffer:</span>
                  {isEditMode ? (
                    <input
                      type="number"
                      step="0.01"
                      value={editedOrder.risk_buffer}
                      onChange={(e) => setEditedOrder({
                        ...editedOrder,
                        risk_buffer: parseFloat(e.target.value) || 0,
                        total_amount: editedOrder.subtotal + editedOrder.service_fee + (parseFloat(e.target.value) || 0)
                      })}
                      className="w-24 px-2 py-1 border rounded text-right text-xs"
                    />
                  ) : (
                    <span>{selectedOrder.risk_buffer?.toFixed(2)}&nbsp;€</span>
                  )}
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span>Netto:</span>
                  <span>{((isEditMode ? editedOrder : selectedOrder).total_netto || 0).toFixed(2)}&nbsp;€</span>
                </div>
                <div className="flex justify-between">
                  <span>MwSt. ({(((isEditMode ? editedOrder : selectedOrder).mwst_rate || 0.19) * 100).toFixed(0)}%):</span>
                  <span>{((isEditMode ? editedOrder : selectedOrder).mwst_amount || 0).toFixed(2)}&nbsp;€</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Gesamt (Brutto):</span>
                  <span className="text-blue-600">{(isEditMode ? editedOrder : selectedOrder).total_amount?.toFixed(2)}&nbsp;€</span>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-between">
              <div className="flex gap-2">
                {!isEditMode && selectedOrder.status === 'neu' && (
                  <button
                    onClick={handleEditOrder}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    ✏️ Bearbeiten
                  </button>
                )}
                {!isEditMode && ['neu', 'geprueft'].includes(selectedOrder.status) && (
                  <button
                    onClick={handleSendOffer}
                    disabled={isSendingOffer}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:bg-gray-400"
                  >
                    {isSendingOffer ? '⏳ Wird gesendet...' : '📧 Angebot senden'}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                {isEditMode && (
                  <>
                    <button
                      onClick={() => {
                        setIsEditMode(false);
                        setEditedOrder(null);
                      }}
                      className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      ✓ Speichern
                    </button>
                  </>
                )}
                {!isEditMode && (
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setIsEditMode(false);
                      setEditedOrder(null);
                    }}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                  >
                    Schließen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Password Modal */}
      {showDeleteModal && orderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">⚠️ Bestellung löschen</h3>
            <p className="text-sm text-gray-600 mb-4">
              Diese Bestellung hat den Status <strong>"{orderToDelete.status}"</strong> erreicht.
              Bitte geben Sie das Admin-Passwort ein, um fortzufahren:
            </p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 mb-4"
              placeholder="Passwort eingeben"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && deletePassword) {
                  confirmDelete(orderToDelete._id, deletePassword);
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setOrderToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={() => confirmDelete(orderToDelete._id, deletePassword)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={!deletePassword}
              >
                In Papierkorb verschieben
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Address Edit Modal */}
      {showAddressModal && editingAddress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">📍 Adresse bearbeiten</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Straße</label>
                <input
                  type="text"
                  value={editingAddress.street}
                  onChange={(e) => setEditingAddress({...editingAddress, street: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="Musterstraße 123"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
                  <input
                    type="text"
                    value={editingAddress.plz}
                    onChange={(e) => setEditingAddress({...editingAddress, plz: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2"
                    placeholder="12345"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
                  <input
                    type="text"
                    value={editingAddress.city}
                    onChange={(e) => setEditingAddress({...editingAddress, city: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2"
                    placeholder="Berlin"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Land</label>
                <input
                  type="text"
                  value={editingAddress.country}
                  onChange={(e) => setEditingAddress({...editingAddress, country: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="Deutschland"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAddressModal(false);
                  setEditingAddress(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveAddress}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Info Modal */}
      {showCustomerInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">📝 Kundeninformation hinzufügen</h3>
                <button
                  onClick={() => {
                    setShowCustomerInfoModal(false);
                    setCustomerInfo('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Fügen Sie hier optionale Informationen für den Kunden hinzu. Diese werden in der Angebots-Email angezeigt.
              </p>
              
              <textarea
                value={customerInfo}
                onChange={(e) => setCustomerInfo(e.target.value)}
                placeholder="z.B. Besondere Hinweise zur Lieferung, zusätzliche Produktinformationen, etc."
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <div className="text-sm text-gray-500 mt-2">
                {customerInfo.length} Zeichen
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCustomerInfoModal(false);
                  setCustomerInfo('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmSendOffer}
                disabled={isSendingOffer}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSendingOffer ? '⏳ Wird gesendet...' : '📧 Angebot jetzt senden'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Newsletter Management Component
const AdminNewsletterManagement = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastNewsletterSent, setLastNewsletterSent] = useState(null);
  
  // Subscriber management states
  const [subscriberPage, setSubscriberPage] = useState(1);
  const subscribersPerPage = 25;
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const subscribersRef = useRef(null);

  useEffect(() => {
    loadProducts();
    loadSubscribers();
    loadLastNewsletterSent();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=100`);
      // Backend returns array directly
      const productsList = Array.isArray(response.data) ? response.data : (response.data.products || []);
      setProducts(productsList);
    } catch (error) {
      console.error('Fehler beim Laden der Produkte:', error);
    }
  };

  const loadSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/subscribers`);
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error('Fehler beim Laden der Abonnenten:', error);
    }
  };
  
  const loadLastNewsletterSent = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/last-sent`);
      if (response.data.success && response.data.last_sent) {
        setLastNewsletterSent(response.data.last_sent);
      }
    } catch (error) {
      console.error('Fehler beim Laden des letzten Newsletter-Datums:', error);
    }
  };
  
  // Scroll to subscribers section
  const scrollToSubscribers = () => {
    subscribersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleProductSelection = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      if (selectedProducts.length < 50) {
        setSelectedProducts([...selectedProducts, productId]);
      } else {
        alert('Maximal 50 Produkte können ausgewählt werden');
      }
    }
  };
  
  // Subscriber selection handlers
  const toggleSubscriberSelection = (subscriberId) => {
    if (selectedSubscribers.includes(subscriberId)) {
      setSelectedSubscribers(selectedSubscribers.filter(id => id !== subscriberId));
    } else {
      setSelectedSubscribers([...selectedSubscribers, subscriberId]);
    }
  };
  
  const toggleAllSubscribers = () => {
    if (selectedSubscribers.length === subscribers.length) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.map(sub => sub._id || sub.email));
    }
  };
  
  // Edit subscriber
  const handleEditSubscriber = (subscriber) => {
    setEditingSubscriber({ ...subscriber });
    setShowEditModal(true);
  };
  
  const handleSaveEditSubscriber = async () => {
    try {
      const response = await axios.put(`${API}/newsletter/subscribers/${editingSubscriber._id}`, {
        email: editingSubscriber.email
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Abonnent erfolgreich aktualisiert!' });
        loadSubscribers();
        setShowEditModal(false);
      }
    } catch (error) {
      console.error('Edit subscriber error:', error);
      setMessage({ type: 'error', text: 'Fehler beim Aktualisieren des Abonnenten' });
    }
  };
  
  // Delete subscriber
  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Möchten Sie diesen Abonnenten wirklich löschen?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/newsletter/subscribers/${subscriberId}`);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Abonnent erfolgreich gelöscht!' });
        loadSubscribers();
      }
    } catch (error) {
      console.error('Delete subscriber error:', error);
      setMessage({ type: 'error', text: 'Fehler beim Löschen des Abonnenten' });
    }
  };

  const handleSendNewsletter = async () => {
    if (selectedProducts.length < 3) {
      alert('Bitte wählen Sie mindestens 3 Produkte aus');
      return;
    }
    
    const recipientCount = selectedSubscribers.length > 0 ? selectedSubscribers.length : subscribers.length;

    if (!window.confirm(`Newsletter an ${recipientCount} Abonnenten senden?`)) {
      return;
    }

    setIsSending(true);
    setMessage(null);

    try {
      const response = await axios.post(`${API}/newsletter/send`, {
        product_ids: selectedProducts,
        subscriber_ids: selectedSubscribers.length > 0 ? selectedSubscribers : undefined
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: `✅ Newsletter erfolgreich an ${response.data.sent_count} Abonnenten gesendet!` });
        setSelectedProducts([]);
        setSelectedSubscribers([]);
        loadSubscribers(); // Reload to update newsletter count
        loadLastNewsletterSent(); // Reload last newsletter sent date
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      console.error('Newsletter send error:', error);
      setMessage({ type: 'error', text: 'Fehler beim Senden des Newsletters' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Newsletter-Verwaltung</h2>
        <p className="text-gray-600">
          Abonnenten: <strong>{subscribers.length}</strong> | 
          Ausgewählte Produkte: <strong>{selectedProducts.length}</strong> / 50
        </p>
      </div>

      {/* Send Newsletter Button */}
      <div className="mb-6 bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold mb-2">Newsletter versenden</h3>
            <p className="text-gray-600">
              {selectedProducts.length >= 3 
                ? `${selectedProducts.length} Produkte ausgewählt - bereit zum Versenden!`
                : `Wählen Sie mindestens 3 Produkte aus (${selectedProducts.length}/3)`
              }
            </p>
            {lastNewsletterSent && (
              <p className="text-sm text-gray-500 mt-1">
                Letzter Newsletter: {(() => {
                  const utcDateString = lastNewsletterSent.endsWith('Z') ? lastNewsletterSent : lastNewsletterSent + 'Z';
                  const date = new Date(utcDateString);
                  return date.toLocaleString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Berlin'
                  });
                })()} Uhr
              </p>
            )}
          </div>
          <button
            onClick={handleSendNewsletter}
            disabled={selectedProducts.length < 3 || isSending}
            className={`px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
              selectedProducts.length < 3 || isSending
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 transform hover:scale-105 shadow-lg'
            }`}
          >
            {isSending ? '📧 Wird gesendet...' : '📧 Newsletter versenden'}
          </button>
        </div>

        {message && (
          <div className={`mt-4 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-300 text-green-800' 
              : 'bg-red-50 border border-red-300 text-red-800'
          }`}>
            {message.text}
          </div>
        )}
      </div>

      {/* Quick Jump Button */}
      <div className="mb-6 flex justify-center">
        <button
          onClick={scrollToSubscribers}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg flex items-center gap-2"
        >
          ⬇️ Zu den Abonnenten
        </button>
      </div>

      {/* Product Selection Grid */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-semibold mb-4">Produkte für Newsletter auswählen</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {products.map((product) => {
            const productId = product.id || product._id;
            const isSelected = selectedProducts.includes(productId);
            
            return (
              <div
                key={productId}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleProductSelection(productId);
                }}
                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                  isSelected
                    ? 'border-green-500 ring-4 ring-green-200'
                    : 'border-gray-200 hover:border-blue-500'
                }`}
              >
                {/* Selection Badge */}
                {isSelected && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold z-10">
                    ✓
                  </div>
                )}

                {/* Product Image */}
                <img
                  src={product.main_image || product.images?.[0] || 'https://via.placeholder.com/150'}
                  alt={product.title}
                  className="w-full aspect-square object-cover"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/150'}
                />

                {/* Product Info */}
                <div className="p-2 bg-white">
                  <p className="text-xs font-medium text-gray-800 line-clamp-2">{product.title}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-sm font-bold text-blue-600">
                      {(product.price?.selling_price || product.selling_price || 0).toFixed(2)}&nbsp;€
                    </p>
                    {/* Newsletter Count Badge */}
                    {product.newsletter_sent_count > 0 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800" title={`${product.newsletter_sent_count}x im Newsletter versendet`}>
                        📧 {product.newsletter_sent_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {products.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Keine Produkte verfügbar
          </div>
        )}
      </div>

      {/* Subscribers List */}
      <div ref={subscribersRef} className="mt-6 bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Newsletter-Abonnenten ({subscribers.length})</h3>
          <button
            onClick={toggleAllSubscribers}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
          >
            {selectedSubscribers.length === subscribers.length ? '❌ Alle abwählen' : '✅ Alle auswählen'}
          </button>
        </div>
        
        {subscribers.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nr.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-Mail</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anmeldedatum</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">News Anz.</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Senden</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscribers
                    .slice((subscriberPage - 1) * subscribersPerPage, subscriberPage * subscribersPerPage)
                    .map((sub, index) => {
                      const globalIndex = (subscriberPage - 1) * subscribersPerPage + index + 1;
                      const isSelected = selectedSubscribers.includes(sub._id || sub.email);
                      
                      return (
                        <tr key={sub._id || sub.email} className="hover:bg-gray-50">
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{globalIndex}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{sub.email}</span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className="text-xs text-gray-500">
                              {new Date(sub.subscribed_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-center">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                              {sub.newsletter_received_count || 0}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-center">
                            <button
                              onClick={() => toggleSubscriberSelection(sub._id || sub.email)}
                              className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all ${
                                isSelected
                                  ? 'bg-green-500 hover:bg-green-600'
                                  : 'bg-red-500 hover:bg-red-600'
                              }`}
                              title={isSelected ? 'Erhält Newsletter' : 'Kein Newsletter'}
                            >
                              <span className="text-white text-lg">
                                {isSelected ? '🟢' : '🔴'}
                              </span>
                            </button>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEditSubscriber(sub)}
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                title="Bearbeiten"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteSubscriber(sub._id || sub.email)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Löschen"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {subscribers.length > subscribersPerPage && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Zeige {((subscriberPage - 1) * subscribersPerPage) + 1} - {Math.min(subscriberPage * subscribersPerPage, subscribers.length)} von {subscribers.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSubscriberPage(prev => Math.max(1, prev - 1))}
                    disabled={subscriberPage === 1}
                    className={`px-3 py-1 rounded ${
                      subscriberPage === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    ← Zurück
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.ceil(subscribers.length / subscribersPerPage) }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setSubscriberPage(pageNum)}
                      className={`px-3 py-1 rounded ${
                        pageNum === subscriberPage
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setSubscriberPage(prev => Math.min(Math.ceil(subscribers.length / subscribersPerPage), prev + 1))}
                    disabled={subscriberPage >= Math.ceil(subscribers.length / subscribersPerPage)}
                    className={`px-3 py-1 rounded ${
                      subscriberPage >= Math.ceil(subscribers.length / subscribersPerPage)
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Weiter →
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-gray-500 py-8">Noch keine Abonnenten</p>
        )}
      </div>
      
      {/* Edit Subscriber Modal */}
      {showEditModal && editingSubscriber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Abonnent bearbeiten</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail-Adresse</label>
              <input
                type="email"
                value={editingSubscriber.email}
                onChange={(e) => setEditingSubscriber({ ...editingSubscriber, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSaveEditSubscriber}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Speichern
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Admin Accounting Management Component
const AccountingManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totals, setTotals] = useState({ 
    total_netto: 0, 
    total_mwst: 0, 
    total_brutto: 0,
    total_ali_express_cost: 0,
    total_profit: 0
  });
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editingStatus, setEditingStatus] = useState("");
  const [viewingDetails, setViewingDetails] = useState(null);
  const [editingExpenses, setEditingExpenses] = useState(null);
  const [expensesValue, setExpensesValue] = useState(0);
  const [showCompleted, setShowCompleted] = useState(false); // Filter for completed orders
  const [cancelReason, setCancelReason] = useState(""); // Cancellation reason
  const [showCancelModal, setShowCancelModal] = useState(false); // Cancel modal
  const [trackingNumber, setTrackingNumber] = useState(""); // Tracking number for shipment
  const [showTrackingModal, setShowTrackingModal] = useState(false); // Tracking modal
  const [sendShipmentNotification, setSendShipmentNotification] = useState(false); // Send email notification
  const [refundAmount, setRefundAmount] = useState(0); // Partial refund amount
  const [partialCancelReason, setPartialCancelReason] = useState(""); // Partial cancellation reason
  const [showPartialCancelModal, setShowPartialCancelModal] = useState(false); // Partial cancel modal
  const [showAddressModal, setShowAddressModal] = useState(false); // Address edit modal
  const [editingAddress, setEditingAddress] = useState(null); // Address being edited
  
  // Search, Filter, Sort states for Accounting
  const [accountingSearchQuery, setAccountingSearchQuery] = useState('');
  const [accountingStatusFilter, setAccountingStatusFilter] = useState('');
  const [paidDateSortOrder, setPaidDateSortOrder] = useState('desc'); // 'asc' or 'desc'

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/accounting/invoices`);
      if (response.data.success) {
        setInvoices(response.data.invoices);
        setTotals({
          total_netto: response.data.total_netto,
          total_mwst: response.data.total_mwst,
          total_brutto: response.data.total_brutto,
          total_ali_express_cost: response.data.total_ali_express_cost || 0,
          total_profit: response.data.total_profit || 0
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungen:', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusEdit = (invoiceId, currentStatus) => {
    setEditingInvoiceId(invoiceId);
    setEditingStatus(currentStatus);
  };

  const handleStatusSave = async (invoiceId) => {
    // Check if status is "storniert" - require cancellation reason
    if (editingStatus === 'storniert') {
      setShowCancelModal(true);
      return;
    }
    
    // Check if status is "teilstorniert" - require refund amount and reason
    if (editingStatus === 'teilstorniert') {
      setShowPartialCancelModal(true);
      return;
    }
    
    // Check if status is "versendet" - require tracking number
    if (editingStatus === 'versendet') {
      setShowTrackingModal(true);
      return;
    }
    
    // Save status normally for other statuses
    await saveStatus(invoiceId, editingStatus, null, null, null);
  };
  
  const saveStatus = async (invoiceId, status, reason = null, tracking = null, refund = null, sendEmail = false) => {
    try {
      const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
      const response = await axios.put(`${API}/wishlist/orders/${invoiceId}/status`, {
        status: status,
        user: user.username || 'System',
        cancel_reason: reason,
        tracking_number: tracking,
        refund_amount: refund,
        send_shipment_notification: sendEmail
      });

      if (response.data.success) {
        toast.success(sendEmail ? '✅ Status aktualisiert und E-Mail gesendet' : 'Status aktualisiert');
        loadInvoices();
        setEditingInvoiceId(null);
        setShowCancelModal(false);
        setShowPartialCancelModal(false);
        setShowTrackingModal(false);
        setCancelReason("");
        setPartialCancelReason("");
        setRefundAmount(0);
        setTrackingNumber("");
        setSendShipmentNotification(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleStatusCancel = () => {
    setEditingInvoiceId(null);
    setEditingStatus("");
  };

  const loadOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API}/wishlist/orders/${orderId}`);
      if (response.data.success) {
        setViewingDetails(response.data.order);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Details:', error);
      toast.error('Fehler beim Laden der Bestelldetails');
    }
  };

  const handleExpensesEdit = (invoiceId, currentExpenses) => {
    setEditingExpenses(invoiceId);
    setExpensesValue(currentExpenses);
  };

  const handleExpensesSave = async (invoiceId) => {
    try {
      const response = await axios.put(`${API}/wishlist/orders/${invoiceId}/expenses`, {
        ali_express_total: parseFloat(expensesValue)
      });

      if (response.data.success) {
        toast.success('Ausgaben aktualisiert');
        loadInvoices();
        setEditingExpenses(null);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Ausgaben:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleExpensesCancel = () => {
    setEditingExpenses(null);
    setExpensesValue(0);
  };



  const handleEditAddress = (order) => {
    setEditingAddress({
      orderId: order._id,
      street: order.customer_street || '',
      plz: order.customer_plz || '',
      city: order.customer_city || '',
      country: order.customer_country || ''
    });
    setShowAddressModal(true);
  };

  const handleSaveAddress = async () => {
    try {
      const response = await axios.put(`${API}/wishlist/orders/${editingAddress.orderId}/address`, {
        street: editingAddress.street,
        plz: editingAddress.plz,
        city: editingAddress.city,
        country: editingAddress.country,
        user: JSON.parse(localStorage.getItem('admin_user') || '{}').username || 'Admin'
      });

      if (response.data.success) {
        toast.success('Adresse aktualisiert');
        setShowAddressModal(false);
        setEditingAddress(null);
        loadInvoices();
        // Reload details if viewing
        if (viewingDetails && viewingDetails._id === editingAddress.orderId) {
          loadOrderDetails(editingAddress.orderId);
        }
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Adresse:', error);
      toast.error('Fehler beim Speichern der Adresse');
    }
  };

  const downloadCSV = () => {
    // Export ALL invoices to CSV (including completed)
    const headers = ['Rechnungsnr.', 'Kunde', 'Email', 'Netto', 'MwSt', 'Brutto', 'Ausgaben', 'Gewinn', 'Status', 'Bezahlt am', 'Bearbeiter'];
    const rows = invoices.map(inv => [
      inv.order_number,
      inv.customer_name,
      inv.customer_email,
      inv.total_netto.toFixed(2),
      inv.mwst_amount.toFixed(2),
      inv.total_amount.toFixed(2),
      inv.ali_express_cost.toFixed(2),
      inv.profit.toFixed(2),
      inv.status,
      new Date(inv.paid_at).toLocaleDateString('de-DE'),
      inv.paid_by
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Abrechnung_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV-Datei heruntergeladen');
  };
  
  // Calculate filtered totals based on showCompleted toggle
  const filteredInvoices = invoices.filter(invoice => {
    if (!showCompleted) {
      // Hide completed orders (versendet, abgeschlossen, storniert, teilstorniert)
      const isCompleted = ['versendet', 'abgeschlossen', 'storniert', 'teilstorniert'].includes(invoice.status);
      return !isCompleted;
    }
    return true;
  });
  
  console.log('Accounting Debug:', {
    totalInvoices: invoices.length,
    filteredInvoices: filteredInvoices.length,
    showCompleted: showCompleted,
    statuses: invoices.map(i => i.status)
  });
  
  const filteredTotals = {
    total_netto: filteredInvoices.reduce((sum, inv) => sum + inv.total_netto, 0),
    total_mwst: filteredInvoices.reduce((sum, inv) => sum + inv.mwst_amount, 0),
    total_brutto: filteredInvoices.reduce((sum, inv) => sum + inv.total_amount, 0),
    total_ali_express_cost: filteredInvoices.reduce((sum, inv) => sum + inv.ali_express_cost, 0),
    total_profit: filteredInvoices.reduce((sum, inv) => sum + inv.profit, 0)
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Abrechnungen</h2>
          <p className="text-gray-600 mt-1">
            Übersicht aller bezahlten Bestellungen 
            {!showCompleted && filteredInvoices.length < invoices.length && (
              <span className="text-orange-600 ml-2">
                ({invoices.length - filteredInvoices.length} ausgeblendet)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Toggle for completed orders */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => {
                console.log('Checkbox changed:', e.target.checked);
                setShowCompleted(e.target.checked);
              }}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">
              Abgeschlossene anzeigen ({invoices.length - filteredInvoices.length})
            </span>
          </label>
          
          <button
            onClick={downloadCSV}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            📥 CSV Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Netto-Umsatz</div>
          <div className="text-2xl font-bold text-blue-600">{filteredTotals.total_netto.toFixed(2)} €</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">MwSt (19%)</div>
          <div className="text-2xl font-bold text-orange-600">{filteredTotals.total_mwst.toFixed(2)} €</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Brutto-Umsatz</div>
          <div className="text-2xl font-bold text-green-600">{filteredTotals.total_brutto.toFixed(2)} €</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Ausgaben</div>
          <div className="text-2xl font-bold text-red-600">{filteredTotals.total_ali_express_cost.toFixed(2)} €</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">💰 Gewinn</div>
          <div className="text-2xl font-bold text-purple-600">{filteredTotals.total_profit.toFixed(2)} €</div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Lade Abrechnungen...</p>
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">Noch keine bezahlten Bestellungen</p>
        </div>
      ) : (
        <>
          {/* Such-, Filter- und Sortierbereich */}
          <div className="mb-4 space-y-3">
            {/* Suchfeld */}
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="🔍 Suche nach Rechnungsnummer, Kundenname oder Email..."
                  value={accountingSearchQuery}
                  onChange={(e) => setAccountingSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Filter und Sortierung */}
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <select
                  value={accountingStatusFilter}
                  onChange={(e) => setAccountingStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Alle Status</option>
                  <option value="bezahlt">💰 Bezahlt</option>
                  <option value="bestellt">🛒 Bestellt</option>
                  <option value="versendet">✈️ Versendet</option>
                  <option value="abgeschlossen">✅ Abgeschlossen</option>
                  <option value="teilstorniert">⚠️ Teilstorniert</option>
                  <option value="storniert">❌ Storniert</option>
                </select>
              </div>
              
              <button
                onClick={() => setPaidDateSortOrder(paidDateSortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold text-gray-700 flex items-center gap-2"
              >
                Bezahlt am {paidDateSortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rechnungsnr.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kunde</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Netto</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">MwSt</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Brutto</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ausgaben</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gewinn</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bezahlt am</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bearbeiter</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(() => {
                let displayInvoices = filteredInvoices;
                
                // Apply search filter
                if (accountingSearchQuery.trim()) {
                  const query = accountingSearchQuery.toLowerCase();
                  displayInvoices = displayInvoices.filter(invoice => 
                    (invoice.order_number && invoice.order_number.toLowerCase().includes(query)) ||
                    (invoice.customer_name && invoice.customer_name.toLowerCase().includes(query)) ||
                    (invoice.customer_email && invoice.customer_email.toLowerCase().includes(query))
                  );
                }
                
                // Apply status filter
                if (accountingStatusFilter) {
                  displayInvoices = displayInvoices.filter(invoice => invoice.status === accountingStatusFilter);
                }
                
                // Apply date sorting
                displayInvoices = [...displayInvoices].sort((a, b) => {
                  const dateA = new Date(a.paid_at);
                  const dateB = new Date(b.paid_at);
                  return paidDateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
                });
                
                return displayInvoices.map((invoice) => (
                <tr key={invoice._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-mono font-bold text-blue-600">{invoice.order_number}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.customer_name}</div>
                    <div className="text-xs text-gray-500">{invoice.customer_email}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">{invoice.total_netto.toFixed(2)}&nbsp;€</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">{invoice.mwst_amount.toFixed(2)}&nbsp;€</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold">{invoice.total_amount.toFixed(2)}&nbsp;€</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-red-600">
                    {editingExpenses === invoice._id ? (
                      <div className="flex items-center gap-2 justify-end">
                        <input
                          type="number"
                          step="0.01"
                          value={expensesValue}
                          onChange={(e) => setExpensesValue(e.target.value)}
                          className="border rounded px-2 py-1 text-sm w-24 text-right"
                        />
                        <button
                          onClick={() => handleExpensesSave(invoice._id)}
                          className="text-green-600 hover:text-green-700"
                          title="Speichern"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleExpensesCancel}
                          className="text-red-600 hover:text-red-700"
                          title="Abbrechen"
                        >
                          ✗
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-end">
                        <span>-{invoice.ali_express_cost.toFixed(2)}&nbsp;€</span>
                        <button
                          onClick={() => handleExpensesEdit(invoice._id, invoice.ali_express_cost)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Ausgaben bearbeiten"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                    <span className={`font-bold ${invoice.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {invoice.profit >= 0 ? '+' : ''}{invoice.profit.toFixed(2)}&nbsp;€
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {editingInvoiceId === invoice._id ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={editingStatus}
                          onChange={(e) => setEditingStatus(e.target.value)}
                          className="border rounded px-2 py-1 text-sm"
                        >
                          <option value="neu">Neu</option>
                          <option value="in_bearbeitung">In Bearbeitung</option>
                          <option value="angebot_gesendet">Angebot gesendet</option>
                          <option value="bezahlt">Bezahlt</option>
                          <option value="bestellt">Bestellt</option>
                          <option value="versendet">Versendet</option>
                          <option value="abgeschlossen">Abgeschlossen</option>
                          <option value="teilstorniert">Teilstorniert</option>
                          <option value="storniert">Storniert</option>
                        </select>
                        <button
                          onClick={() => handleStatusSave(invoice._id)}
                          className="text-green-600 hover:text-green-700"
                          title="Speichern"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleStatusCancel}
                          className="text-red-600 hover:text-red-700"
                          title="Abbrechen"
                        >
                          ✗
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded ${
                          invoice.status === 'bezahlt' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'versendet' ? 'bg-blue-100 text-blue-800' :
                          invoice.status === 'abgeschlossen' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status}
                        </span>
                        <button
                          onClick={() => handleStatusEdit(invoice._id, invoice.status)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Status bearbeiten"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">{(() => {
                    const utcDateString = invoice.paid_at.endsWith('Z') ? invoice.paid_at : invoice.paid_at + 'Z';
                    const date = new Date(utcDateString);
                    return date.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
                  })()}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{invoice.paid_by || 'Unbekannt'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <button
                      onClick={() => loadOrderDetails(invoice._id)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      title="Details anzeigen"
                    >
                      🔍 Details
                    </button>
                  </td>
                </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Details Modal */}
      {viewingDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">Bestelldetails - {viewingDetails.order_number}</h3>
                <button
                  onClick={() => setViewingDetails(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Customer Info */}
              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-lg">Kundeninformationen</h4>
                  <button
                    onClick={() => handleEditAddress(viewingDetails)}
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    📍 Adresse ändern
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Name:</span>
                    <p className="font-medium">{viewingDetails.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Email:</span>
                    <p className="font-medium">{viewingDetails.customer_email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Adresse:</span>
                    <p className="font-medium">
                      {viewingDetails.customer_street || viewingDetails.customer_address}<br/>
                      {viewingDetails.customer_plz} {viewingDetails.customer_city}<br/>
                      {viewingDetails.customer_country}
                    </p>
                  </div>
                  {viewingDetails.customer_phone && (
                    <div>
                      <span className="text-sm text-gray-600">Telefon:</span>
                      <p className="font-medium">{viewingDetails.customer_phone}</p>
                    </div>
                  )}
                </div>
                {viewingDetails.customer_note && (
                  <div className="mt-4">
                    <span className="text-sm text-gray-600">Bemerkungen:</span>
                    <p className="font-medium bg-yellow-50 p-3 rounded mt-1">{viewingDetails.customer_note}</p>
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3">Bestellte Produkte</h4>
                <div className="space-y-3">
                  {viewingDetails.items && viewingDetails.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                      <div>
                        <p className="font-medium">{item.title}</p>
                        <p className="text-sm text-gray-600">Menge: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{item.price.toFixed(2)} €</p>
                        <p className="text-sm text-gray-600">Gesamt: {(item.price * item.quantity).toFixed(2)} €</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status History */}
              {viewingDetails.status_history && viewingDetails.status_history.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-lg mb-3">Status-Verlauf</h4>
                  <div className="space-y-2">
                    {viewingDetails.status_history.slice().reverse().map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-white p-3 rounded border-l-4 border-blue-500">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {entry.status === 'neu' && '🆕'}
                            {entry.status === 'in_bearbeitung' && '⚙️'}
                            {entry.status === 'angebot_gesendet' && '📧'}
                            {entry.status === 'geprueft' && '✔️'}
                            {entry.status === 'bezahlt' && '💰'}
                            {entry.status === 'bestellt' && '🛒'}
                            {entry.status === 'versendet' && '✈️'}
                            {entry.status === 'abgeschlossen' && '✅'}
                            {entry.status === 'teilstorniert' && '⚠️'}
                            {entry.status === 'storniert' && '❌'}
                          </span>
                          <div>
                            <p className="font-semibold text-gray-900">{entry.status}</p>
                            {entry.note && <p className="text-sm text-gray-600">{entry.note}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {(() => {
                              const utcDateString = entry.timestamp.endsWith('Z') ? entry.timestamp : entry.timestamp + 'Z';
                              const date = new Date(utcDateString);
                              return date.toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'Europe/Berlin'
                              });
                            })()}
                          </p>
                          <p className="text-xs text-blue-600 font-semibold">👤 {entry.user || 'System'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    ℹ️ Hinweis: Nur Statusänderungen ab jetzt werden vollständig protokolliert
                  </p>
                </div>
              )}
              
              {/* Cancellation Reason if storniert */}
              {viewingDetails.status === 'storniert' && viewingDetails.cancel_reason && (
                <div className="mb-6 bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-lg mb-2 text-red-700">❌ Stornierungsgrund</h4>
                  <p className="text-gray-700">{viewingDetails.cancel_reason}</p>
                </div>
              )}
              
              {/* Partial Cancellation Info if teilstorniert */}
              {viewingDetails.status === 'teilstorniert' && (
                <div className="mb-6 bg-orange-50 border border-orange-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-lg mb-2 text-orange-700">⚠️ Teilstorno</h4>
                  <p className="text-gray-700 mb-2">
                    <strong>Rückerstattungsbetrag:</strong> {(viewingDetails.refund_amount || 0).toFixed(2)} €
                  </p>
                  {viewingDetails.cancel_reason && (
                    <p className="text-gray-700">
                      <strong>Grund:</strong> {viewingDetails.cancel_reason}
                    </p>
                  )}
                </div>
              )}
              
              {/* Tracking Numbers if versendet or teilstorniert */}
              {(viewingDetails.status === 'versendet' || viewingDetails.status === 'teilstorniert' || viewingDetails.status === 'abgeschlossen') && viewingDetails.tracking_numbers && viewingDetails.tracking_numbers.length > 0 && (
                <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-lg text-blue-700">📦 Sendungsnummern</h4>
                    <button
                      onClick={() => {
                        setTrackingNumber("");
                        setEditingInvoiceId(viewingDetails._id);
                        setShowTrackingModal(true);
                      }}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      title="Sendungsnummer hinzufügen"
                    >
                      ➕ Hinzufügen
                    </button>
                  </div>
                  <div className="space-y-2">
                    {viewingDetails.tracking_numbers.map((tracking, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm font-medium">#{index + 1}</span>
                        <p className="text-gray-700 font-mono text-lg flex-1">{tracking}</p>
                        {index === 0 && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Neueste</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Show "Add Tracking" button if no tracking numbers exist yet */}
              {(viewingDetails.status === 'versendet' || viewingDetails.status === 'teilstorniert' || viewingDetails.status === 'abgeschlossen') && (!viewingDetails.tracking_numbers || viewingDetails.tracking_numbers.length === 0) && (
                <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-lg text-blue-700">📦 Sendungsnummer</h4>
                    <button
                      onClick={() => {
                        setTrackingNumber("");
                        setEditingInvoiceId(viewingDetails._id);
                        setShowTrackingModal(true);
                      }}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      title="Sendungsnummer hinzufügen"
                    >
                      ➕ Hinzufügen
                    </button>
                  </div>
                  <p className="text-gray-500 text-sm mt-2">Noch keine Sendungsnummer hinzugefügt</p>
                </div>
              )}

              {/* Financial Summary */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">Finanzübersicht</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Netto:</span>
                    <span className="font-semibold">{(viewingDetails.total_netto || 0).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span>MwSt ({viewingDetails.tax_rate || 19}%):</span>
                    <span className="font-semibold">{(viewingDetails.mwst_amount || viewingDetails.vat_total || 0).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Brutto:</span>
                    <span>{(viewingDetails.total_amount || 0).toFixed(2)} €</span>
                  </div>
                  {viewingDetails.status === 'teilstorniert' && viewingDetails.refund_amount > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span>Rückerstattung:</span>
                      <span className="font-semibold">-{(viewingDetails.refund_amount || 0).toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-red-600 border-t pt-2">
                    <span>Ausgaben:</span>
                    <span className="font-semibold">-{(viewingDetails.ali_express_total || 0).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-green-600 border-t pt-2">
                    <span>Gewinn:</span>
                    <span>
                      {(() => {
                        const total_netto = viewingDetails.total_netto || 0;
                        const total_brutto = viewingDetails.total_amount || 0;
                        const ali_express_cost = viewingDetails.ali_express_total || 0;
                        const refund_amount = viewingDetails.refund_amount || 0;
                        
                        let profit = 0;
                        if (viewingDetails.status === 'storniert') {
                          // Full cancellation: only expenses as loss
                          profit = 0 - ali_express_cost;
                        } else if (viewingDetails.status === 'teilstorniert') {
                          // Partial cancellation: remaining netto after refund - expenses
                          const remaining_netto = (total_brutto - refund_amount) / 1.19;
                          profit = remaining_netto - ali_express_cost;
                        } else {
                          // Normal order: netto - expenses
                          profit = total_netto - ali_express_cost;
                        }
                        
                        return profit.toFixed(2);
                      })()}
                       €
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Cancellation Reason Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Stornierungsgrund</h3>
            <p className="text-sm text-gray-600 mb-4">
              Bitte geben Sie einen Grund für die Stornierung ein:
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 min-h-[100px]"
              placeholder="Grund für Stornierung..."
              required
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason("");
                  setEditingInvoiceId(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  if (!cancelReason.trim()) {
                    toast.error('Bitte geben Sie einen Stornierungsgrund ein');
                    return;
                  }
                  saveStatus(editingInvoiceId, 'storniert', cancelReason, null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Stornieren
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Tracking Number Modal */}
      {showTrackingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">📦 Sendungsnummer hinzufügen</h3>
            <p className="text-sm text-gray-600 mb-4">
              Bitte geben Sie die Sendungsnummer (Tracking-ID) ein:
            </p>
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 mb-4"
              placeholder="z.B. DHL12345678901"
              required
            />
            
            {/* Checkbox für Kundeninformation */}
            <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="sendNotification"
                checked={sendShipmentNotification}
                onChange={(e) => setSendShipmentNotification(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="sendNotification" className="text-sm text-gray-700 cursor-pointer">
                📧 Kundeninformation Versand (E-Mail mit Sendungsnummer an Kunden senden)
              </label>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowTrackingModal(false);
                  setTrackingNumber("");
                  setEditingInvoiceId(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  if (!trackingNumber.trim()) {
                    toast.error('Bitte geben Sie eine Sendungsnummer ein');
                    return;
                  }
                  // Check if we're editing from details view (viewingDetails exists) or from table
                  const currentStatus = viewingDetails ? viewingDetails.status : 'versendet';
                  saveStatus(editingInvoiceId, currentStatus, null, trackingNumber, viewingDetails ? viewingDetails.refund_amount : null, sendShipmentNotification);
                  
                  // Reload details if viewing details
                  if (viewingDetails) {
                    setTimeout(() => {
                      loadOrderDetails(editingInvoiceId);
                    }, 500);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Sendungsnummer hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Partial Cancellation Modal */}
      {showPartialCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4 text-orange-600">⚠️ Teilstorno</h3>
            <p className="text-sm text-gray-600 mb-4">
              Bitte geben Sie den Rückerstattungsbetrag und Grund ein:
            </p>
            
            <label className="block text-sm font-medium mb-2">Rückerstattungsbetrag (€)</label>
            <input
              type="number"
              step="0.01"
              value={refundAmount}
              onChange={(e) => setRefundAmount(parseFloat(e.target.value))}
              className="w-full border border-gray-300 rounded-lg p-3 mb-4"
              placeholder="0.00"
              required
            />
            
            <label className="block text-sm font-medium mb-2">Grund für Teilstorno</label>
            <textarea
              value={partialCancelReason}
              onChange={(e) => setPartialCancelReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 min-h-[80px]"
              placeholder="Grund für die Teilrückerstattung..."
              required
            />
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowPartialCancelModal(false);
                  setRefundAmount(0);
                  setPartialCancelReason("");
                  setEditingInvoiceId(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  if (!refundAmount || refundAmount <= 0) {
                    toast.error('Bitte geben Sie einen gültigen Rückerstattungsbetrag ein');
                    return;
                  }
                  if (!partialCancelReason.trim()) {
                    toast.error('Bitte geben Sie einen Grund ein');
                    return;
                  }
                  saveStatus(editingInvoiceId, 'teilstorniert', partialCancelReason, null, refundAmount);
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Teilstorno durchführen
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Address Edit Modal */}
      {showAddressModal && editingAddress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">📍 Adresse bearbeiten</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Straße</label>
                <input
                  type="text"
                  value={editingAddress.street}
                  onChange={(e) => setEditingAddress({...editingAddress, street: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="Musterstraße 123"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
                  <input
                    type="text"
                    value={editingAddress.plz}
                    onChange={(e) => setEditingAddress({...editingAddress, plz: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2"
                    placeholder="12345"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
                  <input
                    type="text"
                    value={editingAddress.city}
                    onChange={(e) => setEditingAddress({...editingAddress, city: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2"
                    placeholder="Berlin"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Land</label>
                <input
                  type="text"
                  value={editingAddress.country}
                  onChange={(e) => setEditingAddress({...editingAddress, country: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="Deutschland"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAddressModal(false);
                  setEditingAddress(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveAddress}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


// Wishlist Page Component
const WishlistPage = ({ 
  wishlist, 
  updateQuantity, 
  removeItem, 
  clearWishlist,
  API
}) => {
  const [showSubmitForm, setShowSubmitForm] = React.useState(false);
  const [customerData, setCustomerData] = React.useState({
    name: '',
    email: '',
    street: '',
    plz: '',
    city: '',
    country: 'Deutschland',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [shareLink, setShareLink] = React.useState('');
  
  // Calculate totals
  const subtotal = wishlist.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const serviceFee = 1.99;
  const riskBuffer = subtotal * 0.025; // 2.5% Risikopuffer
  const totalBrutto = subtotal + serviceFee + riskBuffer; // Gesamt Brutto
  const totalNetto = totalBrutto / 1.19; // Netto berechnen
  const vat = totalBrutto - totalNetto; // MwSt berechnen
  const total = totalBrutto; // Gesamt ist Brutto
  const MINIMUM_ORDER_VALUE = 10; // Mindestbestellwert in €
  
  const handleSubmitToImported24 = async () => {
    // Check minimum order value
    if (total < MINIMUM_ORDER_VALUE) {
      toast.error(`Mindestbestellwert beträgt ${MINIMUM_ORDER_VALUE}€`);
      return;
    }
    
    if (!customerData.name || !customerData.email || !customerData.street || !customerData.plz || !customerData.city) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const emailData = {
        customer: customerData,
        items: wishlist,
        calculation: {
          subtotal,
          serviceFee,
          riskBuffer,
          total
        }
      };
      
      // Send to backend
      const response = await axios.post(`${API}/wishlist/submit`, emailData);
      
      if (response.data.success) {
        toast.success('Wunschliste erfolgreich übermittelt! Sie erhalten in Kürze ein Angebot per Email. Das erstellte Angebot ist 48 Stunden gültig.');
        setShowSubmitForm(false);
        setCustomerData({ name: '', email: '', street: '', plz: '', city: '', country: 'Deutschland', phone: '', note: '' });
      } else {
        toast.error('Fehler beim Senden der Wunschliste');
      }
    } catch (error) {
      console.error('Error submitting wishlist:', error);
      toast.error('Fehler beim Senden der Wunschliste');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const [emailModalOpen, setEmailModalOpen] = React.useState(false);
  const [shareEmail, setShareEmail] = React.useState('');
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  
  const handleSendEmailShare = async () => {
    if (!shareEmail || !shareEmail.includes('@')) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }
    
    setIsSendingEmail(true);
    try {
      const encoded = btoa(JSON.stringify(wishlist));
      const link = `${window.location.origin}/?wishlist=${encoded}`;
      
      // Send email via backend
      const response = await axios.post(`${API}/wishlist/share-email`, {
        email: shareEmail,
        link: link,
        items: wishlist
      });
      
      if (response.data.success) {
        toast.success('Wunschliste erfolgreich per E-Mail versendet! 📧');
        setEmailModalOpen(false);
        setShareEmail('');
      } else {
        toast.error('Fehler beim Versenden der E-Mail');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Fehler beim Versenden der E-Mail');
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  const generateShareLink = () => {
    setEmailModalOpen(true);
  };
  
  if (wishlist.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center">
          <div className="text-8xl mb-6">❤️</div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Ihre Wunschliste ist leer</h1>
          <p className="text-gray-600 mb-8">Fügen Sie Produkte hinzu, um sie später zu kaufen oder an imported24 zu senden.</p>
          <a
            href="/"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Weiter einkaufen
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-bold text-gray-900">
          ❤️ Meine Wunschliste ({wishlist.length})
        </h1>
        <button
          onClick={clearWishlist}
          className="text-red-600 hover:text-red-800 font-medium"
        >
          🗑️ Alle entfernen
        </button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Produktliste */}
        <div className="lg:col-span-2 space-y-4">
          {wishlist.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-md p-4 flex gap-4">
              <img
                src={item.image || 'https://via.placeholder.com/100'}
                alt={item.title}
                className="w-24 h-24 object-cover rounded"
              />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-bold text-blue-600">{item.price.toFixed(2)}&nbsp;€</span>
                  {item.old_price && item.old_price > item.price && (
                    <span className="text-sm text-gray-500 line-through">{item.old_price.toFixed(2)}&nbsp;€</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      −
                    </button>
                    <span className="w-12 text-center font-semibold">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Entfernen
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {(item.price * item.quantity).toFixed(2)}&nbsp;€
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Zusammenfassung */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
            <h2 className="text-2xl font-bold mb-6">Zusammenfassung</h2>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-gray-700">
                <span>Zwischensumme:</span>
                <span>{subtotal.toFixed(2)}&nbsp;€</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Servicegebühr:</span>
                <span>{serviceFee.toFixed(2)}&nbsp;€</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Risikopuffer (2,5%):</span>
                <span>{riskBuffer.toFixed(2)}&nbsp;€</span>
              </div>
              <div className="border-t pt-3 flex justify-between text-lg font-semibold">
                <span>Gesamt (Brutto):</span>
                <span className="text-blue-600">{totalBrutto.toFixed(2)}&nbsp;€</span>
              </div>
              <div className="text-sm text-gray-500 space-y-1 pl-4">
                <div className="flex justify-between">
                  <span>davon Netto:</span>
                  <span>{totalNetto.toFixed(2)}&nbsp;€</span>
                </div>
                <div className="flex justify-between">
                  <span>davon MwSt. (19%):</span>
                  <span>{vat.toFixed(2)}&nbsp;€</span>
                </div>
              </div>
              {total < MINIMUM_ORDER_VALUE && (
                <div className="text-red-600 text-sm font-semibold mt-2">
                  ⚠️ Mindestbestellwert: {MINIMUM_ORDER_VALUE}€
                </div>
              )}
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Unverbindliche Vorberechnung</strong><br/>
                Der finale Preis kann nach Prüfung der Verfügbarkeit und Mengenrabatten abweichen.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => setShowSubmitForm(true)}
                disabled={total < MINIMUM_ORDER_VALUE}
                className={`w-full py-3 rounded-lg font-bold transition-colors ${
                  total < MINIMUM_ORDER_VALUE
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                📧 An imported24 senden
              </button>
              
              <button
                onClick={generateShareLink}
                className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                📧 Als E-Mail versenden
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Email Share Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Wunschliste per E-Mail versenden</h2>
              <button
                onClick={() => {
                  setEmailModalOpen(false);
                  setShareEmail('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail-Adresse des Empfängers</label>
              <input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="empfaenger@example.com"
              />
              <p className="text-xs text-gray-500 mt-2">
                Der Empfänger erhält einen Link zu Ihrer Wunschliste
              </p>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={handleSendEmailShare}
                disabled={isSendingEmail}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {isSendingEmail ? 'Wird gesendet...' : '✓ E-Mail senden'}
              </button>
              <button
                onClick={() => {
                  setEmailModalOpen(false);
                  setShareEmail('');
                }}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Submit Form Modal */}
      {showSubmitForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Bestellung an imported24 übermitteln</h2>
              <button
                onClick={() => setShowSubmitForm(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={customerData.name}
                  onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Max Mustermann"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail *</label>
                <input
                  type="email"
                  value={customerData.email}
                  onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="max@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Straße & Hausnummer *</label>
                <input
                  type="text"
                  value={customerData.street}
                  onChange={(e) => setCustomerData({ ...customerData, street: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Musterstraße 123"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PLZ *</label>
                  <input
                    type="text"
                    value={customerData.plz}
                    onChange={(e) => setCustomerData({ ...customerData, plz: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="12345"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stadt *</label>
                  <input
                    type="text"
                    value={customerData.city}
                    onChange={(e) => setCustomerData({ ...customerData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Berlin"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Land *</label>
                <select
                  value={customerData.country}
                  onChange={(e) => setCustomerData({ ...customerData, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Deutschland">Deutschland</option>
                  <option value="Österreich">Österreich</option>
                  <option value="Schweiz">Schweiz</option>
                  <option value="Andere">Andere</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefon (optional)</label>
                <input
                  type="tel"
                  value={customerData.phone}
                  onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="+49 123 456789"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bemerkungen (optional)</label>
                <textarea
                  value={customerData.note || ''}
                  onChange={(e) => setCustomerData({ ...customerData, note: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-y"
                  rows="3"
                  placeholder="Zusätzliche Hinweise oder Wünsche zur Bestellung..."
                />
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>So geht es weiter:</strong><br/>
                1. Wir prüfen Ihre Bestellung und passen ggf. Preise an<br/>
                2. Sie erhalten ein verbindliches Angebot per Email<br/>
                3. Nach Zahlungseingang lösen wir die Bestellung aus<br/>
                4. Versand erfolgt direkt von AliExpress (5-21 Tage)
              </p>
              <p className="text-sm text-blue-800 mt-3 font-semibold">
                ⏱️ Das erstellte Angebot ist 48 Stunden gültig.
              </p>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={handleSubmitToImported24}
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {isSubmitting ? 'Wird gesendet...' : '✓ Jetzt übermitteln'}
              </button>
              <button
                onClick={() => setShowSubmitForm(false)}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Settings Management Component
const AdminSettingsManagement = () => {
  const [settings, setSettings] = useState({
    company_name: 'imported24 Inh. Steve Schultze',
    address: 'Straße der Einheit 2, 15834 Rangsdorf',
    email: 'kontakt@imported24.de',
    phone: '',
    vat_id: 'DE295820071',
    responsible_person: 'imported24',
    impressum_content: '',
    agb_content: '',
    datenschutz_content: '',
    wishlist_enabled: true,
    maintenance_mode: false
  });
  const [linkCheckerSettings, setLinkCheckerSettings] = useState({
    link_check_enabled: false,
    link_check_time: '03:00',
    link_check_verify_aliexpress: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeSettingsTab, setActiveSettingsTab] = useState('company'); // company, legal, link_checker, users, app_function
  
  // App Function States
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [currentFavicon, setCurrentFavicon] = useState(null);
  const [faviconUploading, setFaviconUploading] = useState(false);
  
  // User Management States
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'mitarbeiter'
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadSettings();
    loadLinkCheckerSettings();
    loadUsers();
    loadCurrentFavicon();
    // Load current user from localStorage
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    setCurrentUser(user);
    
    // Set default tab based on role
    if (user.role !== 'admin') {
      setActiveSettingsTab('link_checker');
    }
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.success && response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einstellungen:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadLinkCheckerSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/link-checker`);
      if (response.data.success && response.data.settings) {
        setLinkCheckerSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Link-Checker Einstellungen:', error);
    }
  };

  const loadCurrentFavicon = async () => {
    try {
      const response = await axios.get(`${API}/settings/favicon`);
      if (response.data.success) {
        setCurrentFavicon(response.data);
      }
    } catch (error) {
      // No custom favicon set, that's okay
      console.log('No custom favicon set');
    }
  };

  const handleFaviconFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFaviconFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconUpload = async () => {
    if (!faviconFile) {
      setMessage({ type: 'error', text: 'Bitte wählen Sie eine Datei aus' });
      return;
    }

    setFaviconUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', faviconFile);

      const response = await axios.post(`${API}/settings/favicon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Favicon erfolgreich hochgeladen! Seite wird neu geladen...' });
        await loadCurrentFavicon();
        // Reload page to apply new favicon
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      console.error('Fehler beim Hochladen:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Fehler beim Hochladen des Favicons' });
    } finally {
      setFaviconUploading(false);
    }
  };

  const handleFaviconDelete = async () => {
    if (!confirm('Möchten Sie das Custom Favicon wirklich löschen? Das Standard-Favicon wird wiederhergestellt.')) {
      return;
    }

    setMessage(null);

    try {
      const response = await axios.delete(`${API}/settings/favicon`);

      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Favicon gelöscht! Seite wird neu geladen...' });
        setCurrentFavicon(null);
        setFaviconFile(null);
        setFaviconPreview(null);
        // Reload page to apply default favicon
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      setMessage({ type: 'error', text: 'Fehler beim Löschen des Favicons' });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await axios.post(`${API}/settings`, settings);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Einstellungen erfolgreich gespeichert!' });
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern der Einstellungen' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveLinkChecker = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await axios.put(`${API}/settings/link-checker`, linkCheckerSettings);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Link-Checker Einstellungen erfolgreich gespeichert!' });
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern der Link-Checker Einstellungen' });
    } finally {
      setIsSaving(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserFormData({
      username: '',
      email: '',
      password: '',
      role: 'mitarbeiter'
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role || 'mitarbeiter'
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        // Update existing user
        const response = await axios.put(`${API}/admin/users/${editingUser._id}`, userFormData);
        if (response.data.success) {
          toast.success('Benutzer aktualisiert');
          loadUsers();
          setShowUserModal(false);
        } else {
          toast.error(response.data.message);
        }
      } else {
        // Create new user
        const response = await axios.post(`${API}/admin/users`, userFormData);
        if (response.data.success) {
          toast.success('Benutzer erstellt');
          loadUsers();
          setShowUserModal(false);
        } else {
          toast.error(response.data.message);
        }
      }
    } catch (error) {
      toast.error('Fehler beim Speichern des Benutzers');
      console.error('Save user error:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich löschen?')) return;
    
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`);
      if (response.data.success) {
        toast.success('Benutzer gelöscht');
        loadUsers();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Fehler beim Löschen des Benutzers');
      console.error('Delete user error:', error);
    }
  };

  const handleChangePassword = async () => {
    if (passwordChangeData.new_password !== passwordChangeData.confirm_password) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    try {
      const response = await axios.put(`${API}/admin/change-password`, {
        username: currentUser.username,
        old_password: passwordChangeData.old_password,
        new_password: passwordChangeData.new_password
      });

      if (response.data.success) {
        toast.success('Passwort erfolgreich geändert');
        setShowPasswordChange(false);
        setPasswordChangeData({ old_password: '', new_password: '', confirm_password: '' });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Fehler beim Ändern des Passworts');
      console.error('Change password error:', error);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Lädt Einstellungen...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Profil & Einstellungen</h2>
        <p className="text-gray-600">Impressum- und Unternehmensdaten verwalten</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          {currentUser?.role === 'admin' && (
            <>
              <button
                onClick={() => setActiveSettingsTab('company')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSettingsTab === 'company'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🏢 Firmendaten
              </button>
              <button
                onClick={() => setActiveSettingsTab('legal')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSettingsTab === 'legal'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📄 Rechtliche Seiten
              </button>
            </>
          )}
          <button
            onClick={() => setActiveSettingsTab('link_checker')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeSettingsTab === 'link_checker'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🔗 Link-Checker
          </button>
          {currentUser?.role === 'admin' && (
            <>
              <button
                onClick={() => setActiveSettingsTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSettingsTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                👥 Benutzer
              </button>
              <button
                onClick={() => setActiveSettingsTab('app_function')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSettingsTab === 'app_function'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ⚙️ App Funktion
              </button>
            </>
          )}
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        {activeSettingsTab === 'company' && currentUser?.role === 'admin' && (
          <div className="space-y-6">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Firmenname / Betreibername *
            </label>
            <input
              type="text"
              value={settings.company_name}
              onChange={(e) => setSettings({...settings, company_name: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="imported24 Inh. Steve Schultze"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vollständige Adresse *
            </label>
            <input
              type="text"
              value={settings.address}
              onChange={(e) => setSettings({...settings, address: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Straße der Einheit 2, 15834 Rangsdorf"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-Mail-Adresse *
            </label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings({...settings, email: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="kontakt@imported24.de"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefonnummer
            </label>
            <input
              type="tel"
              value={settings.phone}
              onChange={(e) => setSettings({...settings, phone: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="+49 (0) 123 456789"
            />
          </div>

          {/* VAT ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              USt-IdNr.
            </label>
            <input
              type="text"
              value={settings.vat_id}
              onChange={(e) => setSettings({...settings, vat_id: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="DE295820071"
            />
          </div>

          {/* Responsible Person */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verantwortlich für den Inhalt (§ 55 Abs. 2 RStV) *
            </label>
            <input
              type="text"
              value={settings.responsible_person}
              onChange={(e) => setSettings({...settings, responsible_person: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="imported24"
            />
          </div>

          {/* Save Button */}
          <div className="pt-6 border-t">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`w-full px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
                isSaving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 shadow-lg'
              }`}
            >
              {isSaving ? '💾 Wird gespeichert...' : '💾 Einstellungen speichern'}
            </button>
          </div>

        </div>
        )}

        {activeSettingsTab === 'legal' && currentUser?.role === 'admin' && (
          <div className="space-y-8">
            <p className="text-gray-600 mb-4">
              Bearbeiten Sie hier die Inhalte von Impressum, AGB und Datenschutz. 
              Der Text wird als HTML gespeichert und auf den jeweiligen Seiten angezeigt.
            </p>

            {/* Impressum */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-3">
                📄 Impressum
              </label>
              <textarea
                value={settings.impressum_content}
                onChange={(e) => setSettings({...settings, impressum_content: e.target.value})}
                rows="12"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="HTML-Inhalt für Impressum-Seite eingeben..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Tipp: Sie können HTML verwenden, z.B. &lt;h2&gt;Überschrift&lt;/h2&gt; oder &lt;p&gt;Text&lt;/p&gt;
              </p>
            </div>

            {/* AGB */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-3">
                📋 Allgemeine Geschäftsbedingungen (AGB)
              </label>
              <textarea
                value={settings.agb_content}
                onChange={(e) => setSettings({...settings, agb_content: e.target.value})}
                rows="12"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="HTML-Inhalt für AGB-Seite eingeben..."
              />
            </div>

            {/* Datenschutz */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-3">
                🔒 Datenschutzerklärung
              </label>
              <textarea
                value={settings.datenschutz_content}
                onChange={(e) => setSettings({...settings, datenschutz_content: e.target.value})}
                rows="12"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="HTML-Inhalt für Datenschutz-Seite eingeben..."
              />
            </div>

            {/* Save Button for Legal Content */}
            <div className="pt-6 border-t">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`w-full px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
                  isSaving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 shadow-lg'
                }`}
              >
                {isSaving ? '💾 Wird gespeichert...' : '💾 Rechtliche Seiten speichern'}
              </button>
            </div>
          </div>
        )}

        {activeSettingsTab === 'link_checker' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">🔍 Was ist der Link-Checker?</h3>
              <p className="text-sm text-blue-800">
                Der Link-Checker überprüft automatisch alle Ihre Affiliate-Links auf Funktionsfähigkeit. 
                Defekte Links werden automatisch erkannt und die entsprechenden Produkte offline gesetzt.
              </p>
            </div>

            {/* Enable Link Checker */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  Link-Checker aktivieren
                </label>
                <p className="text-xs text-gray-600">
                  Automatische tägliche Überprüfung aller Affiliate-Links
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={linkCheckerSettings.link_check_enabled}
                  onChange={(e) => setLinkCheckerSettings({...linkCheckerSettings, link_check_enabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Check Time */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                ⏰ Tägliche Prüfzeit
              </label>
              <input
                type="time"
                value={linkCheckerSettings.link_check_time}
                onChange={(e) => setLinkCheckerSettings({...linkCheckerSettings, link_check_time: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Standard: 03:00 Uhr (nachts, wenn wenig Traffic ist)
              </p>
            </div>

            {/* Verify AliExpress */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  AliExpress-Verifizierung
                </label>
                <p className="text-xs text-gray-600">
                  Prüft zusätzlich, ob der Link noch auf AliExpress führt (kann false-positives erzeugen)
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={linkCheckerSettings.link_check_verify_aliexpress}
                  onChange={(e) => setLinkCheckerSettings({...linkCheckerSettings, link_check_verify_aliexpress: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Info Box */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Wichtige Hinweise</h4>
              <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                <li>Der Link-Checker läuft täglich zur eingestellten Zeit</li>
                <li>Defekte Produkte werden automatisch offline gesetzt</li>
                <li>Sie können Links auch manuell in der Produktverwaltung prüfen</li>
                <li>Die AliExpress-Verifizierung ist optional (empfohlen: aus)</li>
              </ul>
            </div>

            {/* Save Button */}
            <div className="pt-6 border-t">
              <button
                onClick={handleSaveLinkChecker}
                disabled={isSaving}
                className={`w-full px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
                  isSaving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 shadow-lg'
                }`}
              >
                {isSaving ? '💾 Wird gespeichert...' : '💾 Link-Checker Einstellungen speichern'}
              </button>
            </div>
          </div>
        )}



        {/* User Management Tab */}
        {activeSettingsTab === 'users' && currentUser?.role === 'admin' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Benutzerverwaltung</h2>
                <p className="text-gray-600 mt-1">Verwalten Sie Admin-Benutzer und Mitarbeiter</p>
              </div>
              <button
                onClick={handleAddUser}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-semibold"
              >
                + Benutzer hinzufügen
              </button>
            </div>

            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Benutzername</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rolle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Letzter Login</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium">{user.username}</span>
                        {user._id === currentUser?.id && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Sie</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {user.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role === 'admin' ? '👑 Admin' : '👤 Mitarbeiter'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString('de-DE') : 'Noch nie'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-800 mx-2"
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                        {user._id !== currentUser?.id && (
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="text-red-600 hover:text-red-800 mx-2"
                            title="Löschen"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* My Profile Section */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Mein Profil</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm text-gray-600">Benutzername:</span>
                  <p className="font-medium">{currentUser?.username}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Rolle:</span>
                  <p className="font-medium">{currentUser?.role === 'admin' ? 'Admin' : 'Mitarbeiter'}</p>
                </div>
              </div>
              <button
                onClick={() => setShowPasswordChange(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
              >
                🔒 Passwort ändern
              </button>
            </div>
          </div>
        )}

        {/* User Modal */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold mb-4">
                {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer hinzufügen'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Benutzername *</label>
                  <input
                    type="text"
                    value={userFormData.username}
                    onChange={(e) => setUserFormData({...userFormData, username: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    disabled={!!editingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail</label>
                  <input
                    type="email"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {editingUser ? 'Neues Passwort (leer lassen für keine Änderung)' : 'Passwort *'}
                  </label>
                  <input
                    type="password"
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rolle *</label>
                  <select
                    value={userFormData.role}
                    onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="mitarbeiter">Mitarbeiter</option>
                    <option value="admin">Admin</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Mitarbeiter haben keinen Zugriff auf Einstellungen
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveUser}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingUser ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordChange && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold mb-4">Passwort ändern</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Altes Passwort</label>
                  <input
                    type="password"
                    value={passwordChangeData.old_password}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, old_password: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Neues Passwort</label>
                  <input
                    type="password"
                    value={passwordChangeData.new_password}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, new_password: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Passwort bestätigen</label>
                  <input
                    type="password"
                    value={passwordChangeData.confirm_password}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, confirm_password: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordChangeData({ old_password: '', new_password: '', confirm_password: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleChangePassword}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Passwort ändern
                </button>
              </div>
            </div>
          </div>
        )}

        {/* App Function Tab */}
        {activeSettingsTab === 'app_function' && currentUser?.role === 'admin' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">App Funktion</h2>
              <p className="text-gray-600 mt-1">Passen Sie das Erscheinungsbild Ihrer App an</p>
            </div>

            {/* Browser Tab Icon Section */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🖼️ Browser-Tab-Icon ändern</h3>
              <p className="text-sm text-gray-600 mb-4">
                Laden Sie Ihr eigenes Favicon hoch (PNG, ICO, SVG oder JPEG). Empfohlene Größe: 32x32 oder 64x64 Pixel.
              </p>

              {/* Current Favicon Display */}
              {currentFavicon && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Aktuelles Favicon:</p>
                  <div className="flex items-center gap-4">
                    <img 
                      src={`data:${currentFavicon.content_type};base64,${currentFavicon.data}`}
                      alt="Current Favicon"
                      className="w-16 h-16 border border-gray-300 rounded"
                    />
                    <div>
                      <p className="text-sm text-gray-600">{currentFavicon.filename}</p>
                      <p className="text-xs text-gray-400">
                        Hochgeladen: {new Date(currentFavicon.uploaded_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <button
                      onClick={handleFaviconDelete}
                      className="ml-auto bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      🗑️ Löschen
                    </button>
                  </div>
                </div>
              )}

              {/* Upload New Favicon */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neues Favicon hochladen
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/x-icon,image/svg+xml,image/jpeg"
                    onChange={handleFaviconFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                {/* Preview */}
                {faviconPreview && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Vorschau:</p>
                    <img 
                      src={faviconPreview}
                      alt="Favicon Preview"
                      className="w-16 h-16 border border-gray-300 rounded"
                    />
                  </div>
                )}

                {/* Upload Button */}
                <button
                  onClick={handleFaviconUpload}
                  disabled={!faviconFile || faviconUploading}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    !faviconFile || faviconUploading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                  }`}
                >
                  {faviconUploading ? '⏳ Wird hochgeladen...' : '📤 Favicon hochladen'}
                </button>
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  💡 <strong>Tipp:</strong> Nach dem Hochladen wird die Seite automatisch neu geladen, damit das neue Favicon angezeigt wird. 
                  In manchen Browsern müssen Sie den Cache leeren (Strg+F5), um das neue Icon zu sehen.
                </p>
              </div>
            </div>

            {/* Hero GIF Section */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🎬 Startseiten-Animation (Hero GIF)</h3>
              <p className="text-sm text-gray-600 mb-4">
                Laden Sie ein animiertes GIF für die Startseite hoch (empfohlen: GIF oder PNG). Das Bild erscheint im Hintergrund der Hero-Section.
              </p>

              {/* Current Hero GIF Display */}
              {settings.hero_gif_url && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Aktuelles Hero GIF:</p>
                  <div className="flex items-center gap-4">
                    <img 
                      src={settings.hero_gif_url}
                      alt="Current Hero GIF"
                      className="w-32 h-32 border border-gray-300 rounded object-contain bg-white"
                    />
                    <div>
                      <p className="text-sm text-gray-600">{settings.hero_gif_filename || 'Custom GIF'}</p>
                      {settings.hero_gif_updated_at && (
                        <p className="text-xs text-gray-400">
                          Hochgeladen: {new Date(settings.hero_gif_updated_at).toLocaleDateString('de-DE')}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/settings/hero-gif`);
                          alert('✅ Hero GIF gelöscht');
                          loadSettings();
                        } catch (error) {
                          alert('❌ Fehler beim Löschen');
                        }
                      }}
                      className="ml-auto bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      🗑️ Löschen
                    </button>
                  </div>
                </div>
              )}

              {/* Upload New Hero GIF */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neues Hero GIF hochladen
                  </label>
                  <input
                    type="file"
                    accept="image/gif,image/png,image/jpeg"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        const response = await axios.post(`${API}/settings/upload-hero-gif`, formData, {
                          headers: {'Content-Type': 'multipart/form-data'}
                        });
                        
                        alert('✅ Hero GIF erfolgreich hochgeladen!');
                        loadSettings();
                      } catch (error) {
                        alert('❌ Fehler beim Hochladen: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                </div>
              </div>

              {/* Overlay Toggle */}
              <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Halbtransparentes Overlay</p>
                    <p className="text-xs text-gray-500">
                      Legt eine Farbschicht über das GIF für bessere Textlesbarkeit
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const newValue = !settings.hero_overlay_enabled;
                      try {
                        await axios.put(`${API}/settings/hero-overlay`, {enabled: newValue});
                        setSettings({...settings, hero_overlay_enabled: newValue});
                        alert(`✅ Overlay ${newValue ? 'aktiviert' : 'deaktiviert'}`);
                      } catch (error) {
                        alert('❌ Fehler beim Umschalten');
                      }
                    }}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      settings.hero_overlay_enabled !== false ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        settings.hero_overlay_enabled !== false ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-xs text-purple-800">
                  💡 <strong>Tipp:</strong> Verwenden Sie ein animiertes GIF für den besten Effekt. Die Standardgröße ist optimiert für Desktop und Mobile.
                </p>
              </div>
            </div>

            {/* Wishlist Function Section */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">❤️ Wunschlisten-Funktion</h3>
              <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Aktivieren/Deaktivieren Sie die Möglichkeit für Kunden, Wunschlisten zu erstellen und an imported24 zu senden.
                  </p>
                  <p className="text-xs text-gray-500">
                    Nützlich wenn Ihr Auftragsbuch voll ist.
                  </p>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSettings({...settings, wishlist_enabled: !settings.wishlist_enabled})}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      settings.wishlist_enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        settings.wishlist_enabled ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm font-semibold whitespace-nowrap">
                    {settings.wishlist_enabled ? '✓ Aktiv' : '✗ Inaktiv'}
                  </span>
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="mt-4 w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? '⏳ Speichern...' : '💾 Speichern'}
              </button>
            </div>

            {/* Maintenance Mode Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🔧 Wartungsmodus</h3>
              <div className="flex items-center justify-between bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Wenn aktiviert, sehen normale Besucher eine Wartungsseite. Admins haben weiterhin Zugriff.
                  </p>
                  <p className="text-xs text-gray-500">
                    Nutzen Sie dies, wenn Sie Updates durchführen oder die Seite vorübergehend schließen möchten.
                  </p>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSettings({...settings, maintenance_mode: !settings.maintenance_mode})}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      settings.maintenance_mode ? 'bg-orange-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        settings.maintenance_mode ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm font-semibold whitespace-nowrap">
                    {settings.maintenance_mode ? '⚠️ Wartung' : '✓ Online'}
                  </span>
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="mt-4 w-full bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? '⏳ Speichern...' : '💾 Speichern'}
              </button>
              
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-xs text-orange-800">
                  ⚠️ <strong>Hinweis:</strong> Im Wartungsmodus können Sie sich als Admin über 3x-Klick auf das Footer-Logo anmelden.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`mt-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-300 text-green-800' 
              : 'bg-red-50 border border-red-300 text-red-800'
          }`}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Category Management Component
const AdminCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ id: '', name: '', icon: '📦', commission: 5.0 });
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
    }
  };

  const handleAddCategory = async () => {
    try {
      await axios.post(`${API}/categories`, newCategory);
      setNewCategory({ id: '', name: '', icon: '📦', commission: 5.0 });
      loadCategories();
    } catch (error) {
      console.error('Fehler beim Hinzufügen:', error);
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    try {
      await axios.put(`${API}/categories/${categoryId}`, editingCategory);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      loadCategories();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Kategorien verwalten</h2>
        <p className="text-gray-600">Kategorien hinzufügen, bearbeiten und löschen</p>
      </div>

      {/* Add New Category */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Neue Kategorie hinzufügen</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="ID (z.B. neuheit)"
            value={newCategory.id}
            onChange={(e) => setNewCategory({...newCategory, id: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
          <input
            type="text"
            placeholder="Name (z.B. Neuheiten)"
            value={newCategory.name}
            onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
          <input
            type="text"
            placeholder="Icon (z.B. 🎁)"
            value={newCategory.icon}
            onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
          <input
            type="number"
            step="0.1"
            placeholder="Provision %"
            value={newCategory.commission}
            onChange={(e) => setNewCategory({...newCategory, commission: parseFloat(e.target.value)})}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <button
          onClick={handleAddCategory}
          className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
        >
          ➕ Kategorie hinzufügen
        </button>
      </div>

      {/* Existing Categories */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provision</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map((cat) => (
              <tr key={cat.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-2xl">{cat.icon}</td>
                <td className="px-6 py-4">
                  {editingCategory?.id === cat.id ? (
                    <input
                      type="text"
                      value={editingCategory.id}
                      onChange={(e) => setEditingCategory({...editingCategory, id: e.target.value})}
                      className="px-2 py-1 border rounded"
                    />
                  ) : (
                    <span className="text-sm text-gray-600">{cat.id}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingCategory?.id === cat.id ? (
                    <input
                      type="text"
                      value={editingCategory.name}
                      onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                      className="px-2 py-1 border rounded"
                    />
                  ) : (
                    <span className="font-medium">{cat.name}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingCategory?.id === cat.id ? (
                    <input
                      type="number"
                      step="0.1"
                      value={editingCategory.commission}
                      onChange={(e) => setEditingCategory({...editingCategory, commission: parseFloat(e.target.value)})}
                      className="px-2 py-1 border rounded w-20"
                    />
                  ) : (
                    <span>{cat.commission}%</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingCategory?.id === cat.id ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleUpdateCategory(cat.id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        ✅ Speichern
                      </button>
                      <button
                        onClick={() => setEditingCategory(null)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        ❌ Abbrechen
                      </button>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingCategory(cat)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        ✏️ Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        🗑️ Löschen
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Admin Product Management Component
const AdminProductManagement = ({ setActiveTab }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Pagination & Filter States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalProducts, setTotalProducts] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'online', 'offline', 'link_broken'
  const [filterLinkCheck, setFilterLinkCheck] = useState('all'); // 'all', 'checked', 'not_checked'
  const [filterPriceCheck, setFilterPriceCheck] = useState('all'); // 'all', 'fresh', 'warning', 'urgent'
  const [filterCategory, setFilterCategory] = useState('all'); // 'all' or specific category
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at', 'title', 'status', 'link_status', 'price_check'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [searchQuery, setSearchQuery] = useState(''); // Suchfeld für die API
  const [searchInput, setSearchInput] = useState(''); // Suchfeld für Input-Wert
  const [linkCheckerSettings, setLinkCheckerSettings] = useState(null); // Link-Checker Settings
  const searchTimeoutRef = useRef(null); // Ref für Timeout

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadLinkCheckerSettings();
  }, []);

  // Reload products when pagination or filters change
  useEffect(() => {
    loadProducts();
  }, [currentPage, itemsPerPage, filterStatus, filterLinkCheck, filterPriceCheck, filterCategory, sortBy, sortOrder, searchQuery]);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        skip: ((currentPage - 1) * itemsPerPage).toString(),
        limit: itemsPerPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Add search query
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      // Add filters
      if (filterStatus === 'online') {
        params.append('is_active', 'true');
      } else if (filterStatus === 'offline') {
        params.append('is_active', 'false');
      } else if (filterStatus === 'link_broken') {
        params.append('link_status', 'broken');
      }
      
      if (filterLinkCheck === 'checked') {
        params.append('link_checked', 'true');
      } else if (filterLinkCheck === 'not_checked') {
        params.append('link_checked', 'false');
      } else if (filterLinkCheck === 'broken') {
        params.append('link_status', 'broken');
      }
      
      // Add category filter
      if (filterCategory && filterCategory !== 'all') {
        params.append('category', filterCategory);
      }
      
      // Add cache-busting parameter to force fresh data
      params.append('_t', Date.now().toString());
      
      const response = await axios.get(`${API}/products/admin?${params.toString()}`);
      
      if (response.data) {
        setProducts(response.data.products || response.data);
        setTotalProducts(response.data.total || response.data.length);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Produkte:', error);
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setIsLoading(false);
      setInitialLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      if (response.data.success && response.data.categories) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
      // Fallback to default categories
      setCategories([
        { id: 'electronics', name: 'Elektronik', icon: '📱' },
        { id: 'gadgets', name: 'Gadgets', icon: '🔧' },
        { id: 'home', name: 'Haushalt', icon: '🏠' },
        { id: 'decoration', name: 'Dekoration', icon: '🎨' },
        { id: 'leisure', name: 'Freizeit', icon: '⚽' }
      ]);
    }
  };

  const loadLinkCheckerSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/link-checker`);
      if (response.data.success && response.data.settings) {
        setLinkCheckerSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Link-Checker Einstellungen:', error);
    }
  };

  // Debounced search function
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value); // Update input field immediately
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value); // This will trigger loadProducts via useEffect
      setCurrentPage(1);
    }, 500);
  };

  const getNextCheckTime = () => {
    if (!linkCheckerSettings) {
      return 'Lade...';
    }
    
    if (!linkCheckerSettings.link_check_enabled) {
      return 'Deaktiviert';
    }
    
    const checkTime = linkCheckerSettings.link_check_time || '03:00';
    const [hours, minutes] = checkTime.split(':');
    const now = new Date();
    const nextCheck = new Date();
    nextCheck.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // Wenn die Zeit heute schon vorbei ist, setze auf morgen
    if (nextCheck <= now) {
      nextCheck.setDate(nextCheck.getDate() + 1);
    }
    
    return nextCheck.toLocaleString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleToggleActive = async (productId, currentStatus) => {
    try {
      const newIsActive = currentStatus !== 'active'; // Toggle: wenn 'active', dann false, sonst true
      const response = await axios.patch(`${API}/products/${productId}/status`, {
        is_active: newIsActive
      });
      
      if (response.data.success) {
        toast.success(`Status zu "${newIsActive ? 'Online' : 'Offline'}" geändert!`);
        loadProducts();
      } else {
        toast.error('Fehler: ' + response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Status:', error);
      toast.error('Fehler beim Ändern des Status: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleBanner = async (productId) => {
    try {
      const response = await axios.patch(`${API}/products/${productId}/toggle-banner`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        loadProducts();
      } else {
        toast.error('Fehler: ' + response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Banner-Status:', error);
      toast.error('Fehler beim Ändern des Banner-Status');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Möchten Sie dieses Produkt wirklich löschen?')) {
      try {
        const response = await axios.delete(`${API}/products/${productId}`);
        if (response.data.success) {
          toast.success('Produkt gelöscht!');
          loadProducts();
        } else {
          toast.error('Fehler: ' + response.data.message);
        }
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        toast.error('Fehler beim Löschen: ' + (error.response?.data?.detail || error.message));
      }
    }
  };
  
  const handleCheckAllLinks = async () => {
    if (window.confirm('Möchten Sie alle Produkt-Links jetzt überprüfen? Dies kann einige Minuten dauern.')) {
      try {
        toast.loading('Link-Überprüfung gestartet...');
        const response = await axios.post(`${API}/check-links/all`);
        if (response.data.success) {
          toast.success('Link-Überprüfung läuft im Hintergrund. Seite wird in 5 Sekunden neu geladen.');
          setTimeout(() => {
            loadProducts();
          }, 5000);
        } else {
          toast.error('Fehler: ' + response.data.message);
        }
      } catch (error) {
        console.error('Fehler beim Link-Check:', error);
        toast.error('Fehler beim Link-Check: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleCheckSingleLink = async (productId) => {
    try {
      toast.loading('Prüfe Link...', { id: `check-${productId}` });
      
      const response = await axios.post(`${API}/check-links/${productId}`);
      
      if (response.data.success) {
        const result = response.data.result;
        if (result.status === 'valid') {
          toast.success('Link ist gültig! ✅', { id: `check-${productId}` });
        } else {
          toast.error(`Link defekt: ${result.error_reason}`, { id: `check-${productId}` });
        }
        // Reload products to show updated status
        loadProducts();
      } else {
        toast.error('Fehler beim Prüfen', { id: `check-${productId}` });
      }
    } catch (error) {
      console.error('Fehler beim Link-Check:', error);
      toast.error('Fehler beim Prüfen des Links', { id: `check-${productId}` });
    }
  };
  
  const handleResetPriceCheck = async (productId, event) => {
    event.stopPropagation(); // Prevent row click
    try {
      const response = await axios.post(`${API}/products/${productId}/reset-price-check`);
      
      if (response.data.success) {
        // Reload products to show updated status
        loadProducts();
      }
    } catch (error) {
      console.error('Fehler beim Zurücksetzen der Preisprüfung:', error);
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const handleEditProduct = (product) => {
    // Flatten nested price structure if it exists
    // Use ?? instead of || to preserve 0 values
    const flatProduct = {
      ...product,
      selling_price: product.selling_price ?? product.price?.selling_price ?? 0,
      market_price: product.market_price ?? product.price?.market_price ?? 0,
      ali_price: product.ali_price ?? product.price?.ali_price ?? 0,
      // Store the ID explicitly
      _id: product._id || product.id || product.ali_product_id
    };
    console.log('Editing product with ID:', flatProduct._id, flatProduct);
    console.log('  ali_price from product:', product.ali_price);
    console.log('  ali_price from product.price:', product.price?.ali_price);
    console.log('  final ali_price:', flatProduct.ali_price);
    setEditingProduct(flatProduct);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const productId = editingProduct._id || editingProduct.id;
      
      if (!productId) {
        console.error('Keine Produkt-ID gefunden:', editingProduct);
        alert('Fehler: Produkt-ID nicht gefunden. Bitte Seite neu laden.');
        return;
      }
      
      // Debug: Show raw values before parsing
      console.log('Raw values before parsing:');
      console.log('  selling_price:', editingProduct.selling_price, typeof editingProduct.selling_price);
      console.log('  market_price:', editingProduct.market_price, typeof editingProduct.market_price);
      console.log('  ali_price:', editingProduct.ali_price, typeof editingProduct.ali_price);
      
      // Prepare data - convert empty strings to numbers and handle German decimal separator
      const parsePrice = (value) => {
        console.log('  parsePrice called with:', value, typeof value);
        if (value === '' || value === null || value === undefined) {
          console.log('  → returning 0 (empty/null/undefined)');
          return 0;
        }
        // Replace German comma with dot
        const normalizedValue = String(value).replace(',', '.');
        console.log('  → normalized:', normalizedValue);
        const parsed = parseFloat(normalizedValue);
        console.log('  → parsed:', parsed, 'isNaN:', isNaN(parsed));
        return isNaN(parsed) ? 0 : parsed;
      };
      
      const updateData = {
        title: editingProduct.title,
        description: editingProduct.description,
        category: editingProduct.category,
        selling_price: parsePrice(editingProduct.selling_price),
        market_price: parsePrice(editingProduct.market_price),
        ali_price: parsePrice(editingProduct.ali_price),
        rating: editingProduct.rating,
        review_count: editingProduct.review_count,
        sales_count: editingProduct.sales_count,
        inventory: editingProduct.inventory,
        shipping_time: editingProduct.shipping_time,
        affiliate_link: editingProduct.affiliate_link || editingProduct.ali_product_link,
        main_image: editingProduct.main_image,
        images: editingProduct.images
      };
      
      console.log('Saving product ID:', productId);
      console.log('Update data:', updateData);
      
      const response = await axios.put(`${API}/products/${productId}`, updateData);
      
      console.log('Server response:', response.data);
      
      if (response.data.success) {
        alert('✅ Produkt erfolgreich gespeichert!');
        setShowEditModal(false);
        setEditingProduct(null);
        // Add delay to ensure backend has finished processing
        setTimeout(() => {
          loadProducts();
        }, 300);
      } else {
        alert('❌ Fehler beim Speichern: ' + response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      console.error('Error details:', error.response?.data);
      alert('❌ Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (initialLoading) {
    return <div className="text-center py-8">Lädt Produkte...</div>;
  }

  return (
    <>
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Produktverwaltung</h2>
          <div className="flex gap-3">
            <button
              onClick={() => setActiveTab('admin-add')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
            >
              ➕ Artikel hinzufügen
            </button>
            <button
              onClick={() => handleCheckAllLinks()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              🔗 Alle Links prüfen
            </button>
          </div>
        </div>
        
        {/* Search Field */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="🔍 Suche nach Produktnummer, Titel, Kategorie oder Status..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchInput}
              onChange={handleSearchChange}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                  setCurrentPage(1);
                }}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Next Check Time Display */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-blue-800">
                Nächste automatische Link-Überprüfung:
              </span>
            </div>
            <span className="text-sm font-bold text-blue-900">
              {linkCheckerSettings ? getNextCheckTime() : 'Lade...'}
            </span>
          </div>
          {linkCheckerSettings && linkCheckerSettings.link_check_enabled && (
            <p className="text-xs text-blue-600 mt-1">
              Automatische Überprüfung ist aktiviert und läuft täglich um {linkCheckerSettings.link_check_time || '03:00'} Uhr
            </p>
          )}
          {linkCheckerSettings && !linkCheckerSettings.link_check_enabled && (
            <p className="text-xs text-orange-600 mt-1">
              ⚠️ Automatische Überprüfung ist deaktiviert. Aktivieren Sie sie in den Einstellungen.
            </p>
          )}
        </div>
        
        {/* Filter & Pagination Controls */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle</option>
              <option value="online">🟢 Online</option>
              <option value="offline">⚫ Offline</option>
              <option value="link_broken">❌ Link defekt</option>
            </select>
          </div>
          
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kategorie</label>
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle Kategorien</option>
              {categories.map((cat) => (
                <option key={cat.id || cat.category} value={cat.id || cat.category}>
                  {cat.icon} {cat.name || cat.displayName}
                </option>
              ))}
            </select>
          </div>
          
          {/* Link Check Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Link-Prüfung</label>
            <select
              value={filterLinkCheck}
              onChange={(e) => {
                setFilterLinkCheck(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle</option>
              <option value="checked">✅ Geprüft</option>
              <option value="not_checked">⏳ Nicht geprüft</option>
              <option value="broken">❌ Fehler</option>
            </select>
          </div>
          
          {/* Price Check Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preis-Prüfung</label>
            <select
              value={filterPriceCheck}
              onChange={(e) => {
                setFilterPriceCheck(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle</option>
              <option value="fresh">🟢 Frisch (0-10d)</option>
              <option value="warning">🟡 Bald prüfen (11-21d)</option>
              <option value="urgent">🔴 Dringend (>21d)</option>
            </select>
          </div>
          
          {/* Items per page */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pro Seite</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          
          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sortieren</label>
            <select
              value={`${sortBy}_${sortOrder}`}
              onChange={(e) => {
                const value = e.target.value;
                // Letzter Teil nach dem letzten Unterstrich ist die Richtung
                const lastUnderscoreIndex = value.lastIndexOf('_');
                const field = value.substring(0, lastUnderscoreIndex);
                const order = value.substring(lastUnderscoreIndex + 1);
                setSortBy(field);
                setSortOrder(order);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="created_at_desc">Neueste zuerst</option>
              <option value="created_at_asc">Älteste zuerst</option>
              <option value="title_asc">Titel A-Z</option>
              <option value="title_desc">Titel Z-A</option>
              <option value="selling_price_asc">Preis aufsteigend</option>
              <option value="selling_price_desc">Preis absteigend</option>
              <option value="link_status_desc">Link-Status</option>
              <option value="price_check_asc">Preis-Check älteste</option>
              <option value="price_check_desc">Preis-Check neueste</option>
            </select>
          </div>
        </div>
        
        {/* Results Info */}
        <div className="mb-4 text-sm text-gray-600">
          Zeige {products.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} - {Math.min(currentPage * itemsPerPage, totalProducts)} von {totalProducts} Produkten
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nr.</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bild</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titel</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preis</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Netto</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E.Kauf</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kat.</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">News</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Klicks</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Preis</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Online</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Banner</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">Aktionen</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading && products.length === 0 ? (
                <tr>
                  <td colSpan="15" className="px-3 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Lädt Produkte...
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="15" className="px-3 py-8 text-center text-gray-500">
                    Keine Produkte gefunden
                  </td>
                </tr>
              ) : (
                products.filter((product) => {
                  // Apply price check filter
                  if (filterPriceCheck !== 'all') {
                    const daysSinceCheck = product.price_last_checked 
                      ? Math.floor((Date.now() - new Date(product.price_last_checked).getTime()) / (1000 * 60 * 60 * 24))
                      : 999;
                    
                    if (filterPriceCheck === 'fresh' && daysSinceCheck > 10) return false;
                    if (filterPriceCheck === 'warning' && (daysSinceCheck <= 10 || daysSinceCheck > 21)) return false;
                    if (filterPriceCheck === 'urgent' && daysSinceCheck <= 21) return false;
                  }
                  return true;
                }).map((product, index) => {
                const sellingPrice = product.price?.selling_price || product.selling_price || product.market_price || product.price || 0;
                
                // Get market_price from product data (promo material)
                let marketPrice = product.price?.market_price || product.market_price || 0;
                
                // Only calculate fallback price if no market_price is set
                if (!marketPrice || marketPrice === 0) {
                  marketPrice = sellingPrice * 1.35; // 35% higher as fallback
                }
                
                // Calculate row number based on pagination
                const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;
                
                return (
                  <tr key={product._id || product.id} className="hover:bg-gray-50">
                    {/* Produktnummer */}
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="inline-block px-1.5 py-0.5 bg-blue-100 rounded">
                        <span className="text-xs font-bold text-blue-800">
                          #{product.product_number || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="w-12 h-12 flex-shrink-0">
                        <img 
                          src={product.main_image || product.images?.[0] || 'https://via.placeholder.com/100'} 
                          alt={product.title}
                          className="w-full h-full object-cover rounded"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/100'}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1.5" style={{ maxWidth: '200px' }}>
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowDetailsModal(true);
                        }}
                        className="text-xs font-medium text-blue-600 hover:text-blue-900 hover:underline truncate text-left block w-full"
                        style={{ maxWidth: '200px' }}
                      >
                        {product.title}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="text-xs font-semibold text-green-600">{sellingPrice.toFixed(2)}&nbsp;€</div>
                    </td>
                    {/* Netto (Verkaufspreis - 19% MwSt) */}
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="text-xs text-gray-700">{(sellingPrice / 1.19).toFixed(2)}&nbsp;€</div>
                    </td>
                    {/* Einkaufspreis mit Link */}
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {product.affiliate_link || product.ali_product_link ? (
                        <a 
                          href={product.affiliate_link || product.ali_product_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                        >
                          {(product.ali_price || product.price?.ali_price || 0).toFixed(2)}&nbsp;€
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      ) : (
                        <div className="text-xs text-gray-500">{(product.ali_price || product.price?.ali_price || 0).toFixed(2)}&nbsp;€</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className="px-1.5 inline-flex text-xs leading-4 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-center">
                      <div className="text-xs font-semibold text-purple-600">{product.newsletter_sent_count || 0}</div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-center">
                      <div className="text-xs text-gray-900">{product.click_count || 0}</div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="text-xs text-gray-500">
                        {product.created_at ? new Date(product.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <button 
                          onClick={() => handleCheckSingleLink(product._id || product.id)}
                          className="hover:scale-110 transition-transform cursor-pointer"
                          title="Link prüfen"
                        >
                          {product.link_status === 'valid' && (
                            <div className="flex items-center space-x-0.5">
                              <span className="text-sm">✅</span>
                              {product.link_last_checked && (
                                <span className="text-xs text-gray-500">
                                  {Math.floor((Date.now() - new Date(product.link_last_checked).getTime()) / (1000 * 60 * 60 * 24))}d
                                </span>
                              )}
                            </div>
                          )}
                          {product.link_status === 'broken' && <span className="text-sm">❌</span>}
                          {(!product.link_status || product.link_status === 'pending') && <span className="text-sm">⏳</span>}
                        </button>
                      </div>
                    </td>
                    {/* Preis-Check Status */}
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      <div className="flex items-center justify-center space-x-1">
                        {(() => {
                          const daysSinceCheck = product.price_last_checked 
                            ? Math.floor((Date.now() - new Date(product.price_last_checked).getTime()) / (1000 * 60 * 60 * 24))
                            : 999;
                          
                          let circleColor = '';
                          if (daysSinceCheck <= 10) {
                            circleColor = 'bg-green-500';
                          } else if (daysSinceCheck <= 21) {
                            circleColor = 'bg-yellow-500';
                          } else {
                            circleColor = 'bg-red-500';
                          }
                          
                          return (
                            <>
                              <button
                                onClick={(e) => handleResetPriceCheck(product._id || product.id, e)}
                                className={`w-4 h-4 rounded-full ${circleColor} hover:opacity-80 transition-opacity cursor-pointer flex-shrink-0`}
                                title={`Preis vor ${daysSinceCheck}d geprüft`}
                              />
                              <span className="text-xs text-gray-600">{daysSinceCheck}d</span>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleActive(product._id || product.id, product.is_active ? 'active' : 'inactive')}
                        className="text-base hover:opacity-80 transition-opacity"
                        title={product.is_active !== false ? 'Online' : 'Offline'}
                      >
                        {product.is_active !== false ? '🟢' : '⚫'}
                      </button>
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleBanner(product._id || product.id)}
                        className="text-lg hover:opacity-80 transition-opacity"
                        title={product.featured_in_banner ? 'Im Banner (klicken zum Entfernen)' : 'Zum Banner hinzufügen'}
                      >
                        {product.featured_in_banner ? '⭐' : '☆'}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs sticky right-0 bg-white shadow-lg">
                      <div className="flex justify-between items-center">
                        <button
                          onClick={() => handleEditProduct(product)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product._id || product.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Löschen"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalProducts > itemsPerPage && (
          <div className="mt-6 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Seite {currentPage} von {Math.ceil(totalProducts / itemsPerPage)}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                ← Zurück
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(totalProducts / itemsPerPage), currentPage + 1))}
                disabled={currentPage >= Math.ceil(totalProducts / itemsPerPage)}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Weiter →
              </button>
            </div>
          </div>
        )}
        
        {products.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Keine Produkte gefunden
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingProduct && (
        <div className="fixed inset-0 z-[99999] overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowEditModal(false)}></div>
            
            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl relative z-[99999]">
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-4">Produkt bearbeiten</h3>
                
                {/* Product Info Banner */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 rounded">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {editingProduct.ali_product_id && (
                      <div>
                        <span className="font-semibold text-blue-900">AliExpress ID:</span>
                        <span className="ml-2 text-blue-700">{editingProduct.ali_product_id}</span>
                      </div>
                    )}
                    {editingProduct.updated_at && (
                      <div>
                        <span className="font-semibold text-blue-900">Zuletzt aktualisiert:</span>
                        <span className="ml-2 text-blue-700">
                          {new Date(editingProduct.updated_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    )}
                    {editingProduct.last_checked && (
                      <div>
                        <span className="font-semibold text-blue-900">Zuletzt geprüft:</span>
                        <span className="ml-2 text-blue-700">
                          {new Date(editingProduct.last_checked).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titel *</label>
                    <input
                      type="text"
                      value={editingProduct.title || ''}
                      onChange={(e) => setEditingProduct({...editingProduct, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {/* Prices */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Verkaufspreis (EUR) *</label>
                      <input
                        type="text"
                        value={editingProduct.selling_price || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, selling_price: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="z.B. 3,99 oder 3.99"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Alter Preis / UVP (EUR)</label>
                      <input
                        type="text"
                        value={editingProduct.market_price || editingProduct.original_price || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, market_price: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="z.B. 5,99 oder 5.99"
                      />
                      {editingProduct.original_price && (
                        <p className="text-xs text-gray-500 mt-1">
                          Original aus Promo: {editingProduct.original_price.toFixed(2)} €
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">AliExpress Einkaufspreis (EUR)</label>
                      <input
                        type="text"
                        value={editingProduct.ali_price || ''}
                        onChange={(e) => {
                          console.log('ali_price onChange:', e.target.value);
                          setEditingProduct({...editingProduct, ali_price: e.target.value});
                          console.log('State updated to:', e.target.value);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="z.B. 1,99 oder 1.99"
                      />
                      <p className="text-xs text-gray-500 mt-1">Aktueller Wert: {editingProduct.ali_price}</p>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Kategorie *</label>
                    <select
                      value={editingProduct.category || 'electronics'}
                      onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.length > 0 ? (
                        categories.map((cat) => (
                          <option key={cat.id || cat._id} value={cat.id || cat._id}>
                            {cat.icon} {cat.name}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="electronics">📱 Elektronik</option>
                          <option value="gadgets">🔧 Gadgets</option>
                          <option value="home">🏠 Haushalt</option>
                          <option value="decoration">🎨 Dekoration</option>
                          <option value="leisure">⚽ Freizeit</option>
                        </>
                      )}
                    </select>
                  </div>

                  {/* Video URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">🎥 Video URL</label>
                    <input
                      type="url"
                      value={editingProduct.video_url || ''}
                      onChange={(e) => setEditingProduct({...editingProduct, video_url: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/video.mp4"
                    />
                    {editingProduct.video_url && (
                      <p className="text-xs text-green-600 mt-1">✅ Video-URL gesetzt</p>
                    )}
                  </div>
                  
                  {/* Shipping Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">📦 Lieferzeit</label>
                    <select
                      value={editingProduct.shipping_time || '7-15 Tage'}
                      onChange={(e) => setEditingProduct({...editingProduct, shipping_time: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="3-5 Tage">🚀 Express: 3-5 Tage</option>
                      <option value="5-10 Tage">⚡ Schnell: 5-10 Tage</option>
                      <option value="7-15 Tage">📦 Standard: 7-15 Tage</option>
                      <option value="10-21 Tage">🌍 Economy: 10-21 Tage</option>
                      <option value="14-30 Tage">🚢 Sparversand: 14-30 Tage</option>
                      <option value="5-21 Tage">📅 Variabel: 5-21 Tage</option>
                    </select>
                  </div>

                  {/* Rating and Reviews */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">⭐ Bewertung (1-5)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="1"
                        max="5"
                        value={editingProduct.rating || 4.5}
                        onChange={(e) => setEditingProduct({...editingProduct, rating: parseFloat(e.target.value)})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">💬 Bewertungen</label>
                      <input
                        type="number"
                        value={editingProduct.review_count || 0}
                        onChange={(e) => setEditingProduct({...editingProduct, review_count: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">📦 Verkäufe</label>
                      <input
                        type="number"
                        value={editingProduct.sales_count || 0}
                        onChange={(e) => setEditingProduct({...editingProduct, sales_count: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Affiliate Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">🔗 Affiliate-Link</label>
                    <input
                      type="url"
                      value={editingProduct.affiliate_link || editingProduct.aliexpress_url || ''}
                      onChange={(e) => setEditingProduct({...editingProduct, affiliate_link: e.target.value, aliexpress_url: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://s.click.aliexpress.com/e/..."
                    />
                  </div>

                  {/* Images Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">🖼️ Produktbilder</label>
                    
                    {editingProduct.images && editingProduct.images.length > 0 ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          {editingProduct.images.map((img, index) => (
                            <div 
                              key={index} 
                              className={`relative group cursor-pointer border-4 rounded-lg overflow-hidden transition-all ${
                                (editingProduct.main_image === img || (!editingProduct.main_image && index === 0)) 
                                  ? 'border-blue-500 shadow-lg' 
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                              onClick={() => setEditingProduct({...editingProduct, main_image: img})}
                            >
                              <img 
                                src={img} 
                                alt={`Produktbild ${index + 1}`}
                                className="w-full h-32 object-cover"
                                onError={(e) => e.target.src = 'https://via.placeholder.com/200x200?text=Bild+nicht+verfügbar'}
                              />
                              {(editingProduct.main_image === img || (!editingProduct.main_image && index === 0)) && (
                                <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-bold">
                                  ★ HAUPT
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                                <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">
                                  Als Hauptbild wählen
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          💡 Klicken Sie auf ein Bild, um es als Hauptbild festzulegen
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                        <p className="text-gray-500">Keine Bilder vorhanden</p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
                    <textarea
                      value={editingProduct.description || ''}
                      onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                      rows="6"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {/* Promo Material */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📢 Promo-Material / Marketing-Text
                      <span className="text-xs text-gray-500 ml-2">(aus AliExpress Import)</span>
                    </label>
                    <textarea
                      value={editingProduct.promo_text || ''}
                      onChange={(e) => setEditingProduct({...editingProduct, promo_text: e.target.value})}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Werbetexte, Highlights, besondere Features aus AliExpress..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-4 pt-4 border-t sticky bottom-0 bg-white">
                    <button
                      onClick={() => setShowEditModal(false)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Speichern
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Product Details Modal */}
      {showDetailsModal && selectedProduct && (
        <ProductDetailsModal 
          product={selectedProduct}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </div>
    </>
  );
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });

      if (response.data.success) {
        // Store user info in localStorage
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        localStorage.setItem('admin_logged_in', 'true');
        onLogin(true);
      } else {
        setError(response.data.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (err) {
      setError('Ungültige Anmeldedaten');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/password-reset-request`, {
        email: resetEmail
      });
      
      if (response.data.success) {
        toast.success('Reset-Link wurde an Ihre E-Mail gesendet');
        setShowForgotPassword(false);
        setResetEmail('');
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error('Fehler beim Senden des Reset-Links');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20">
      <div className="bg-white rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-magenta-600 p-3 rounded-lg inline-block mb-4">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Anmeldung</h2>
          <p className="text-gray-600 mt-2">Bitte melden Sie sich an</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Benutzername
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••••"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition duration-300 ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 via-purple-600 to-magenta-600 text-white hover:from-blue-700 hover:via-purple-700 hover:to-magenta-700'
            }`}
          >
            {isLoading ? 'Anmeldung läuft...' : 'Anmelden'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Passwort vergessen?
            </button>
          </div>
        </form>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold mb-4">Passwort zurücksetzen</h3>
              <form onSubmit={handleForgotPassword}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail-Adresse
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ihre@email.de"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold ${
                      isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isLoading ? 'Senden...' : 'Reset-Link senden'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Admin Panel Component

// Duplicate Decision Modal Component
const DuplicateDecisionModal = ({ isOpen, onClose, duplicates, decisions, onDecisionChange, onConfirm }) => {
  if (!isOpen || !duplicates || duplicates.length === 0) return null;

  const getDecisionStyle = (decision) => {
    switch (decision) {
      case 'ignore':
        return 'bg-green-100 border-green-500 text-green-800';
      case 'replace':
        return 'bg-blue-100 border-blue-500 text-blue-800';
      case 'skip':
      default:
        return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity z-[99998]" onClick={onClose}></div>
        
        <div className="inline-block w-full max-w-5xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl relative z-[99999]">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b bg-yellow-50">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <span className="mr-2">⚠️</span>
                Duplikate gefunden
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {duplicates.length} Produkte sind bereits in der Datenbank. Bitte wählen Sie für jedes Duplikat eine Aktion.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="space-y-4">
              {duplicates.map((duplicate, index) => (
                <div key={duplicate.product_id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start gap-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      <img 
                        src={duplicate.image_url || 'https://via.placeholder.com/80'} 
                        alt={duplicate.title}
                        className="w-20 h-20 object-cover rounded"
                      />
                    </div>
                    
                    {/* Product Info */}
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{duplicate.title}</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>🆔 Product ID: {duplicate.product_id}</div>
                        <div>💰 Neuer Preis: {duplicate.discount_price.toFixed(2)} €</div>
                        <div>📊 Verkäufe: {duplicate.sales}</div>
                        <div>💵 Provision: {duplicate.commission_rate}%</div>
                      </div>
                      
                      {/* Existing Product Info */}
                      {duplicate.existing_product && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                          <div className="font-medium text-blue-900 mb-1">Vorhandenes Produkt:</div>
                          <div className="text-blue-700">
                            <div>Titel: {duplicate.existing_product.title}</div>
                            <div>Preis: {duplicate.existing_product.selling_price.toFixed(2)} €</div>
                            <div>Kategorie: {duplicate.existing_product.category}</div>
                            <div>Status: {duplicate.existing_product.is_active ? '✅ Aktiv' : '❌ Inaktiv'}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Decision Buttons */}
                    <div className="flex-shrink-0">
                      <div className="text-xs text-gray-600 mb-2 font-semibold">Aktion wählen:</div>
                      <div className="space-y-2">
                        <button
                          onClick={() => onDecisionChange(duplicate.product_id, 'ignore')}
                          className={`w-full px-3 py-2 text-sm rounded border-2 transition-all ${
                            decisions[duplicate.product_id] === 'ignore' 
                              ? getDecisionStyle('ignore') + ' font-semibold' 
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          ➕ Hinzufügen
                          <div className="text-xs mt-0.5 opacity-75">Beide behalten</div>
                        </button>
                        
                        <button
                          onClick={() => onDecisionChange(duplicate.product_id, 'replace')}
                          className={`w-full px-3 py-2 text-sm rounded border-2 transition-all ${
                            decisions[duplicate.product_id] === 'replace' 
                              ? getDecisionStyle('replace') + ' font-semibold' 
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          🔄 Ersetzen
                          <div className="text-xs mt-0.5 opacity-75">Altes überschreiben</div>
                        </button>
                        
                        <button
                          onClick={() => onDecisionChange(duplicate.product_id, 'skip')}
                          className={`w-full px-3 py-2 text-sm rounded border-2 transition-all ${
                            decisions[duplicate.product_id] === 'skip' 
                              ? getDecisionStyle('skip') + ' font-semibold' 
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          ⏭️ Überspringen
                          <div className="text-xs mt-0.5 opacity-75">Nicht importieren</div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-6 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{duplicates.length}</span> Duplikate zu überprüfen
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
              >
                Abbrechen
              </button>
              <button
                onClick={onConfirm}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition font-medium"
              >
                ✅ Import fortsetzen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const AdminPanel = ({ onImportProducts, stats, isLoading }) => {
  const [importKeyword, setImportKeyword] = useState("");
  const [importCategory, setImportCategory] = useState("electronics");
  const [isImporting, setIsImporting] = useState(false);
  
  // CSV Import State
  const [isCSVUploading, setIsCSVUploading] = useState(false);
  const [csvImportResult, setCSVImportResult] = useState(null);

  const handleImport = async () => {
    if (!importKeyword.trim()) return;
    
    setIsImporting(true);
    try {
      await onImportProducts(importKeyword, importCategory);
      setImportKeyword("");
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setIsImporting(false);
    }
  };

  // CSV Upload Handler
  const handleCSVUpload = async (file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Bitte wählen Sie eine CSV-Datei');
      return;
    }
    
    setIsCSVUploading(true);
    setCSVImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/csv/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setCSVImportResult(response.data);
        toast.success(`${response.data.successful_imports} Produkte erfolgreich importiert!`);
        
        // Refresh products list and stats
        window.location.reload(); // Simple refresh for now
      } else {
        setCSVImportResult(response.data);
        toast.error(response.data.message || 'CSV-Import fehlgeschlagen');
      }
      
    } catch (error) {
      console.error('CSV upload failed:', error);
      const errorMessage = error.response?.data?.detail || 'CSV-Upload fehlgeschlagen';
      setCSVImportResult({
        success: false,
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setIsCSVUploading(false);
    }
  };

  // Download CSV Template
  const downloadCSVTemplate = async () => {
    try {
      const response = await axios.get(`${API}/csv/template`);
      
      if (response.data.success) {
        const blob = new Blob([response.data.template], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('CSV-Vorlage heruntergeladen');
      }
    } catch (error) {
      console.error('Template download failed:', error);
      toast.error('Fehler beim Herunterladen der Vorlage');
    }
  };

  // View CSV Products
  const viewCSVProducts = async () => {
    try {
      const response = await axios.get(`${API}/csv/products?limit=10`);
      
      if (response.data.success) {
        const count = response.data.count;
        if (count > 0) {
          toast.success(`${count} CSV-Produkte gefunden - Siehe Produktkatalog`);
          // Could implement a modal to show CSV products here
        } else {
          toast.info('Keine CSV-Produkte gefunden');
        }
      }
    } catch (error) {
      console.error('Failed to get CSV products:', error);
      toast.error('Fehler beim Laden der CSV-Produkte');
    }
  };

  // Clear CSV Products
  const clearCSVProducts = async () => {
    if (!window.confirm('Alle CSV-Produkte löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/csv/products`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setCSVImportResult(null);
        // Refresh products list and stats
        window.location.reload(); // Simple refresh for now
      }
    } catch (error) {
      console.error('Failed to clear CSV products:', error);
      toast.error('Fehler beim Löschen der CSV-Produkte');
    }
  };


  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4">Admin Dashboard</h3>
        
        {/* Enhanced Stats with eBay metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">{stats.total_products}</div>
            <div className="text-sm opacity-90">Produkte im Katalog</div>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">{stats.average_margin}%</div>
            <div className="text-sm opacity-90">Durchschnittsmarge</div>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">{stats.total_orders}</div>
            <div className="text-sm opacity-90">Bestellungen</div>
          </div>
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">{stats.total_revenue}&nbsp;€</div>
            <div className="text-sm opacity-90">Gesamtumsatz</div>
          </div>
        </div>

        {/* New row for eBay and additional metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">{stats.ebay_listings || 0}</div>
            <div className="text-sm opacity-90">eBay Listings aktiv</div>
          </div>
          <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">{stats.categories_count}</div>
            <div className="text-sm opacity-90">Produktkategorien</div>
          </div>
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold">Multi-Channel</div>
            <div className="text-sm opacity-90">Website + eBay</div>
          </div>
        </div>

        {/* AliExpress Import Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold mb-3 flex items-center">
            <span className="mr-2">🚀</span>
            AliExpress Produktimport
          </h4>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Suchbegriff eingeben..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              value={importKeyword}
              onChange={(e) => setImportKeyword(e.target.value)}
            />
            <select
              className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              value={importCategory}
              onChange={(e) => setImportCategory(e.target.value)}
            >
              <option value="electronics">Elektronik</option>
              <option value="gadgets">Gadgets</option>
              <option value="home">Haushalt</option>
              <option value="decoration">Dekoration</option>
              <option value="leisure">Freizeit</option>
            </select>
            <button
              onClick={handleImport}
              disabled={isImporting || !importKeyword.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? "Importiere..." : "Importieren"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            ⚠️ AliExpress API momentan im Wartungsmodus - Bald wieder verfügbar
          </p>
        </div>

        {/* CSV Import Section */}
        <div className="bg-green-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold mb-3 flex items-center">
            <span className="mr-2">📄</span>
            CSV/Excel Produktimport
          </h4>
          
          {/* CSV Upload Area */}
          <div className="border-2 border-dashed border-green-300 rounded-lg p-6 mb-4 text-center">
            <input
              type="file"
              accept=".csv"
              onChange={(e) => handleCSVUpload(e.target.files[0])}
              className="hidden"
              id="csvFile"
              disabled={isCSVUploading}
            />
            <label
              htmlFor="csvFile"
              className={`cursor-pointer ${isCSVUploading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="text-green-500 text-4xl mb-2">📁</div>
              <div className="text-gray-700 font-medium mb-1">
                {isCSVUploading ? "Importiere CSV..." : "CSV-Datei auswählen"}
              </div>
              <div className="text-sm text-gray-500">
                Klicken oder Datei hierher ziehen (max. 10MB)
              </div>
            </label>
          </div>
          
          {/* CSV Import Results */}
          {csvImportResult && (
            <div className={`p-3 rounded-lg mb-3 ${csvImportResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <div className="font-medium">{csvImportResult.message}</div>
              {csvImportResult.success && csvImportResult.successful_imports > 0 && (
                <div className="text-sm mt-1">
                  {csvImportResult.successful_imports} von {csvImportResult.total_rows} Zeilen erfolgreich importiert
                  {csvImportResult.errors && csvImportResult.errors.length > 0 && (
                    <div className="mt-2">
                      <details>
                        <summary className="cursor-pointer text-orange-700">Fehler anzeigen ({csvImportResult.errors.length})</summary>
                        <ul className="mt-1 text-xs">
                          {csvImportResult.errors.slice(0, 5).map((error, index) => (
                            <li key={index} className="text-red-600">• {error}</li>
                          ))}
                          {csvImportResult.errors.length > 5 && (
                            <li className="text-gray-600">... und {csvImportResult.errors.length - 5} weitere</li>
                          )}
                        </ul>
                      </details>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* CSV Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={downloadCSVTemplate}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              📋 Vorlage herunterladen
            </button>
            <button
              onClick={viewCSVProducts}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              👁️ CSV-Produkte anzeigen
            </button>
            <button
              onClick={clearCSVProducts}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              🗑️ CSV-Produkte löschen
            </button>
          </div>
          
          <div className="text-xs text-gray-500 mt-3">
            <strong>Erforderliche Spalten:</strong> title, price, category<br/>
            <strong>Optionale Spalten:</strong> description, image_url, inventory, brand, shipping_time
          </div>
        </div>

        {/* AliExpress Excel Import Section */}
        <div className="bg-purple-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold mb-3 flex items-center">
            <span className="mr-2">📊</span>
            AliExpress Excel Import (Ad-Center Export)
          </h4>
          
          {/* Excel Upload Area */}
          <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 mb-4 text-center">
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => handleExcelFileSelect(e.target.files[0])}
              className="hidden"
              id="excelFile"
              disabled={isAnalyzingExcel || isImportingExcel}
            />
            <label
              htmlFor="excelFile"
              className={`cursor-pointer ${(isAnalyzingExcel || isImportingExcel) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="text-purple-500 text-4xl mb-2">📂</div>
              <div className="text-gray-700 font-medium mb-1">
                {excelFile ? `📄 ${excelFile.name}` : "Excel-Datei auswählen (.xls, .xlsx)"}
              </div>
              <div className="text-sm text-gray-500">
                AliExpress Ad-Center Export hochladen
              </div>
            </label>
          </div>
          
          {/* Analyze Button */}
          {excelFile && !excelAnalysis && (
            <div className="mb-4">
              <button
                onClick={analyzeExcelFile}
                disabled={isAnalyzingExcel}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 font-medium"
              >
                {isAnalyzingExcel ? '🔄 Analysiere Datei...' : '🔍 Datei analysieren'}
              </button>
            </div>
          )}
          
          {/* Analysis Results */}
          {excelAnalysis && (
            <div className="bg-white rounded-lg p-4 mb-4">
              <h5 className="font-semibold mb-3">📋 Analyse-Ergebnis</h5>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-blue-50 p-3 rounded">
                  <div className="text-gray-600">Gesamt</div>
                  <div className="text-2xl font-bold text-blue-600">{excelAnalysis.total_rows}</div>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <div className="text-gray-600">Neue Produkte</div>
                  <div className="text-2xl font-bold text-green-600">{excelAnalysis.new_products}</div>
                </div>
                <div className="bg-yellow-50 p-3 rounded">
                  <div className="text-gray-600">Duplikate</div>
                  <div className="text-2xl font-bold text-yellow-600">{excelAnalysis.duplicates}</div>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <div className="text-gray-600">Fehler</div>
                  <div className="text-2xl font-bold text-red-600">{excelAnalysis.errors}</div>
                </div>
              </div>
              
              {excelAnalysis.duplicates > 0 && (
                <button
                  onClick={() => setShowExcelDuplicateModal(true)}
                  className="w-full mt-3 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-medium"
                >
                  ⚠️ {excelAnalysis.duplicates} Duplikate überprüfen
                </button>
              )}
              
              {excelAnalysis.duplicates === 0 && (
                <button
                  onClick={executeExcelImport}
                  disabled={isImportingExcel}
                  className="w-full mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 font-medium"
                >
                  {isImportingExcel ? '⏳ Importiere...' : `✅ ${excelAnalysis.new_products} Produkte importieren`}
                </button>
              )}
            </div>
          )}
          
          {/* Import Results */}
          {excelImportResult && (
            <div className={`p-4 rounded-lg mb-3 ${excelImportResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <div className="font-medium">{excelImportResult.message}</div>
              {excelImportResult.success && (
                <div className="text-sm mt-2 space-y-1">
                  <div>✅ Importiert: {excelImportResult.imported}</div>
                  <div>🔄 Ersetzt: {excelImportResult.replaced}</div>
                  <div>⏭️ Übersprungen: {excelImportResult.skipped}</div>
                  {excelImportResult.errors > 0 && (
                    <div className="text-red-600">❌ Fehler: {excelImportResult.errors}</div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="text-xs text-gray-500 mt-3">
            <strong>ℹ️ Hinweis:</strong> Alle importierten Produkte werden in die Kategorie "neue Produkte" eingeordnet.
            Eine manuelle Kategoriezuordnung kann später erfolgen.
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-semibold mb-3 flex items-center">
            <span className="mr-2">⚡</span>
            Schnellaktionen
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button className="flex items-center justify-center p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition duration-300">
              <span className="mr-2">📧</span>
              E-Mail System testen
            </button>
            <button className="flex items-center justify-center p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition duration-300">
              <span className="mr-2">🏪</span>
              eBay Listings verwalten
            </button>
            <button className="flex items-center justify-center p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition duration-300">
              <span className="mr-2">📊</span>
              Verkaufsstatistiken
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  const [products, setProducts] = useState([]);
  const [bannerProducts, setBannerProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    total_products: 0,
    total_orders: 0,
    total_revenue: 0,
    average_margin: 0,
    categories_count: 0,
    ebay_listings: 0
  });
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('active_tab') || "products";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("home"); // home, impressum, agb, wishlist
  const [siteSettings, setSiteSettings] = useState({
    company_name: "imported24 Inh. Steve Schultze",
    address: "Straße der Einheit 2, 15834 Rangsdorf",
    email: "kontakt@imported24.de",
    phone: "",
    vat_id: "DE295820071",
    responsible_person: "imported24",
    wishlist_enabled: true
  });
  
  // Save activeTab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('active_tab', activeTab);
  }, [activeTab]);
  
  // AliExpress Excel Import State (Haupt-App-Level)
  const [excelFile, setExcelFile] = useState(null);
  const [excelAnalysis, setExcelAnalysis] = useState(null);
  const [isAnalyzingExcel, setIsAnalyzingExcel] = useState(false);
  const [isImportingExcel, setIsImportingExcel] = useState(false);
  const [excelImportResult, setExcelImportResult] = useState(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState({});
  const [showExcelDuplicateModal, setShowExcelDuplicateModal] = useState(false);
  
  // Pagination für öffentlichen Katalog
  const [publicPage, setPublicPage] = useState(1);
  const [publicLimit, setPublicLimit] = useState(20); // Optimiert: 20 Produkte für gute Balance
  const [totalProducts, setTotalProducts] = useState(0);
  const [publicSortOrder, setPublicSortOrder] = useState("newest");
  
  // Wunschliste (Wishlist)
  const [wishlist, setWishlist] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  
  // Admin authentication
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(() => {
    return localStorage.getItem('admin_logged_in') === 'true';
  });

  const handleAdminLogout = () => {
    setIsAdminLoggedIn(false);
    localStorage.removeItem('admin_logged_in');
    setActiveTab('products');
  };

  // Load site settings on mount
  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data.success && response.data.settings) {
          setSiteSettings(response.data.settings);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSiteSettings();
  }, []);

  // Categories data
  const categories = [
    {
      category: "electronics",
      displayName: "Elektronik",
      icon: "📱",
      description: "Smartphones, Tablets, Zubehör & mehr",
      productCount: 0
    },
    {
      category: "gadgets",
      displayName: "Gadgets",
      icon: "🔧",
      description: "Innovative Tools & Werkzeuge",
      productCount: 0
    },
    {
      category: "home",
      displayName: "Haushalt",
      icon: "🏠",
      description: "Küche, Bad, Wohnen",
      productCount: 0
    },
    {
      category: "decoration",
      displayName: "Dekoration",
      icon: "🎨",
      description: "Wanddeko, Figuren, Lichter",
      productCount: 0
    },
    {
      category: "leisure",
      displayName: "Freizeit",
      icon: "⚽",
      description: "Sport, Outdoor & Freizeit",
      productCount: 0
    }
  ];

  // Fetch products mit Pagination
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const skip = (publicPage - 1) * publicLimit;
      
      // Build query params
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: publicLimit.toString(),
        format: 'object',  // Request object format with pagination
        sort: publicSortOrder  // Add sorting
      });
      
      console.log('Fetching products with sort:', publicSortOrder, 'URL:', `${API}/products?${params.toString()}`);
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      
      console.log('Received products:', response.data.products?.length, 'total:', response.data.total);
      
      // Check if response is array (old format) or object (new format with pagination)
      if (Array.isArray(response.data)) {
        setProducts(response.data);
        setTotalProducts(response.data.length);
      } else {
        setProducts(response.data.products || []);
        setTotalProducts(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };
  
  // Fetch banner products
  const fetchBannerProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/banner/featured`);
      if (response.data.success) {
        setBannerProducts(response.data.products || []);
      }
    } catch (error) {
      console.error("Error fetching banner products:", error);
    }
  };

  // Import products
  const handleImportProducts = async (keyword, category) => {
    try {
      const response = await axios.post(`${API}/products/import`, {
        keyword,
        category,
        sort: "relevance"
      });
      
      if (response.data.imported_count > 0) {
        await fetchProducts();
        await fetchStats();
        toast.success(`${response.data.imported_count} Produkte erfolgreich importiert!`);
      } else {
        toast.info("Keine neuen Produkte gefunden.");
      }
    } catch (error) {
      console.error("Error importing products:", error);
      toast.error("Fehler beim Importieren der Produkte.");
    }
  };

  // AliExpress Excel Import Handlers
  const handleExcelFileSelect = (file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.xls') && !file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Bitte wählen Sie eine Excel-Datei (.xls oder .xlsx)');
      return;
    }
    
    setExcelFile(file);
    setExcelAnalysis(null);
    setExcelImportResult(null);
    setDuplicateDecisions({});
  };

  const analyzeExcelFile = async () => {
    if (!excelFile) {
      toast.error('Bitte wählen Sie zuerst eine Excel-Datei');
      return;
    }
    
    setIsAnalyzingExcel(true);
    setExcelAnalysis(null);
    
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      
      const response = await axios.post(`${API}/products/import/excel/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setExcelAnalysis(response.data);
        
        // If there are duplicates (not auto-replace), show modal for user decision
        if (response.data.duplicates > 0) {
          setShowExcelDuplicateModal(true);
          // Initialize all duplicate decisions to 'skip' by default
          const initialDecisions = {};
          response.data.duplicates_list.forEach(dup => {
            initialDecisions[dup.product_id] = 'skip';
          });
          setDuplicateDecisions(initialDecisions);
          
          // Show info about auto-replace
          if (response.data.auto_replace > 0) {
            toast.info(`${response.data.auto_replace} Produkte werden automatisch aktualisiert (Preis-Updates)`);
          }
        } else {
          // No duplicates, can import directly
          const totalNew = response.data.new_products + (response.data.auto_replace || 0);
          if (response.data.auto_replace > 0) {
            toast.success(`Analyse abgeschlossen: ${response.data.new_products} neue Produkte, ${response.data.auto_replace} Updates`);
          } else {
            toast.success(`Analyse abgeschlossen: ${response.data.new_products} neue Produkte gefunden`);
          }
        }
      }
      
    } catch (error) {
      console.error('Excel analysis failed:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Dateianalyse');
    } finally {
      setIsAnalyzingExcel(false);
    }
  };

  const handleDuplicateDecision = (productId, decision) => {
    setDuplicateDecisions(prev => ({
      ...prev,
      [productId]: decision
    }));
  };

  const executeExcelImport = async () => {
    if (!excelAnalysis) {
      toast.error('Bitte analysieren Sie zuerst die Datei');
      return;
    }
    
    setIsImportingExcel(true);
    setShowExcelDuplicateModal(false);
    
    try {
      // Collect auto_replace product IDs
      const autoReplaceIds = (excelAnalysis.auto_replace_list || []).map(p => p.product_id);
      
      const response = await axios.post(`${API}/products/import/excel/execute`, {
        temp_file_path: excelAnalysis.temp_file_path,
        duplicate_decisions: duplicateDecisions,
        auto_replace_ids: autoReplaceIds,
        category: 'neue_produkte'
      });
      
      if (response.data.success) {
        setExcelImportResult(response.data);
        toast.success(response.data.message);
        
        // Reset state
        setExcelFile(null);
        setExcelAnalysis(null);
        setDuplicateDecisions({});
        
        // Refresh products
        await fetchProducts();
        await fetchStats();
      }
      
    } catch (error) {
      console.error('Excel import failed:', error);
      toast.error(error.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setIsImportingExcel(false);
    }
  };


  // View product details
  const handleViewProductDetails = (product) => {
    setSelectedProduct(product);
    setIsProductDetailOpen(true);
  };

  // Load custom favicon on app mount
  useEffect(() => {
    const loadCustomFavicon = async () => {
      try {
        const response = await axios.get(`${API}/settings/favicon`);
        if (response.data.success) {
          // Update favicon link in head
          const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
          favicon.type = response.data.content_type;
          favicon.rel = 'icon';
          favicon.href = `data:${response.data.content_type};base64,${response.data.data}`;
          document.getElementsByTagName('head')[0].appendChild(favicon);
        }
      } catch (error) {
        // No custom favicon, use default
        console.log('Using default favicon');
      }
    };
    loadCustomFavicon();
  }, []);

  // Load data on component mount and when pagination changes
  useEffect(() => {
    fetchProducts();
    fetchStats();
    fetchBannerProducts();
    loadWishlist();
  }, [publicPage, publicLimit, publicSortOrder]);
  
  // Load wishlist from localStorage
  const loadWishlist = () => {
    try {
      const savedWishlist = localStorage.getItem('imported24_wishlist');
      if (savedWishlist) {
        const parsed = JSON.parse(savedWishlist);
        setWishlist(parsed);
        setWishlistCount(parsed.reduce((sum, item) => sum + item.quantity, 0));
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };
  
  // Add to wishlist
  const addToWishlist = (product, quantity = 1) => {
    const existingItem = wishlist.find(item => item.id === (product.id || product._id));
    
    let newWishlist;
    if (existingItem) {
      newWishlist = wishlist.map(item => 
        item.id === (product.id || product._id)
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      const wishlistItem = {
        id: product.id || product._id,
        title: product.title,
        price: product.price?.selling_price || product.selling_price || 0,
        old_price: product.price?.market_price || product.market_price || 0,
        image: product.main_image || product.images?.[0],
        affiliate_link: product.affiliate_link || product.aliexpress_url,
        quantity: quantity
      };
      newWishlist = [...wishlist, wishlistItem];
    }
    
    setWishlist(newWishlist);
    setWishlistCount(newWishlist.reduce((sum, item) => sum + item.quantity, 0));
    localStorage.setItem('imported24_wishlist', JSON.stringify(newWishlist));
    
    toast.success('Zur Wunschliste hinzugefügt! ❤️');
  };
  
  // Remove from wishlist
  const removeFromWishlist = (productId) => {
    const newWishlist = wishlist.filter(item => item.id !== productId);
    setWishlist(newWishlist);
    setWishlistCount(newWishlist.reduce((sum, item) => sum + item.quantity, 0));
    localStorage.setItem('imported24_wishlist', JSON.stringify(newWishlist));
  };
  
  // Update wishlist quantity
  const updateWishlistQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromWishlist(productId);
      return;
    }
    
    const newWishlist = wishlist.map(item =>
      item.id === productId ? { ...item, quantity } : item
    );
    setWishlist(newWishlist);
    setWishlistCount(newWishlist.reduce((sum, item) => sum + item.quantity, 0));
    localStorage.setItem('imported24_wishlist', JSON.stringify(newWishlist));
  };
  
  // Clear wishlist
  const clearWishlist = () => {
    setWishlist([]);
    setWishlistCount(0);
    localStorage.removeItem('imported24_wishlist');
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <CartProvider>
      <div className="min-h-screen bg-gray-50">
        <Toaster position="top-right" />
        
        <Header 
          onOpenCart={() => {}} 
          onSetActiveTab={(tab) => {
            setActiveTab(tab);
            setCurrentPage('home');
          }}
          activeTab={activeTab}
          wishlistCount={wishlistCount}
          onNavigate={handleNavigate}
          wishlistEnabled={siteSettings.wishlist_enabled !== false}
        />
        
        {/* Maintenance Mode for Non-Admins */}
        {siteSettings.maintenance_mode && !isAdminLoggedIn && activeTab !== "add-product" && (
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="relative overflow-hidden text-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-200 rounded-2xl p-12 shadow-xl">
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-purple-100/20 to-pink-100/20 pointer-events-none"></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="text-6xl mb-6">🔧</div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  Wartungsarbeiten
                </h1>
                <p className="text-xl text-gray-700 mb-8">
                  Wir führen derzeit wichtige Wartungsarbeiten durch, um Ihnen ein noch besseres Erlebnis zu bieten.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Admin Login - Always accessible (even in maintenance mode) */}
        {activeTab === "add-product" && !isAdminLoggedIn && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <AdminLogin onLogin={setIsAdminLoggedIn} />
          </div>
        )}
        
        {/* Page Routing - Hidden during maintenance for non-admins */}
        {(!siteSettings.maintenance_mode || isAdminLoggedIn) && (
          <>
        {currentPage === 'impressum' && (
          <ImpressumPage settings={siteSettings} />
        )}
        
        {currentPage === 'agb' && (
          <AGBPage settings={siteSettings} />
        )}
        
        {currentPage === 'datenschutz' && (
          <DatenschutzPage settings={siteSettings} />
        )}
        
        {currentPage === 'wishlist' && (
          siteSettings.wishlist_enabled === false ? (
            <div className="max-w-7xl mx-auto px-4 py-16">
              <div className="text-center bg-yellow-50 border border-yellow-200 rounded-lg p-8">
                <div className="text-6xl mb-4">⚠️</div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Wunschlisten-Funktion vorübergehend deaktiviert</h1>
                <p className="text-gray-600 mb-6">
                  Aufgrund hoher Auslastung können wir derzeit keine neuen Wunschlisten-Bestellungen annehmen.<br/>
                  Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.
                </p>
                <button
                  onClick={() => handleNavigate('home')}
                  className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Zurück zur Startseite
                </button>
              </div>
            </div>
          ) : (
            <WishlistPage 
              wishlist={wishlist}
              updateQuantity={updateWishlistQuantity}
              removeItem={removeFromWishlist}
              clearWishlist={clearWishlist}
              API={API}
            />
          )
        )}
        
        {/* Home Page Content */}
        {currentPage === 'home' && (
          <>
            {/* Hero Section - only show on products tab */}
            {activeTab === "products" && <HeroSection />}
            
            {/* Scrolling Deals Banner */}
            <ScrollingDealsBanner />

            {/* Product Image Banner - Neuheit & Schnäppchen */}
            <ProductImageBanner 
              featuredProducts={bannerProducts} 
              onProductClick={(product) => {
                setSelectedProduct(product);
                setIsProductDetailOpen(true);
              }}
              onAddToWishlist={addToWishlist}
              wishlistEnabled={siteSettings.wishlist_enabled !== false}
            />

            {/* Newsletter Subscription */}
            <NewsletterSubscription />

            {/* Wishlist Service Feature - Only when enabled */}
            {siteSettings.wishlist_enabled !== false && (
              <section className="bg-gradient-to-r from-pink-50 to-purple-50 py-4 my-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="text-center mb-3">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                      ❤️ Wunschlisten-Service
                    </h2>
                    <p className="text-xs text-gray-600">
                      Sie möchten über uns bestellen? Wir kümmern uns um alles!
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-3 max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg p-2.5 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="text-sm font-semibold mb-0.5">1. Produkte auswählen 📝</h3>
                      <p className="text-xs text-gray-600">
                        Fügen Sie beliebig viele Produkte zu Ihrer Wunschliste hinzu
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-2.5 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="text-sm font-semibold mb-0.5">2. An uns senden 📧</h3>
                      <p className="text-xs text-gray-600">
                        Übermitteln Sie Ihre Wunschliste direkt an imported24.de
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-2.5 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="text-sm font-semibold mb-0.5">3. Angebot erhalten 💰</h3>
                      <p className="text-xs text-gray-600">
                        Wir prüfen die Preise und senden Ihnen ein finales Angebot per Email
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-center mt-4">
                    <button
                      onClick={() => handleNavigate('wishlist')}
                      className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full font-bold text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                    >
                      ❤️ Zur Wunschliste
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      ⏱️ Angebote sind 48 Stunden gültig
                    </p>
                  </div>
                </div>
              </section>
            )}

        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="sm:hidden">
            <label htmlFor="tabs" className="sr-only">
              Wählen Sie einen Tab
            </label>
            <select
              id="tabs"
              name="tabs"
              className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              <option value="products">Produktkatalog</option>
              <option value="categories">Kategorien</option>
              {isAdminLoggedIn && <option value="add-product">+ Produkt hinzufügen</option>}
            </select>
          </div>
          <div className="hidden sm:block">
            <nav className="flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab("products")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "products"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Produktkatalog
              </button>
              <button
                onClick={() => setActiveTab("categories")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "categories"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Kategorien
              </button>
              {isAdminLoggedIn && (
                <>
                  <button
                    onClick={() => setActiveTab("add-product")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "add-product"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    🔧 Verwaltung
                  </button>
                  <button
                    onClick={handleAdminLogout}
                    className="py-2 px-4 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 font-medium text-sm"
                  >
                    🚪 Logout
                  </button>
                </>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-8">
            {activeTab === "products" && (
              <div id="products">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Main Content */}
                  <div className="flex-1">
                    <div>
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <h2 className="text-3xl font-bold text-gray-900">Produktkatalog</h2>
                        
                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                          {/* Sortierung */}
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600 font-medium">Sortierung:</label>
                            <select
                              value={publicSortOrder}
                              onChange={(e) => {
                                setPublicSortOrder(e.target.value);
                                setPublicPage(1); // Reset to first page
                              }}
                              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="newest">Neueste zuerst</option>
                              <option value="price_asc">Preis aufsteigend</option>
                              <option value="price_desc">Preis absteigend</option>
                              <option value="popular">Beliebteste</option>
                              <option value="rating">Beste Bewertung</option>
                            </select>
                          </div>
                          
                          {/* Items per page */}
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600 font-medium">Pro Seite:</label>
                            <select
                              value={publicLimit}
                              onChange={(e) => {
                                setPublicLimit(Number(e.target.value));
                                setPublicPage(1); // Reset to first page
                              }}
                              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                          
                          <div className="text-sm text-gray-500 whitespace-nowrap">
                            {totalProducts} Produkte gesamt
                          </div>
                        </div>
                      </div>

                      {isLoading && products.length === 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6">
                          {[...Array(publicLimit > 20 ? 20 : publicLimit)].map((_, index) => (
                            <ProductSkeleton key={`skeleton-${index}`} />
                          ))}
                        </div>
                      ) : products.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="text-gray-400 text-6xl mb-4">🔍</div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Produkte gefunden</h3>
                          <p className="text-gray-500 mb-4">Suchen Sie nach Produkten oder importieren Sie neue</p>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6">
                            {products.map((product, index) => (
                              <div 
                                key={product.id || product._id}
                                className="animate-fadeIn"
                                style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                              >
                                <ProductCard 
                                  product={product}
                                  onAddToCart={(product) => {
                                    // For affiliate products, this will be handled in the component
                                    // For regular products, we could add cart functionality here
                                    console.log('Add to cart:', product);
                                  }}
                                  onViewDetails={handleViewProductDetails}
                                  onAddToWishlist={addToWishlist}
                                  wishlistEnabled={siteSettings.wishlist_enabled !== false}
                                />
                              </div>
                            ))}
                          </div>
                          
                          {/* Pagination Controls */}
                          {totalProducts > publicLimit && (
                            <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                              {/* Previous Button */}
                              <button
                                onClick={() => setPublicPage(prev => Math.max(1, prev - 1))}
                                disabled={publicPage === 1}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                  publicPage === 1
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                              >
                                ← Zurück
                              </button>
                              
                              {/* Page Numbers */}
                              <div className="flex gap-2 flex-wrap justify-center">
                                {(() => {
                                  const totalPages = Math.ceil(totalProducts / publicLimit);
                                  const pages = [];
                                  const maxVisiblePages = 7;
                                  
                                  let startPage = Math.max(1, publicPage - Math.floor(maxVisiblePages / 2));
                                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                  
                                  if (endPage - startPage < maxVisiblePages - 1) {
                                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                  }
                                  
                                  // First page
                                  if (startPage > 1) {
                                    pages.push(
                                      <button
                                        key={1}
                                        onClick={() => setPublicPage(1)}
                                        className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 font-medium"
                                      >
                                        1
                                      </button>
                                    );
                                    if (startPage > 2) {
                                      pages.push(<span key="dots1" className="px-2">...</span>);
                                    }
                                  }
                                  
                                  // Middle pages
                                  for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                      <button
                                        key={i}
                                        onClick={() => setPublicPage(i)}
                                        className={`px-3 py-1 rounded font-medium transition-all ${
                                          i === publicPage
                                            ? 'bg-blue-600 text-white'
                                            : 'border border-gray-300 hover:bg-gray-100'
                                        }`}
                                      >
                                        {i}
                                      </button>
                                    );
                                  }
                                  
                                  // Last page
                                  if (endPage < totalPages) {
                                    if (endPage < totalPages - 1) {
                                      pages.push(<span key="dots2" className="px-2">...</span>);
                                    }
                                    pages.push(
                                      <button
                                        key={totalPages}
                                        onClick={() => setPublicPage(totalPages)}
                                        className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 font-medium"
                                      >
                                        {totalPages}
                                      </button>
                                    );
                                  }
                                  
                                  return pages;
                                })()}
                              </div>
                              
                              {/* Next Button */}
                              <button
                                onClick={() => setPublicPage(prev => Math.min(Math.ceil(totalProducts / publicLimit), prev + 1))}
                                disabled={publicPage >= Math.ceil(totalProducts / publicLimit)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                  publicPage >= Math.ceil(totalProducts / publicLimit)
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                              >
                                Weiter →
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "categories" && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Kategorien durchsuchen</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {categories.map((cat, index) => (
                    <div 
                      key={index} 
                      onClick={() => {
                        setActiveTab('products');
                        setSelectedCategory(cat.category);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all cursor-pointer transform hover:scale-105"
                    >
                      <div className="text-4xl mb-4">{cat.icon}</div>
                      <h3 className="text-xl font-semibold mb-2">{cat.displayName}</h3>
                      <p className="text-gray-600 mb-4">{cat.description}</p>
                      <div className="text-sm text-blue-600 font-medium flex items-center justify-center gap-2">
                        <span>Jetzt durchstöbern</span>
                        <span>→</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "add-product" && isAdminLoggedIn && (
              <div>
                <div>
                    {/* Admin Dashboard */}
                    <div className="mb-8 flex justify-between items-center">
                      <div>
                        <h2 className="text-3xl font-bold mb-2">Admin-Bereich</h2>
                        <p className="text-gray-600">Willkommen im Verwaltungsbereich</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Eingeloggt als:</p>
                        <p className="text-lg font-semibold text-blue-600">
                          👤 {JSON.parse(localStorage.getItem('admin_user') || '{}').username || 'Admin'}
                        </p>
                      </div>
                    </div>

                    {/* Admin Actions Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                      <button
                        onClick={() => setActiveTab("admin-manage")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-500 text-left"
                      >
                        <div className="text-4xl mb-3">📋</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">Produktverwaltung</h3>
                        <p className="text-gray-600">Produkte bearbeiten, löschen und Status ändern</p>
                      </button>
                      
                      <button
                        onClick={() => setActiveTab("admin-add")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 text-left"
                      >
                        <div className="text-4xl mb-3">➕</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">Produkt hinzufügen</h3>
                        <p className="text-gray-600">Neue Produkte mit Promo-Material hinzufügen</p>
                      </button>
                      
                      <button
                        onClick={() => setActiveTab("admin-categories")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-purple-500 text-left"
                      >
                        <div className="text-4xl mb-3">🏷️</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">Kategorien</h3>
                        <p className="text-gray-600">Kategorien verwalten und bearbeiten</p>
                      </button>

                      <button
                        onClick={() => setActiveTab("admin-newsletter")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-pink-500 text-left"
                      >
                        <div className="text-4xl mb-3">📧</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">Newsletter</h3>
                        <p className="text-gray-600">Newsletter erstellen und versenden</p>
                      </button>

                      <button
                        onClick={() => setActiveTab("admin-wishlist")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-red-500 text-left"
                      >
                        <div className="text-4xl mb-3">❤️</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">Wunschliste</h3>
                        <p className="text-gray-600">Wunschliste verwalten und bearbeiten</p>
                      </button>

                      <button
                        onClick={() => setActiveTab("admin-accounting")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-yellow-500 text-left"
                      >
                        <div className="text-4xl mb-3">💰</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">Abrechnung</h3>
                        <p className="text-gray-600">Bezahlte Bestellungen und Gewinnübersicht</p>
                      </button>

                      <button
                        onClick={() => setActiveTab("admin-settings")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-orange-500 text-left"
                      >
                        <div className="text-4xl mb-3">⚙️</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">Einstellungen</h3>
                        <p className="text-gray-600">
                          {JSON.parse(localStorage.getItem('admin_user') || '{}').role === 'admin' 
                            ? 'Impressum, Profildaten und Benutzerverwaltung' 
                            : 'Impressum und Profildaten'}
                        </p>
                      </button>
                      
                      <button
                        onClick={() => setActiveTab("admin-excel-import")}
                        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-purple-500 text-left"
                      >
                        <div className="text-4xl mb-3">📊</div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">AliExpress Excel Import</h3>
                        <p className="text-gray-600">Produkte aus AliExpress Ad-Center importieren</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "admin-manage" && isAdminLoggedIn && (
              <div>
                <AdminProductManagement setActiveTab={setActiveTab} />
              </div>
            )}

            {activeTab === "admin-add" && isAdminLoggedIn && (
              <div className="bg-gray-50 rounded-lg p-8">
                <NewProductEntry setActiveTab={setActiveTab} />
              </div>
            )}

            {activeTab === "admin-categories" && isAdminLoggedIn && (
              <div>
                <AdminCategoryManagement />
              </div>
            )}

            {activeTab === "admin-newsletter" && isAdminLoggedIn && (
              <div>
                <AdminNewsletterManagement />
              </div>
            )}

            {activeTab === "admin-wishlist" && isAdminLoggedIn && (
              <div>
                <AdminWishlistManagement />
              </div>
            )}

            {activeTab === "admin-accounting" && isAdminLoggedIn && (
              <div>
                <AccountingManagement />
              </div>
            )}

            {activeTab === "admin-settings" && isAdminLoggedIn && (
              <div>
                <AdminSettingsManagement />
              </div>
            )}

            {activeTab === "admin-excel-import" && isAdminLoggedIn && (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6">
                  <button
                    onClick={() => setActiveTab("add-product")}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    <span className="mr-2">←</span>
                    Zurück zum Dashboard
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold mb-2 flex items-center">
                      <span className="mr-3">📊</span>
                      AliExpress Excel Import
                    </h2>
                    <p className="text-gray-600">Importieren Sie Produkte aus dem AliExpress Ad-Center Export</p>
                  </div>

                  {/* Excel Upload Area */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 mb-6 text-center bg-purple-50">
                    <input
                      type="file"
                      accept=".xls,.xlsx"
                      onChange={(e) => handleExcelFileSelect(e.target.files[0])}
                      className="hidden"
                      id="excelFile"
                      disabled={isAnalyzingExcel || isImportingExcel}
                    />
                    <label
                      htmlFor="excelFile"
                      className={`cursor-pointer ${(isAnalyzingExcel || isImportingExcel) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    >
                      <div className="text-purple-500 text-6xl mb-4">📂</div>
                      <div className="text-gray-700 font-medium mb-2 text-xl">
                        {excelFile ? `📄 ${excelFile.name}` : "Excel-Datei auswählen"}
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Klicken zum Hochladen oder Datei hierher ziehen
                      </div>
                      <div className="text-xs text-gray-400">
                        Unterstützte Formate: .xls, .xlsx
                      </div>
                    </label>
                  </div>
                  
                  {/* Analyze Button */}
                  {excelFile && !excelAnalysis && (
                    <div className="mb-6">
                      <button
                        onClick={analyzeExcelFile}
                        disabled={isAnalyzingExcel}
                        className="w-full px-8 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 font-medium text-lg transition-all shadow-lg hover:shadow-xl"
                      >
                        {isAnalyzingExcel ? '🔄 Analysiere Datei...' : '🔍 Datei analysieren'}
                      </button>
                    </div>
                  )}
                  
                  {/* Analysis Results */}
                  {excelAnalysis && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-6 shadow-md">
                      <h5 className="font-semibold mb-4 text-xl">📋 Analyse-Ergebnis</h5>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white p-5 rounded-lg shadow">
                          <div className="text-gray-600 mb-2 text-sm">Gesamt</div>
                          <div className="text-4xl font-bold text-blue-600">{excelAnalysis.total_rows}</div>
                        </div>
                        <div className="bg-white p-5 rounded-lg shadow">
                          <div className="text-gray-600 mb-2 text-sm">Neue Produkte</div>
                          <div className="text-4xl font-bold text-green-600">{excelAnalysis.new_products}</div>
                        </div>
                        <div className="bg-white p-5 rounded-lg shadow">
                          <div className="text-gray-600 mb-2 text-sm">Auto-Updates</div>
                          <div className="text-4xl font-bold text-purple-600">{excelAnalysis.auto_replace || 0}</div>
                        </div>
                        <div className="bg-white p-5 rounded-lg shadow">
                          <div className="text-gray-600 mb-2 text-sm">Duplikate</div>
                          <div className="text-4xl font-bold text-yellow-600">{excelAnalysis.duplicates}</div>
                        </div>
                      </div>
                      
                      {excelAnalysis.auto_replace > 0 && (
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-4 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">🔄</span>
                            <div className="text-sm text-purple-800">
                              <strong>{excelAnalysis.auto_replace} Produkte</strong> mit gleicher AliExpress Product-ID gefunden.
                              Diese werden automatisch aktualisiert (Preis-Updates).
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {excelAnalysis.duplicates > 0 && (
                        <button
                          onClick={() => setShowExcelDuplicateModal(true)}
                          className="w-full px-6 py-4 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium text-lg transition-all shadow-lg hover:shadow-xl"
                        >
                          ⚠️ {excelAnalysis.duplicates} Duplikate überprüfen
                        </button>
                      )}
                      
                      {excelAnalysis.duplicates === 0 && (
                        <button
                          onClick={executeExcelImport}
                          disabled={isImportingExcel}
                          className="w-full px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-medium text-lg transition-all shadow-lg hover:shadow-xl"
                        >
                          {isImportingExcel ? '⏳ Importiere...' : `✅ ${excelAnalysis.new_products} Produkte importieren`}
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Import Results */}
                  {excelImportResult && (
                    <div className={`p-6 rounded-lg mb-6 ${excelImportResult.success ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'} shadow-md`}>
                      <div className={`font-bold text-xl mb-3 ${excelImportResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {excelImportResult.message}
                      </div>
                      {excelImportResult.success && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-3 text-green-700">
                            <span className="text-2xl">✅</span>
                            <span className="text-lg">Importiert: <strong>{excelImportResult.imported}</strong></span>
                          </div>
                          <div className="flex items-center gap-3 text-blue-700">
                            <span className="text-2xl">🔄</span>
                            <span className="text-lg">Ersetzt: <strong>{excelImportResult.replaced}</strong></span>
                          </div>
                          <div className="flex items-center gap-3 text-gray-700">
                            <span className="text-2xl">⏭️</span>
                            <span className="text-lg">Übersprungen: <strong>{excelImportResult.skipped}</strong></span>
                          </div>
                          {excelImportResult.errors > 0 && (
                            <div className="flex items-center gap-3 text-red-600">
                              <span className="text-2xl">❌</span>
                              <span className="text-lg">Fehler: <strong>{excelImportResult.errors}</strong></span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">ℹ️</span>
                      <div className="text-sm text-gray-700">
                        <strong className="block mb-1">Wichtige Hinweise:</strong>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Alle importierten Produkte werden in die Kategorie <strong>"neue Produkte"</strong> eingeordnet</li>
                          <li>Bei Duplikaten können Sie wählen: Hinzufügen, Ersetzen oder Überspringen</li>
                          <li>Eine manuelle Kategoriezuordnung kann später erfolgen</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

          </>
        )}

        {/* Product Details Modal */}
        <ProductDetailsModal
          product={selectedProduct}
          isOpen={isProductDetailOpen}
          onClose={() => {
            setIsProductDetailOpen(false);
            setSelectedProduct(null);
          }}
          onAddToWishlist={addToWishlist}
          wishlistEnabled={siteSettings.wishlist_enabled !== false}
        />

        
        {/* Duplicate Decision Modal for Excel Import */}
        <DuplicateDecisionModal
          isOpen={showExcelDuplicateModal}
          onClose={() => setShowExcelDuplicateModal(false)}
          duplicates={excelAnalysis?.duplicates_list || []}
          decisions={duplicateDecisions}
          onDecisionChange={handleDuplicateDecision}
          onConfirm={executeExcelImport}
        />

        </>
        )}

        {/* Scroll to Top Button - Always visible */}
        <ScrollToTopButton />

        {/* Footer - Always visible (even in maintenance mode for admin login) */}
        <Footer onNavigate={handleNavigate} settings={siteSettings} onSetActiveTab={setActiveTab} />

      </div>
    </CartProvider>
  );
};

export default App;